import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp, faList, faFileAlt, faCog, faCloudDownloadAlt, faCloudSun, faArrowLeft, faSpinner, faUpload, faMapLocationDot, faLocationDot, faHardHat, faTruck, faPlus, faInfoCircle, faEdit, faLayerGroup, faCheckCircle, faCircleXmark, faGripVertical, faTimes, faExclamationTriangle, faAsterisk, faDatabase, faBan, faSquareCheck, faCircleDot, faFont, faSignature, faTrash, faInbox, faUser, faDownload } from '@fortawesome/free-solid-svg-icons'
import { formService } from '../services/formService'
import { loadCachedAnswers, saveCachedAnswers, clearCachedAnswers } from '../services/formCacheService'
import { profileService } from '../services/profileService'
// PDF.js (for extracting form fields)
// Lazy import inside handler to keep initial bundle smaller
import { permissionService } from '../services/permissionService'
import FormAccessAndNotifications, { DEFAULT_FORM_NOTIFICATIONS, normalizeFormNotifications } from '../components/forms/FormAccessAndNotifications'
import { FormData, Workflow, FormQuestion, PendingFormAssignment, FormFiller } from '../types/forms'
import { useAuth } from '../contexts/AuthContext'
import { apiFetch } from '../utils/apiFetch'
import { API_BASE_URL } from '../utils/apiBaseUrl'
import { FORM_BLOCKS } from '../components/FormBlocks'
import { PdfFieldsTree } from '../components/PdfFieldsTree'
import {
  collectLeafFieldNames,
  fieldNamesUnderPage,
  groupChildren,
  insertGroupFromFields,
  mergeAutoRadioGroups,
  parseOptionsInput,
  persistPages,
  walkSelectableKeys,
} from '../components/pdfFieldsTreeState'
import {
  applyRadioGroupValue,
  buildAutoRadioGroupsFromFields,
  makeRadioMemberName,
  radioGroupNameFromStoredId,
  radioOptionFromQuestion,
  refreshButtonAppearances,
  resolveRadioGroupName,
} from '../utils/pdfRadioFields'
import {
  applySignatureDateAnswers,
  collectLeafQuestions,
  pdfTextForSignatureDate,
  SIGNATURE_DATE_TYPE,
} from '../utils/signatureDateFields'
import { isCompleteSignatureValue } from '../utils/signatureAuth'
import {
  applyReadonlyFlag,
  applyRequiredFlag,
  collectWritableFieldIds,
  DEFAULT_FILLERS,
  fillerLabel,
  isFieldInputLocked,
  isFieldPrefillable,
  isMultiFillerForm,
  isRequiredForCurrentStage,
  normalizeFillers,
} from '../utils/formFillers'
import { EmployeeTypeahead } from '../components/EmployeeTypeahead'
import { formNamesMatch, sanitizeFormFileName } from '../utils/formFileName'


const _isAdmin = true // Simulate admin for now

type SelectionTab = 'pending' | 'workflows' | 'forms' | 'management'

type ViewState =
  | { view: 'selection'; tab: SelectionTab }
  | { view: 'form'; formName: string }
  | { view: 'formManagement'; mode: 'create' | 'edit' }
  | { view: 'toolManagement' }

// Tool definitions
const TOOL_DEFINITIONS = {
  weather:   { icon: faCloudSun, label: 'Weather', description: 'Get weather info' },
  maps:      { icon: faMapLocationDot, label: 'Google Maps', description: 'Open Google Maps' },
  import:    { icon: faCloudDownloadAlt, label: 'Import', description: 'Import previous answers' },
  jobtread:  { icon: faHardHat, label: 'JobTread', description: 'Open JobTread' },
  upload:    { icon: faUpload, label: 'Upload', description: 'Upload a file' },
  linxup:    { icon: faTruck, label: 'Linxup', description: 'Open Linxup tracking' }
};

const InternalNavbar: React.FC<{
  workflowName?: string
  formName: string
  formsInWorkflow?: FormData[]
  currentFormName?: string
  onSwitchForm?: (formName: string) => void
  steps: string[]
  currentStep: number
  onStepClick?: (idx: number) => void
  tools?: string[]
  onToolClick?: (tool: string) => void
}> = ({ 
  workflowName: _wn, 
  formName, 
  formsInWorkflow, 
  currentFormName, 
  onSwitchForm, 
  steps, 
  currentStep, 
  onStepClick, 
  tools = [],
  onToolClick
}) => {
  const hasFormSwitcher = !!(formsInWorkflow && formsInWorkflow.length > 1)
  const renderToolButtons = () => tools.map(tool => {
    const def = TOOL_DEFINITIONS[tool as keyof typeof TOOL_DEFINITIONS]
    if (!def) return null
    return (
      <button
        key={tool}
        className="btn btn-ghost btn-circle btn-sm md:btn-md"
        title={def.label}
        onClick={() => onToolClick && onToolClick(tool)}
      >
        <FontAwesomeIcon icon={def.icon} className="text-lg" />
      </button>
    )
  })

  const renderFormSwitcherMenu = () => (
    <ul tabIndex={0} className="dropdown-content menu shadow bg-base-100 rounded-box w-56 mt-2 max-h-60 overflow-auto z-50">
      {formsInWorkflow!.map(f => (
        <li key={f.formName}>
          <a
            className={f.formName === currentFormName ? 'active' : ''}
            onClick={() => onSwitchForm && onSwitchForm(f.formName)}
          >
            {f.formName}
          </a>
        </li>
      ))}
    </ul>
  )

  return (
  <div className="bg-base-100 rounded-lg shadow mb-8 px-4 py-3 flex flex-col md:flex-row md:items-center w-full gap-2 md:gap-4">
    {/* Mobile: form name on its own row so long titles wrap instead of colliding with the page */}
    <div className="md:hidden flex items-start gap-2 min-w-0 w-full">
      {hasFormSwitcher ? (
        <div className="dropdown dropdown-bottom min-w-0 flex-1">
          <button tabIndex={0} className="btn btn-ghost w-full min-h-0 h-auto py-2 justify-start gap-2 whitespace-normal">
            <FontAwesomeIcon icon={faFileAlt} className="h-5 w-5 shrink-0 mt-0.5" />
            <span className="font-semibold text-left break-words flex-1">{formName}</span>
            <FontAwesomeIcon icon={faChevronDown} className="text-sm shrink-0" />
          </button>
            {renderFormSwitcherMenu()}
        </div>
      ) : (
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <FontAwesomeIcon icon={faFileAlt} className="h-5 w-5 shrink-0 mt-0.5" />
          <span className="font-semibold break-words">{formName}</span>
        </div>
      )}
      {tools.some(tool => TOOL_DEFINITIONS[tool as keyof typeof TOOL_DEFINITIONS]) && (
        <div className="shrink-0 flex gap-1">
          {renderToolButtons()}
        </div>
      )}
    </div>

    {hasFormSwitcher && (
      <div className="hidden md:block shrink-0">
        <div className="dropdown dropdown-bottom">
          <button tabIndex={0} className="btn btn-ghost gap-2 text-base">
            <FontAwesomeIcon icon={faFileAlt} className="h-5 w-5" />
            <span className="font-semibold">{formName}</span>
            <FontAwesomeIcon icon={faChevronDown} className="text-sm" />
          </button>
            {renderFormSwitcherMenu()}
        </div>
      </div>
    )}
    
    {/* Steps - Desktop: horizontal steps, Mobile: full-width row below the form name */}
    <div className="flex-1 min-w-0 w-full">
      <div className="hidden md:block overflow-x-auto">
        <ul className="steps steps-horizontal w-full">
          {steps.map((step, idx) => (
            <li
              key={step}
              className={`step ${idx <= currentStep ? 'step-primary' : ''} cursor-pointer`}
              onClick={() => onStepClick && onStepClick(idx)}
              title={`Go to ${step}`}
            >
              {step}
            </li>
          ))}
        </ul>
      </div>
      <div className="dropdown dropdown-bottom md:hidden w-full">
        <button tabIndex={0} className="btn btn-ghost btn-sm w-full min-h-0 h-auto py-2 justify-between gap-2 whitespace-normal">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <div className="badge badge-primary badge-sm shrink-0 mt-0.5">{currentStep + 1}/{steps.length}</div>
            <span className="font-medium text-left break-words">{steps[currentStep]}</span>
          </div>
          <FontAwesomeIcon icon={faChevronDown} className="text-xs shrink-0 mt-1" />
        </button>
        <ul tabIndex={0} className="dropdown-content menu menu-sm shadow-lg bg-base-100 rounded-box w-full mt-1 p-2 z-50 max-h-64 overflow-y-auto">
          {steps.map((step, idx) => (
            <li key={step}>
              <a 
                className={`flex items-start gap-3 whitespace-normal ${idx === currentStep ? 'active' : ''}`}
                onClick={() => {
                  onStepClick && onStepClick(idx)
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur()
                  }
                }}
              >
                <div className={`badge badge-sm shrink-0 ${idx < currentStep ? 'badge-primary' : idx === currentStep ? 'badge-primary badge-outline' : 'badge-ghost'}`}>
                  {idx + 1}
                </div>
                <span className={`break-words ${idx === currentStep ? 'font-semibold' : ''}`}>{step}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
    <div className="hidden md:flex shrink-0 min-w-0 gap-2">
      {renderToolButtons()}
    </div>
  </div>
  )
}

interface FormsPageProps {
}

const FormsPage: React.FC<FormsPageProps> = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { loading: authLoading, hasPermission, user } = useAuth()
  
  // Permission guard - redirect unauthorized users
  useEffect(() => {
    if (!authLoading && !hasPermission('forms')) {
      navigate('/', { replace: true });
    }
  }, [authLoading, hasPermission, navigate]);

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render anything if user doesn't have permission (will redirect)
  if (!hasPermission('forms')) {
    return null;
  }

  const [view, setView] = useState<ViewState>({ view: 'selection', tab: 'workflows' })
  const [loading, setLoading] = useState(true)
  const [forms, setForms] = useState<FormData[]>([])
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [pendingAssignments, setPendingAssignments] = useState<PendingFormAssignment[]>([])
  const [allPendingAssignments, setAllPendingAssignments] = useState<PendingFormAssignment[]>([])
  const [cancelTarget, setCancelTarget] = useState<PendingFormAssignment | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [activeInstance, setActiveInstance] = useState<{
    id: string
    currentStage: number
    totalStages: number
    fillers: FormFiller[]
    startedByLabel?: string | null
  } | null>(null)
  const [stageSubmitNotice, setStageSubmitNotice] = useState<string | null>(null)
  const [categories, setCategories] = useState<string[]>([])
  const [filters, setFilters] = useState({ category: '', search: '' })
  const [activeWorkflowName, setActiveWorkflowName] = useState<string | undefined>(undefined)
  
  // Form filling state
  const [currentForm, setCurrentForm] = useState<FormData | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [formAnswers, setFormAnswers] = useState<{ [key: string]: any }>({})
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({})
  const [prefilledFieldIds, setPrefilledFieldIds] = useState<Set<string>>(new Set())
  const [confirmedPrefillIds, setConfirmedPrefillIds] = useState<Set<string>>(new Set())

  // Workflow selection modal state (for optional forms)
  const [workflowSelectionModal, setWorkflowSelectionModal] = useState<{
    open: boolean;
    workflowName: string;
    forms: FormData[];
    selectedFormNames: Set<string>;
  }>({
    open: false,
    workflowName: '',
    forms: [],
    selectedFormNames: new Set()
  })
  // Store the forms to show in the current workflow session (after user selection)
  const [workflowFormsToShow, setWorkflowFormsToShow] = useState<FormData[]>([])

  // Management tab state
  const [showEditFormList, setShowEditFormList] = useState(false)
  const importFormFileInputRef = React.useRef<HTMLInputElement | null>(null)
  const [importFormBusy, setImportFormBusy] = useState(false)

  // Form Management state
  const [managementMode, setManagementMode] = useState<'create' | 'edit'>('create')
  const [managementStep, setManagementStep] = useState<0 | 1>(0)
  const [fixingFormJson, setFixingFormJson] = useState(false)
  const [auditingForm, setAuditingForm] = useState(false)
  const [levelNames, setLevelNames] = useState<string[]>([])
  const [levelsLoading, setLevelsLoading] = useState(false)
  const [legacyFormAccess, setLegacyFormAccess] = useState(false)
  const [managementDetails, setManagementDetails] = useState({
    formName: '',
    workflow: '',
    workflowOrder: 1,
    workflowOptional: false,
    category: '',
    estimatedTime: '',
    description: '',
    accessLevels: [] as string[],
    notifications: DEFAULT_FORM_NOTIFICATIONS,
    pdfFile: null as File | null,
    pdfBase64: '' as string,
    pdfDataUrl: '' as string,
    upload: { drive: false, jobtread: false, driveFolder: '', jtFolder: '', jobFieldId: '' } as any,
    cache: { enabled: false, expiryHours: 24 } as { enabled: boolean; expiryHours: number },
    hospitalMaps: { enabled: false, siteAddressField: '', hospitalAddressField: '' } as { enabled: boolean; siteAddressField: string; hospitalAddressField: string },
    fillers: DEFAULT_FILLERS as FormFiller[],
  })
  const managementDetailsRef = React.useRef(managementDetails)
  managementDetailsRef.current = managementDetails
  const [editingSourceFormName, setEditingSourceFormName] = useState('')
  const [openingFormEditor, setOpeningFormEditor] = useState(false)
  type PdfFieldWidget = {
    widgetId: string
    rect?: { x: number; y: number; width: number; height: number }
    page?: number
  }
  type PdfField = {
    // Logical field key used by the builder and answers.
    name: string
    // Canonical AcroForm field name used for pdf-lib lookup.
    acroName?: string
    type: string
    options?: string[]
    // Primary widget position (kept for backward compatibility).
    rect?: { x: number; y: number; width: number; height: number }
    page?: number
    // All widget instances for this logical field.
    widgets?: PdfFieldWidget[]
    instanceCount?: number
    /** Acrobat radio parent /T — members of the same group share this. */
    radioGroup?: string
    /** Export / on-value for this radio button (e.g. "0", "Choice1"). */
    optionValue?: string
  }
  const [parsedPdfFields, setParsedPdfFields] = useState<PdfField[]>([])
  const [selectedPdfField, setSelectedPdfField] = useState<PdfField | null>(null)
  type FieldGroup = {
    id: string
    name: string
    fieldNames: string[]
    children?: Array<{ type: 'field'; fieldName: string } | { type: 'group'; groupId: string }>
  }
  type PageDef = { id: string; name: string; items: Array<{ type: 'field'; key: string } | { type: 'group'; key: string }> }
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>([])
  const [pages, setPages] = useState<PageDef[]>([])
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set())
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [selectedGroup, setSelectedGroup] = useState<FieldGroup | null>(null)
  const [selectedPage, setSelectedPage] = useState<PageDef | null>(null)
  const [groupConfigs, setGroupConfigs] = useState<Record<string, { description?: string; layout?: 'stack' | 'two-columns' | 'three-columns'; showHeading?: boolean; showDescription?: boolean; mode?: 'fields' | 'radio'; fillerStage?: number; readonly?: boolean; required?: boolean }>>({})
  const [pageConfigs, setPageConfigs] = useState<Record<string, { description?: string }>>({})
  type FieldConfig = {
    label: string
    type: FormQuestion['type']
    description?: string
    required: boolean
    readonly?: boolean
    fillerStage?: number
    options?: string[]
    showDescription?: boolean
    tool?: { type: 'weather' | 'maps' | 'jobtread' | 'date' | 'user'; config: any }
    excludeFromCache?: boolean
    signatureSource?: string
    format?: string
  }
  const [fieldConfigs, setFieldConfigs] = useState<Record<string, FieldConfig>>({})
  const [optionsDraft, setOptionsDraft] = useState<Record<string, string>>({})
  const [reExtractingPdfFields, setReExtractingPdfFields] = useState(false)
  const [editingNameKey, setEditingNameKey] = useState<string | null>(null)
  const [editingNameValue, setEditingNameValue] = useState('')
  const [centerTab, setCenterTab] = useState<'pdf' | 'builder'>('pdf')
  const [nameModal, setNameModal] = useState<{
    title: string
    label: string
    value: string
    resolve: (name: string | null) => void
  } | null>(null)

  const promptName = (title: string, defaultValue = '', label = 'Name') =>
    new Promise<string | null>((resolve) => {
      setNameModal({ title, label, value: defaultValue, resolve })
    })
  const [pdfPages, setPdfPages] = useState<{ pageNum: number; width: number; height: number }[]>([])
  const [pdfScale, setPdfScale] = useState(0.75)
  const [pdfDoc, setPdfDoc] = useState<any>(null)
  const [pdfRenderedPages, setPdfRenderedPages] = useState<Set<number>>(new Set())
  const pdfContainerRef = React.useRef<HTMLDivElement>(null)
  const pdfCanvasRefs = React.useRef<Map<number, HTMLCanvasElement>>(new Map())
  const pdfRenderTasks = React.useRef<Map<number, any>>(new Map())
  const pdfRenderLock = React.useRef<boolean>(false)
  const pdfFieldRefs = React.useRef<Map<string, HTMLDivElement>>(new Map())
  const fieldListRefs = React.useRef<Map<string, HTMLElement>>(new Map())
  const fieldListContainerRef = React.useRef<HTMLDivElement>(null)
  const pdfWorkerBlobUrlRef = React.useRef<string | null>(null)
  
  // Re-render PDF pages when scale changes or pages are set
  useEffect(() => {
    const renderPages = async () => {
      if (!pdfDoc || pdfPages.length === 0) return
      if (pdfRenderLock.current) return
      
      pdfRenderLock.current = true
      
      // Cancel any existing render tasks
      for (const [pageNum, task] of pdfRenderTasks.current) {
        try { task.cancel() } catch {}
      }
      pdfRenderTasks.current.clear()
      
      // Small delay to ensure canvas refs are set
      await new Promise(resolve => setTimeout(resolve, 100))
      
      const rendered = new Set<number>()
      
      for (const pageInfo of pdfPages) {
        const canvas = pdfCanvasRefs.current.get(pageInfo.pageNum)
        if (!canvas) continue
        
        try {
          const page = await pdfDoc.getPage(pageInfo.pageNum)
          const viewport = page.getViewport({ scale: pdfScale })
          
          // Clear canvas before rendering
          const context = canvas.getContext('2d')
          if (!context) continue
          
          canvas.width = viewport.width
          canvas.height = viewport.height
          context.clearRect(0, 0, canvas.width, canvas.height)
          
          const renderTask = page.render({ canvasContext: context, viewport })
          pdfRenderTasks.current.set(pageInfo.pageNum, renderTask)
          
          await renderTask.promise
          rendered.add(pageInfo.pageNum)
          pdfRenderTasks.current.delete(pageInfo.pageNum)
        } catch (e: any) {
          if (e?.name !== 'RenderingCancelledException') {
            console.error('Failed to render page:', pageInfo.pageNum, e)
          }
        }
      }
      
      setPdfRenderedPages(rendered)
      pdfRenderLock.current = false
    }
    
    renderPages()
    
    return () => {
      // Cleanup: cancel any pending renders
      for (const [, task] of pdfRenderTasks.current) {
        try { task.cancel() } catch {}
      }
    }
  }, [pdfDoc, pdfPages, pdfScale])

  // Revoke PDF worker Blob URL on unmount to avoid leaks
  useEffect(() => {
    return () => {
      if (pdfWorkerBlobUrlRef.current) {
        URL.revokeObjectURL(pdfWorkerBlobUrlRef.current)
        pdfWorkerBlobUrlRef.current = null
      }
    }
  }, [])

  // Scroll to selected field in PDF when on PDF tab (do not auto-switch tabs; respect user's Preview choice)
  useEffect(() => {
    if (!selectedPdfField?.name) return
    // Only run scroll logic when on PDF tab - avoids any side effects when user is viewing Preview
    if (centerTab !== 'pdf') return

    // Check if the selected field has position data
    const field = parsedPdfFields.find(f => f.name === selectedPdfField.name)
    
    // Small delay to ensure render is complete
    const scrollTimeout = setTimeout(() => {
      // Scroll to field in PDF preview
      if (field?.rect) {
        const fieldEl = pdfFieldRefs.current.get(selectedPdfField.name)
        const container = pdfContainerRef.current
        
        if (fieldEl && container) {
          const fieldRect = fieldEl.getBoundingClientRect()
          const containerRect = container.getBoundingClientRect()
          
          const scrollTop = container.scrollTop + (fieldRect.top - containerRect.top) - (containerRect.height / 2) + (fieldRect.height / 2)
          const scrollLeft = container.scrollLeft + (fieldRect.left - containerRect.left) - (containerRect.width / 2) + (fieldRect.width / 2)
          
          container.scrollTo({
            top: Math.max(0, scrollTop),
            left: Math.max(0, scrollLeft),
            behavior: 'smooth'
          })
        }
      }
      
      // Also scroll to field in the PDF Fields list
      const listItemEl = fieldListRefs.current.get(selectedPdfField.name)
      const listContainer = fieldListContainerRef.current
      
      if (listItemEl && listContainer) {
        const itemRect = listItemEl.getBoundingClientRect()
        const containerRect = listContainer.getBoundingClientRect()
        
        // Only scroll if the item is not visible
        if (itemRect.top < containerRect.top || itemRect.bottom > containerRect.bottom) {
          const scrollTop = listContainer.scrollTop + (itemRect.top - containerRect.top) - (containerRect.height / 2) + (itemRect.height / 2)
          
          listContainer.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth'
          })
        }
      }
    }, 150)
    
    return () => clearTimeout(scrollTimeout)
  }, [selectedPdfField?.name, parsedPdfFields, centerTab])

  const handleManagementStepClick = (idx: 0 | 1) => {
    if (idx === 0) {
      setManagementStep(0)
      return
    }
    // Always allow navigating to Step 2; if a new PDF is present, parse it first
    if (managementDetails.pdfFile) {
      simulateParsePdfFields()
    } else {
      setManagementStep(1)
    }
  }

  const handleReExtractPdfFields = async () => {
    const base64 = managementDetails.pdfBase64
    if (!base64) {
      alert('No PDF available to re-extract from')
      return
    }
    setReExtractingPdfFields(true)
    try {
      const pdfData = atob(base64)
      const pdfArray = new Uint8Array(pdfData.length)
      for (let i = 0; i < pdfData.length; i++) {
        pdfArray[i] = pdfData.charCodeAt(i)
      }
      const blob = new Blob([pdfArray], { type: 'application/pdf' })
      const file = new File([blob], 'form.pdf', { type: 'application/pdf' })
      const fields = await extractPdfFields(file)
      const normalizedFields = normalizeParsedPdfFields(fields)
      setParsedPdfFields(normalizedFields)
      setSelectedPdfField(normalizedFields[0] || null)
      ensureFieldConfigsForParsedFields(normalizedFields)
      applyAutoRadioGroups(normalizedFields, 'merge')
      await renderPdfWithHighlight(base64)
      alert('Audit updated PDF fields in the editor. Click Save Changes to persist.')
    } catch (e) {
      console.error('Re-extract PDF fields failed:', e)
      alert(e instanceof Error ? e.message : 'Failed to re-extract PDF fields')
    } finally {
      setReExtractingPdfFields(false)
    }
  }

  const handleFixFormJson = async () => {
    if (!managementDetails.formName) return
    setFixingFormJson(true)
    try {
      // Fetch the latest saved form definition, then normalize nested group placeholders.
      const formData = await formService.fetchFormByName(managementDetails.formName)
      if (!formData) throw new Error('Could not load form for fix')

      const steps: any[] = Array.isArray(formData.steps) ? formData.steps : []
      const groupById = new Map<string, any>()

      const indexGroups = (node: any) => {
        if (!node) return
        if (node.type === 'group' && typeof node.id === 'string') {
          // Prefer the full definition (with questions) if we see multiple.
          const existing = groupById.get(node.id)
          const existingHasQuestions = Array.isArray(existing?.questions) && existing?.questions.length > 0
          const nodeHasQuestions = Array.isArray(node.questions) && node.questions.length > 0
          if (!existing || (!existingHasQuestions && nodeHasQuestions)) {
            groupById.set(node.id, node)
          }
        }
        if (node.type === 'group' && Array.isArray(node.questions)) {
          for (const q of node.questions) indexGroups(q)
        }
      }

      for (const step of steps) {
        for (const q of step?.questions || []) indexGroups(q)
      }

      const normalizeQuestion = (node: any): any => {
        if (!node) return node
        if (node.type !== 'group') return node

        const normalized = { ...node }

        // Some older/broken editor states may have embedded a group without its `questions`.
        // If we have the real group definition elsewhere, backfill its questions.
        if (!Array.isArray(normalized.questions)) {
          const full = groupById.get(normalized.id)
          if (full && Array.isArray(full.questions)) {
            normalized.questions = full.questions
          } else {
            normalized.questions = []
          }
        }

        normalized.mode = normalized.mode || 'fields'
        normalized.layout = normalized.layout || 'stack'
        normalized.showHeading = normalized.showHeading ?? true
        normalized.showDescription = normalized.showDescription ?? false
        normalized.required = normalized.required !== false

        normalized.questions = (normalized.questions || []).map(normalizeQuestion)
        return normalized
      }

      const normalizedSteps = steps.map((step: any) => ({
        ...step,
        questions: (step?.questions || []).map(normalizeQuestion),
      }))
      const duplicateToolNormalizedSteps = normalizeDuplicateToolsInSteps(normalizedSteps)

      const activePdfBase64 = managementDetails.pdfBase64 || formData.pdf?.base64 || ''
      const activePdfFieldFallback = managementDetails.pdfBase64
        ? normalizeParsedPdfFields(parsedPdfFields)
        : normalizeParsedPdfFields(formData.pdf?.fields || [])

      let normalizedPdfFields = activePdfFieldFallback
      if (activePdfBase64) {
        try {
          const pdfData = atob(activePdfBase64)
          const pdfArray = new Uint8Array(pdfData.length)
          for (let i = 0; i < pdfData.length; i++) pdfArray[i] = pdfData.charCodeAt(i)
          const blob = new Blob([pdfArray], { type: 'application/pdf' })
          const file = new File([blob], 'form.pdf', { type: 'application/pdf' })
          const extracted = await extractPdfFields(file)
          normalizedPdfFields = normalizeParsedPdfFields(extracted)
        } catch (reextractErr) {
          console.warn('Fix form JSON: PDF re-extract failed, using existing fields fallback', reextractErr)
        }
      }

      const validPdfFieldIds = new Set<string>()
      for (const f of normalizedPdfFields) {
        const ids = [f?.name, f?.acroName]
        for (const id of ids) {
          const raw = String(id || '').trim()
          if (!raw) continue
          validPdfFieldIds.add(raw)
          const normalized = normalizePdfLookupKey(raw)
          if (normalized) validPdfFieldIds.add(normalized)
        }
      }

      const isKnownPdfFieldId = (rawId: any): boolean => {
        const raw = String(rawId || '').trim()
        if (!raw) return false
        if (validPdfFieldIds.has(raw)) return true
        const normalized = normalizePdfLookupKey(raw)
        return !!normalized && validPdfFieldIds.has(normalized)
      }

      const pruneQuestionsMissingInPdf = (node: any): any | null => {
        if (!node) return null
        if (node.type === 'group') {
          const prunedChildren = (node.questions || [])
            .map((child: any) => pruneQuestionsMissingInPdf(child))
            .filter(Boolean)
          if (!prunedChildren.length) return null
          return { ...node, questions: prunedChildren }
        }

        const requestedId = String(node?.pdfField?.id || node?.pdfField?.acroName || node?.id || '').trim()
        if (!requestedId) return node
        if (isKnownPdfFieldId(requestedId)) return node
        return null
      }

      const prunedSteps = duplicateToolNormalizedSteps.map((step: any) => ({
        ...step,
        questions: (step?.questions || [])
          .map((q: any) => pruneQuestionsMissingInPdf(q))
          .filter(Boolean),
      }))

      const countLeafQuestionsInSteps = (stepsToCount: any[]): number => {
        let count = 0
        const walk = (node: any) => {
          if (!node) return
          if (node.type === 'group') {
            for (const child of node.questions || []) walk(child)
            return
          }
          count += 1
        }
        for (const step of stepsToCount || []) {
          for (const q of step?.questions || []) walk(q)
        }
        return count
      }

      const collectLeafMeta = (stepsToScan: any[]): Array<{
        id: string
        labelKey: string
        pdfKeys: string[]
      }> => {
        const out: Array<{ id: string; labelKey: string; pdfKeys: string[] }> = []
        const walk = (node: any) => {
          if (!node) return
          if (node.type === 'group') {
            for (const child of node.questions || []) walk(child)
            return
          }
          const id = String(node?.id || '').trim()
          if (!id) return
          const labelKey = normalizePdfLookupKey(String(node?.label || id))
          const pdfKeys = [
            normalizePdfLookupKey(String(node?.pdfField?.id || '')),
            normalizePdfLookupKey(String(node?.pdfField?.acroName || '')),
          ].filter(Boolean)
          out.push({
            id,
            labelKey,
            pdfKeys: Array.from(new Set(pdfKeys)),
          })
        }
        for (const step of stepsToScan || []) {
          for (const q of step?.questions || []) walk(q)
        }
        return out
      }

      const originalLeafMeta = collectLeafMeta(duplicateToolNormalizedSteps)
      const survivingLeafMeta = collectLeafMeta(prunedSteps)
      const survivingIdSet = new Set<string>(survivingLeafMeta.map(m => m.id))
      const survivingByPdfKey = new Map<string, string[]>()
      const survivingByLabelKey = new Map<string, string[]>()
      const originalMetaById = new Map<string, { labelKey: string; pdfKeys: string[] }>()

      for (const m of originalLeafMeta) {
        originalMetaById.set(m.id, { labelKey: m.labelKey, pdfKeys: m.pdfKeys })
      }
      for (const m of survivingLeafMeta) {
        if (m.labelKey) {
          const existing = survivingByLabelKey.get(m.labelKey) || []
          if (!existing.includes(m.id)) existing.push(m.id)
          survivingByLabelKey.set(m.labelKey, existing)
        }
        for (const key of m.pdfKeys) {
          const existing = survivingByPdfKey.get(key) || []
          if (!existing.includes(m.id)) existing.push(m.id)
          survivingByPdfKey.set(key, existing)
        }
      }

      const resolveSourceQuestionId = (sourceRef: any): string | null => {
        const raw = String(sourceRef || '').trim()
        if (!raw) return null
        if (survivingIdSet.has(raw)) return raw

        const rawKey = normalizePdfLookupKey(raw)
        const originalMeta = originalMetaById.get(raw)
        const sourcePdfKeys = Array.from(
          new Set<string>([
            ...(rawKey ? [rawKey] : []),
            ...(originalMeta?.pdfKeys || []),
          ].filter(Boolean)),
        )
        for (const key of sourcePdfKeys) {
          const matches = survivingByPdfKey.get(key)
          if (matches && matches.length > 0) return matches[0]
        }

        const labelCandidates = Array.from(
          new Set<string>([
            ...(originalMeta?.labelKey ? [originalMeta.labelKey] : []),
            ...(rawKey ? [rawKey] : []),
          ].filter(Boolean)),
        )
        for (const labelKey of labelCandidates) {
          const matches = survivingByLabelKey.get(labelKey)
          if (matches && matches.length > 0) return matches[0]
        }

        return null
      }

      const toolSourceConfigKeys = ['prefillSource', 'nearbySourceField', 'locationField'] as const
      let remappedSourceLinks = 0
      let unresolvedSourceLinks = 0

      const remapQuestionToolSources = (node: any): any => {
        if (!node) return node
        if (node.type === 'group') {
          return {
            ...node,
            questions: (node.questions || []).map((child: any) => remapQuestionToolSources(child)),
          }
        }
        const tool = node?.tool
        const config = tool?.config
        let nextNode = node
        if (tool && config && typeof config === 'object') {
          let changed = false
          const nextConfig: any = { ...config }
          for (const sourceKey of toolSourceConfigKeys) {
            const currentRef = String(config?.[sourceKey] || '').trim()
            if (!currentRef) continue
            const resolved = resolveSourceQuestionId(currentRef)
            if (resolved && resolved !== currentRef) {
              nextConfig[sourceKey] = resolved
              remappedSourceLinks += 1
              changed = true
            } else if (!resolved) {
              unresolvedSourceLinks += 1
            }
          }
          if (changed) {
            nextNode = {
              ...node,
              tool: {
                ...tool,
                config: nextConfig,
              },
            }
          }
        }

        const signatureSource = String(nextNode?.signatureSource || '').trim()
        if (nextNode?.type === SIGNATURE_DATE_TYPE && signatureSource) {
          const resolved = resolveSourceQuestionId(signatureSource)
          if (resolved && resolved !== signatureSource) {
            nextNode = { ...nextNode, signatureSource: resolved }
            remappedSourceLinks += 1
          } else if (!resolved) {
            unresolvedSourceLinks += 1
          }
        }
        return nextNode
      }

      const remappedSteps = prunedSteps.map((step: any) => ({
        ...step,
        questions: (step?.questions || []).map((q: any) => remapQuestionToolSources(q)),
      }))
      const prunedQuestionCount = Math.max(0, countLeafQuestionsInSteps(duplicateToolNormalizedSteps) - countLeafQuestionsInSteps(prunedSteps))

      const fixedForm = {
        ...formData,
        steps: remappedSteps,
        pdf: (formData.pdf || activePdfBase64)
          ? {
              ...(formData.pdf || {}),
              base64: activePdfBase64 || formData.pdf?.base64 || '',
              fields: normalizedPdfFields,
            }
          : formData.pdf,
      }

      await formService.saveForm(managementDetails.formName, fixedForm, {
        previousFormName: editingSourceFormName || managementDetails.formName,
      })
      await formService.loadForms(true)
      const loadedForms = formService.getForms()
      const loadedWorkflows = formService.getWorkflows()
      const loadedCategories = formService.getCategories()
      setForms(loadedForms)
      setWorkflows(loadedWorkflows)
      setCategories(loadedCategories)

      // Re-load into editor state so the tree/preview reflects the corrected JSON.
      await handleEditFormSelect(managementDetails.formName)
      console.info('Form audit summary:', {
        remappedSourceLinks,
        unresolvedSourceLinks,
        prunedQuestionCount,
      })
      alert(
        `Form audit complete. JSON and PDF field mappings were upgraded. ` +
        `Remapped prefill links: ${remappedSourceLinks}. ` +
        `Unresolved prefill links: ${unresolvedSourceLinks}. ` +
        `Removed stale fields: ${prunedQuestionCount}.`
      )
    } catch (e: any) {
      console.error('Fix form JSON failed:', e)
      alert(e?.message || 'Failed to fix form JSON')
    } finally {
      setFixingFormJson(false)
    }
  }

  const handleAuditForm = async () => {
    if (auditingForm) return
    setAuditingForm(true)
    try {
      // Edit mode: run full server-backed fix/upgrade + reload.
      if (managementMode === 'edit' && managementDetails.formName) {
        await handleFixFormJson()
      } else {
        // Create mode (or unsaved edits): at least re-extract and normalize current PDF fields.
        await handleReExtractPdfFields()
      }
    } finally {
      setAuditingForm(false)
    }
  }

  // UI helper: badge color per PDF field type
  const getPdfTypeBadgeClass = (type: string): string => {
    const t = type.toLowerCase()
    if (t === 'checkbox') return 'badge-success'
    if (t === 'radio') return 'badge-warning'
    if (t === 'select') return 'badge-info'
    if (t === 'signature') return 'badge-secondary'
    return 'badge-neutral'
  }

  // UI helper: icon for PDF field type (declutter - icons instead of text badges)
  const getPdfTypeIcon = (type: string) => {
    const t = type.toLowerCase()
    if (t === 'checkbox') return faSquareCheck
    if (t === 'radio') return faCircleDot
    if (t === 'select') return faList
    if (t === 'signature') return faSignature
    return faFont
  }

  const normalizePdfFieldName = (raw: string): string => {
    const s = String(raw ?? '').trim()
    return s
      .replace(/\.undefined$/i, '')
      .replace(/\s*undefined$/i, '')
  }

  const normalizePdfLookupKey = (raw: string): string =>
    normalizePdfFieldName(raw).replace(/\s+/g, ' ').trim().toLowerCase()

  const normalizeParsedPdfFields = (fields: any[]): PdfField[] => {
    if (!Array.isArray(fields)) return []
    const normalizedBase = fields
      .map((f: any, idx: number): PdfField | null => {
        const acroName = String(f?.acroName || f?.name || '').trim()
        const logicalName = String(f?.name || acroName).trim()
        if (!logicalName) return null

        const inputWidgets = Array.isArray(f?.widgets) ? f.widgets : []
        const normalizedWidgets: PdfFieldWidget[] = inputWidgets
          .map((w: any, widx: number) => {
            const rect =
              w?.rect &&
              typeof w.rect.x === 'number' &&
              typeof w.rect.y === 'number' &&
              typeof w.rect.width === 'number' &&
              typeof w.rect.height === 'number'
                ? { x: w.rect.x, y: w.rect.y, width: w.rect.width, height: w.rect.height }
                : undefined
            const page = typeof w?.page === 'number' ? w.page : undefined
            return {
              widgetId: String(w?.widgetId || `${logicalName}::${widx + 1}`),
              rect,
              page,
            }
          })
          .filter((w: PdfFieldWidget) => !!w.rect || typeof w.page === 'number')

        const legacyWidget =
          f?.rect || typeof f?.page === 'number'
            ? [{
                widgetId: `${logicalName}::legacy`,
                rect: f.rect,
                page: typeof f.page === 'number' ? f.page : undefined,
              }]
            : []

        const widgets = normalizedWidgets.length > 0 ? normalizedWidgets : legacyWidget
        const primary = widgets.find(w => !!w.rect) || widgets[0]

        return {
          name: logicalName,
          acroName,
          type: String(f?.type || 'text'),
          options: Array.isArray(f?.options) ? f.options : undefined,
          radioGroup: typeof f?.radioGroup === 'string' && f.radioGroup.trim() ? f.radioGroup.trim() : undefined,
          optionValue: typeof f?.optionValue === 'string' && f.optionValue.trim() ? f.optionValue.trim() : undefined,
          rect: primary?.rect || f?.rect,
          page: typeof primary?.page === 'number' ? primary.page : (typeof f?.page === 'number' ? f.page : 0),
          widgets,
          instanceCount: widgets.length > 0 ? widgets.length : (typeof f?.instanceCount === 'number' ? f.instanceCount : 1),
        }
      })
      .filter(Boolean) as PdfField[]

    const usedNames = new Set<string>()
    const ensureUniqueLogicalName = (base: string): string => {
      const candidate = String(base || 'field').trim() || 'field'
      if (!usedNames.has(candidate)) {
        usedNames.add(candidate)
        return candidate
      }
      let n = 2
      while (usedNames.has(`${candidate}__dup_${n}`)) n += 1
      const next = `${candidate}__dup_${n}`
      usedNames.add(next)
      return next
    }

    const expanded: PdfField[] = []
    for (const f of normalizedBase) {
      const widgets = Array.isArray(f.widgets) ? f.widgets : []
      if (f.radioGroup || widgets.length <= 1) {
        expanded.push({
          ...f,
          name: ensureUniqueLogicalName(f.name),
          widgets: widgets.length > 0 ? widgets : (f.rect ? [{ widgetId: `${f.acroName || f.name}::1`, rect: f.rect, page: f.page }] : []),
          instanceCount: Math.max(1, f.instanceCount || widgets.length || 1),
        })
        continue
      }

      // Split duplicate widgets into independent logical rows that still point to the same AcroForm field.
      widgets.forEach((w, idx) => {
        const baseName = idx === 0 ? f.name : `${f.name}__dup_${idx + 1}`
        expanded.push({
          ...f,
          name: ensureUniqueLogicalName(baseName),
          rect: w.rect || f.rect,
          page: typeof w.page === 'number' ? w.page : f.page,
          widgets: [{ ...w, widgetId: `${f.acroName || f.name}::${idx + 1}` }],
          instanceCount: widgets.length,
        })
      })
    }

    return expanded
  }

  const ensureFieldConfigsForParsedFields = (fields: PdfField[]) => {
    setFieldConfigs(prev => {
      const next = { ...prev }
      for (let i = 0; i < fields.length; i++) {
        const f = fields[i]
        if (next[f.name]) continue
        const rawLabelCandidate = (f.options && f.options.length && f.options[0]) ? f.options[0] : f.name.replace(/__dup_\d+$/i, '')
        const defaultLabel = (rawLabelCandidate && !String(rawLabelCandidate).includes('undefined')) ? rawLabelCandidate : `Option ${i + 1}`
        if (f.radioGroup) {
          const optionLabel = f.optionValue || (f.options && f.options[0]) || defaultLabel
          next[f.name] = {
            label: (optionLabel && !String(optionLabel).includes('undefined')) ? String(optionLabel) : defaultLabel,
            type: 'checkbox',
            description: '',
            required: true,
            readonly: false,
            fillerStage: 1,
            options: f.options && f.options.length ? f.options : [String(optionLabel)],
            tool: undefined,
            showDescription: false,
          }
          continue
        }
        next[f.name] = {
          label: defaultLabel,
          type: f.type === 'checkbox' ? 'checkbox' : (f.type === 'select' ? 'select' : (f.type === 'radio' ? 'radio' : 'text')),
          description: '',
          required: true,
          readonly: false,
          fillerStage: 1,
          options: f.type === 'select' || f.type === 'radio' ? (f.options && f.options.length ? f.options : ['Option 1', 'Option 2']) : undefined,
          tool: undefined,
          showDescription: false,
        }
      }
      return next
    })
  }

  const applyAutoRadioGroups = (fields: PdfField[], mode: 'seed' | 'merge' = 'seed') => {
    const autoGroups = buildAutoRadioGroupsFromFields(fields)
    if (!autoGroups.length) return
    const names = fields.map(f => f.name)
    const next = mergeAutoRadioGroups({ pages, fieldGroups }, autoGroups, mode, names)
    setPages(next.pages)
    setFieldGroups(next.fieldGroups)

    setGroupConfigs(prev => {
      const nextCfg = { ...prev }
      for (const g of autoGroups) {
        nextCfg[g.id] = {
          layout: 'stack',
          showHeading: true,
          showDescription: false,
          ...(nextCfg[g.id] || {}),
          mode: 'radio',
        }
      }
      return nextCfg
    })

    setExpandedGroups(prev => {
      const nextSet = new Set(prev)
      for (const g of autoGroups) nextSet.add(g.id)
      return nextSet
    })
  }

  const hasAnswerValue = (v: any): boolean => {
    if (v === undefined || v === null) return false
    if (typeof v === 'string' && v.trim() === '') return false
    return true
  }

  type DuplicateQuestionMaps = {
    questionToPdfKey: Map<string, string>
    pdfKeyToQuestions: Map<string, string[]>
  }

  const buildDuplicateQuestionMapsFromForm = (formLike: any): DuplicateQuestionMaps => {
    const questionToPdfKey = new Map<string, string>()
    const pdfKeyToQuestions = new Map<string, string[]>()
    const walk = (node: any) => {
      if (!node) return
      if (node.type === 'group') {
        for (const child of node.questions || []) walk(child)
        return
      }
      const qid = String(node.id || '').trim()
      const rawPdfId = String(node?.pdfField?.id || '').trim()
      const pdfKey = normalizePdfLookupKey(rawPdfId)
      if (!qid || !pdfKey) return
      questionToPdfKey.set(qid, pdfKey)
      if (!pdfKeyToQuestions.has(pdfKey)) pdfKeyToQuestions.set(pdfKey, [])
      pdfKeyToQuestions.get(pdfKey)!.push(qid)
    }
    for (const step of formLike?.steps || []) {
      for (const q of step?.questions || []) walk(q)
    }
    return { questionToPdfKey, pdfKeyToQuestions }
  }

  const runtimeDuplicateMaps = React.useMemo(
    () => buildDuplicateQuestionMapsFromForm(currentForm as any),
    [currentForm],
  )

  const propagateDuplicateAnswers = (
    answersDraft: { [key: string]: any },
    preferredQuestionId?: string,
    maps: DuplicateQuestionMaps = runtimeDuplicateMaps,
  ): { [key: string]: any } => {
    const next = answersDraft
    for (const [, qids] of maps.pdfKeyToQuestions) {
      if (!qids || qids.length < 2) continue
      let canonical: any = undefined
      if (preferredQuestionId && qids.includes(preferredQuestionId) && hasAnswerValue(next[preferredQuestionId])) {
        canonical = next[preferredQuestionId]
      } else {
        const firstWithValue = qids.find(id => hasAnswerValue(next[id]))
        canonical = firstWithValue ? next[firstWithValue] : next[qids[0]]
      }
      for (const qid of qids) next[qid] = canonical
    }
    return next
  }

  const editorDuplicateGroups = React.useMemo(() => {
    const byKey = new Map<string, string[]>()
    for (const f of parsedPdfFields) {
      const key = normalizePdfLookupKey(f.acroName || f.name)
      if (!key) continue
      if (!byKey.has(key)) byKey.set(key, [])
      byKey.get(key)!.push(f.name)
    }
    return byKey
  }, [parsedPdfFields])

  const selectedPdfDuplicateMeta = React.useMemo(() => {
    const fieldName = selectedPdfField?.name
    if (!fieldName) return { isDuplicate: false, isPrimary: true, primaryField: '', count: 1 }
    const selectedField = parsedPdfFields.find(f => f.name === fieldName)
    if (!selectedField) return { isDuplicate: false, isPrimary: true, primaryField: fieldName, count: 1 }
    const key = normalizePdfLookupKey(selectedField.acroName || selectedField.name)
    const members = key ? (editorDuplicateGroups.get(key) || []) : []
    if (members.length <= 1) return { isDuplicate: false, isPrimary: true, primaryField: fieldName, count: 1 }
    return {
      isDuplicate: true,
      isPrimary: members[0] === fieldName,
      primaryField: members[0],
      count: members.length,
    }
  }, [selectedPdfField?.name, parsedPdfFields, editorDuplicateGroups])

  const normalizeDuplicateToolsInSteps = (steps: any[]): any[] => {
    const cloned = (steps || []).map((s: any) => ({
      ...s,
      questions: (s?.questions || []).map((q: any) => ({ ...q })),
    }))
    const leafRefs: Array<{ node: any; key: string; order: number }> = []
    let order = 0
    const walk = (node: any) => {
      if (!node) return
      if (node.type === 'group') {
        node.questions = (node.questions || []).map((q: any) => ({ ...q }))
        for (const child of node.questions || []) walk(child)
        return
      }
      const key = normalizePdfLookupKey(String(node?.pdfField?.id || ''))
      if (key) leafRefs.push({ node, key, order: order++ })
    }
    for (const step of cloned) {
      for (const q of step.questions || []) walk(q)
    }
    const byKey = new Map<string, Array<{ node: any; order: number }>>()
    for (const ref of leafRefs) {
      if (!byKey.has(ref.key)) byKey.set(ref.key, [])
      byKey.get(ref.key)!.push({ node: ref.node, order: ref.order })
    }
    for (const [, refs] of byKey) {
      if (refs.length < 2) continue
      refs.sort((a, b) => a.order - b.order)
      const primary = refs[0].node
      for (let i = 1; i < refs.length; i++) {
        const node = refs[i].node
        if (node.tool) {
          // Duplicate instances inherit answer from the primary field; tools stay on primary only.
          delete node.tool
        }
      }
      if (primary?.tool) {
        for (const ref of refs) {
          if (!ref.node?.id || ref.node.id === primary.id) continue
          ref.node.excludeFromCache = primary.excludeFromCache
        }
      }
    }
    return cloned
  }

  // Selection helpers and computed list
  const keyForField = (name: string) => `field:${name}`
  const keyForGroup = (id: string) => `group:${id}`
  const parsedFieldNames = parsedPdfFields.map(f => f.name)

  const getFieldNamesUnderPage = (page: PageDef): string[] => fieldNamesUnderPage(page, fieldGroups)

  const allSelectableKeys: string[] = walkSelectableKeys({ pages, fieldGroups }, parsedFieldNames)

  const setSingleSelection = (key: string) => {
    setSelectedKeys([key])
    if (key.startsWith('field:')) {
      const name = key.slice(6)
      const f = parsedPdfFields.find(p => p.name === name) || null
      setSelectedPdfField(f)
      setSelectedGroup(null)
      setSelectedPage(null)
    } else if (key.startsWith('group:')) {
      const id = key.slice(6)
      const g = fieldGroups.find(gr => gr.id === id) || null
      setSelectedGroup(g)
      setSelectedPdfField(null)
      setSelectedPage(null)
    } else if (key.startsWith('page:')) {
      const id = key.slice(5)
      const p = pages.find(pg => pg.id === id) || null
      setSelectedGroup(null)
      setSelectedPdfField(null)
      setSelectedPage(p)
    }
  }

  const handleItemClick = (e: React.MouseEvent, key: string) => {
    const isMulti = e.ctrlKey || e.metaKey
    const isRange = e.shiftKey && selectedKeys.length > 0
    if (isRange) {
      const keys = allSelectableKeys
      const start = keys.indexOf(selectedKeys[selectedKeys.length - 1])
      const end = keys.indexOf(key)
      if (start !== -1 && end !== -1) {
        const min = Math.min(start, end)
        const max = Math.max(start, end)
        const range = keys.slice(min, max + 1)
        setSelectedKeys(range)
        setSelectedPdfField(null)
        setSelectedGroup(null)
        setSelectedPage(null)
        return
      } else {
        setSingleSelection(key)
        return
      }
    }
    if (isMulti) {
      setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])
      setSelectedPdfField(null)
      setSelectedGroup(null)
      setSelectedPage(null)
      return
    }
    setSingleSelection(key)
  }

  const createGroupFromSelection = async () => {
    const onlyFields = selectedKeys.length > 0 && selectedKeys.every(k => k.startsWith('field:'))
    if (!onlyFields) return
    const name = (await promptName('Name this group', 'Group', 'Group name'))?.trim()
    if (!name) return
    const fieldNames = selectedKeys.map(k => k.slice(6))
    const id = `${Date.now()}_${Math.random().toString(36).slice(2,8)}`
    const group: FieldGroup = {
      id,
      name,
      fieldNames,
      children: fieldNames.map(fieldName => ({ type: 'field' as const, fieldName })),
    }
    const next = insertGroupFromFields({ pages, fieldGroups }, group, fieldNames, parsedFieldNames)
    setPages(next.pages)
    setFieldGroups(next.fieldGroups)
    setGroupConfigs(prev => ({ ...prev, [id]: { layout: 'stack', showHeading: true, showDescription: false, mode: 'fields' } }))
    setExpandedGroups(prev => new Set(prev).add(id))
    setSelectedKeys([keyForGroup(id)])
    setSelectedGroup(group)
    setSelectedPdfField(null)
    setSelectedPage(null)
  }

  const deleteSelected = () => {
    const groupIds = selectedKeys.filter(k => k.startsWith('group:')).map(k => k.slice(6))
    const pageIds = selectedKeys.filter(k => k.startsWith('page:')).map(k => k.slice(5))
    if (groupIds.length === 0 && pageIds.length === 0) return
    if (groupIds.length > 0) {
      // Remove groups and clean up page references
      setFieldGroups(prev => prev.filter(g => !groupIds.includes(g.id)))
      setPages(prev => prev.map(p => ({
        ...p,
        items: p.items.filter(it => !(it.type === 'group' && groupIds.includes(it.key)))
      })))
    }
    if (pageIds.length > 0) {
      setPages(prev => prev.filter(p => !pageIds.includes(p.id)))
    }
    setSelectedKeys([])
    setSelectedGroup(null)
    setSelectedPdfField(null)
    setSelectedPage(null)
  }

  const groupSelectionIntoPage = async () => {
    if (selectedKeys.length === 0) return
    const name = (await promptName('Name this page', `Page ${pages.length + 1}`, 'Page name'))?.trim()
    if (!name) return
    const groupIdsToMove = new Set(selectedKeys.filter(k => k.startsWith('group:')).map(k => k.slice(6)))
    const fieldNamesToMove = new Set(selectedKeys.filter(k => k.startsWith('field:')).map(k => k.slice(6)))
    const items: PageDef['items'] = selectedKeys.map(k => k.startsWith('group:') ? { type: 'group', key: k.slice(6) } : { type: 'field', key: k.slice(6) })
    const page: PageDef = { id: `${Date.now()}_${Math.random().toString(36).slice(2,8)}`, name, items }
    setPages(prev => {
      const updated = prev.map(p => ({
        ...p,
        items: p.items.filter(it =>
          it.type === 'group' ? !groupIdsToMove.has(it.key) : !fieldNamesToMove.has(it.key)
        )
      })).filter(p => p.items.length > 0)
      return [...updated, page]
    })
    setExpandedPages(prev => new Set(prev).add(page.id))
  }

  const renamePage = (id: string, newName: string) => {
    const trimmed = newName?.trim()
    if (!trimmed) return
    setPages(prev => prev.map(p => p.id === id ? { ...p, name: trimmed } : p))
    setEditingNameKey(null)
    setEditingNameValue('')
  }

  const renameGroup = (id: string, newName: string) => {
    const trimmed = newName?.trim()
    if (!trimmed) return
    setFieldGroups(prev => prev.map(g => g.id === id ? { ...g, name: trimmed } : g))
    setEditingNameKey(null)
    setEditingNameValue('')
  }

  const startEditingName = (key: string, currentName: string) => {
    setEditingNameKey(key)
    setEditingNameValue(currentName)
  }

  const commitRename = () => {
    if (!editingNameKey) return
    if (editingNameKey.startsWith('page:')) {
      renamePage(editingNameKey.slice(5), editingNameValue)
    } else if (editingNameKey.startsWith('group:')) {
      renameGroup(editingNameKey.slice(6), editingNameValue)
    }
  }

  const cancelRename = () => {
    setEditingNameKey(null)
    setEditingNameValue('')
  }

  // Validation before save
  const validateBeforeSave = (): string[] => {
    const errors: string[] = []
    if (!managementDetails.formName?.trim()) {
      errors.push('Form name is required')
    }
    if (!managementDetails.pdfBase64) {
      errors.push('A fillable PDF must be uploaded')
    }
    for (const extra of normalizeFillers(managementDetails.fillers).slice(1)) {
      if (!(extra as any).employeeId) {
        errors.push(`Filler stage ${extra.stage} needs an assigned employee`)
      }
    }
    // Determine pages to validate (match buildFormJson logic)
    const pagesToCheck = persistPages({ pages, fieldGroups }, parsedFieldNames)

    for (const p of pagesToCheck) {
      for (const itm of p.items) {
        if (itm.type === 'group') {
          const g = fieldGroups.find(gr => gr.id === itm.key)
          if (!g) continue
          const gCfg = groupConfigs[g.id] || {}
          if (gCfg.mode === 'radio') {
            const memberNames = collectLeafFieldNames(fieldGroups, g.id)
            const nonCheckbox = memberNames.filter(fn => (fieldConfigs[fn]?.type || 'text') !== 'checkbox')
            if (nonCheckbox.length) {
              errors.push(`Group "${g.name}" is set to Radio but has non-checkbox items: ${nonCheckbox.join(', ')}`)
            }
          }
          for (const fname of collectLeafFieldNames(fieldGroups, g.id)) {
            const cfg = fieldConfigs[fname]
            if (!cfg) continue
            if ((cfg.type === 'select' || cfg.type === 'radio') && !(cfg.options && cfg.options.length)) {
              errors.push(`Field "${cfg.label || fname}" requires at least one option`)
            }
            if (cfg.required !== false && cfg.readonly) {
              errors.push(`Field "${cfg.label || fname}" cannot be both required and read only`)
            }
            if (cfg.type === SIGNATURE_DATE_TYPE) {
              const src = String(cfg.signatureSource || '').trim()
              if (!src) {
                errors.push(`Field "${cfg.label || fname}" must be linked to a signature field`)
              } else if (src === fname) {
                errors.push(`Field "${cfg.label || fname}" cannot link to itself`)
              } else if (fieldConfigs[src]?.type !== 'signature') {
                errors.push(`Field "${cfg.label || fname}" must link to a signature field`)
              }
            }
          }
        } else {
          const fname = itm.key
          const cfg = fieldConfigs[fname]
          if (!cfg) continue
          if ((cfg.type === 'select' || cfg.type === 'radio') && !(cfg.options && cfg.options.length)) {
            errors.push(`Field "${cfg.label || fname}" requires at least one option`)
          }
          if (cfg.required !== false && cfg.readonly) {
            errors.push(`Field "${cfg.label || fname}" cannot be both required and read only`)
          }
          if (cfg.type === SIGNATURE_DATE_TYPE) {
            const src = String(cfg.signatureSource || '').trim()
            if (!src) {
              errors.push(`Field "${cfg.label || fname}" must be linked to a signature field`)
            } else if (src === fname) {
              errors.push(`Field "${cfg.label || fname}" cannot link to itself`)
            } else if (fieldConfigs[src]?.type !== 'signature') {
              errors.push(`Field "${cfg.label || fname}" must link to a signature field`)
            }
          }
        }
      }
    }
    const fillers = normalizeFillers(managementDetails.fillers)
    if (fillers.length >= 2) {
      const counts = new Map<number, number>()
      for (const cfg of Object.values(fieldConfigs)) {
        const stage = Number(cfg?.fillerStage) >= 1 ? Number(cfg.fillerStage) : 1
        counts.set(stage, (counts.get(stage) || 0) + 1)
      }
      for (const gCfg of Object.values(groupConfigs)) {
        if (gCfg?.mode === 'radio') {
          const stage = Number(gCfg.fillerStage) >= 1 ? Number(gCfg.fillerStage) : 1
          counts.set(stage, (counts.get(stage) || 0) + 1)
        }
      }
      for (const extra of fillers.slice(1)) {
        if (!(counts.get(extra.stage) || 0)) {
          errors.push(`Filler stage ${extra.stage} has no fields assigned`)
        }
      }
    }
    return errors
  }


  
  // Completion modal state
  const [completionOpen, setCompletionOpen] = useState(false)
  const [completionBusy, setCompletionBusy] = useState(false)
  const [completionPhase, setCompletionPhase] = useState<'building' | 'uploading' | null>(null)
  const [uploadResult, setUploadResult] = useState<{ drive?: any; jobtread?: any } | null>(null)
  const [completionBlobUrl, setCompletionBlobUrl] = useState<string | null>(null)
  const [completionId, setCompletionId] = useState<string | null>(null)
  const [completionError, setCompletionError] = useState<string | null>(null)
  const [completionContinue, setCompletionContinue] = useState<(() => void) | null>(null)
  const [jobSelectModalOpen, setJobSelectModalOpen] = useState(false)
  const [jtFallbackSuggest, setJtFallbackSuggest] = useState<Array<{ id?: string; name?: string; number?: string }>>([])
  const [jtFallbackLoading, setJtFallbackLoading] = useState(false)
  const [jtFallbackQuery, setJtFallbackQuery] = useState('')
  const jtFallbackSelectedRef = React.useRef<{ id: string; number?: string } | null>(null)
  const jtFallbackTimerRef = React.useRef<number | null>(null)
  // Maps modal
  const [mapModal, setMapModal] = useState<{ open: boolean; fieldId?: string; config?: any }>(() => ({ open: false }))
  const [mapModalSearch, setMapModalSearch] = useState('')
  const [mapModalResults, setMapModalResults] = useState<Array<{ lat: string; lon: string; display_name: string }>>([])
  const [mapModalSelected, setMapModalSelected] = useState<{ lat: number; lon: number; display_name: string } | null>(null)
  const [mapModalLoading, setMapModalLoading] = useState(false)
  const mapModalSearchTimerRef = React.useRef<number | null>(null)
  // Validation failed modal (submit blocked by missing required / unconfirmed prefills)
  const [validationFailedModal, setValidationFailedModal] = useState<{
    open: boolean
    missingRequired: Array<{ id: string; label: string; stepIndex: number; stepName?: string }>
    unconfirmedPrefills: Array<{ id: string; label: string; stepIndex: number; stepName?: string }>
  }>({ open: false, missingRequired: [], unconfirmedPrefills: [] })
  // Prefill review modal (checkbox confirmation before completion)
  const [prefillReviewModalOpen, setPrefillReviewModalOpen] = useState(false)
  const [prefillReviewConfirmed, setPrefillReviewConfirmed] = useState(false)

  const news = [
    'LTS wins new telecom contract',
    'Quarterly safety training announced',
    'Employee of the Month: Jane Doe',
  ]

  const recentActivity = [
    'User John submitted a new form',
    'Site 12 updated by Admin',
    'GIS data refreshed',
    'Resource "Safety Manual" added',
  ]

  // Helper functions to safely access ViewState properties
  const getCurrentTab = (): SelectionTab => {
    return view.view === 'selection' ? view.tab : 'workflows'
  }

  const getWorkflowName = (): string | undefined => {
    return view.view === 'form' ? view.formName : undefined
  }

  // Load forms on component mount
  useEffect(() => {
    const loadForms = async () => {
      try {
        setLoading(true)
        await formService.loadForms()
        const loadedForms = formService.getForms()
        const loadedWorkflows = formService.getWorkflows()
        const loadedCategories = formService.getCategories()
        const loadedPending = await formService.getPendingAssignments()
        const loadedAllPending = hasPermission('formsManager')
          ? await formService.getAllPendingAssignments()
          : []
        
        setForms(loadedForms)
        setWorkflows(loadedWorkflows)
        setCategories(loadedCategories)
        setPendingAssignments(loadedPending)
        setAllPendingAssignments(loadedAllPending)
        setView(v => {
          if (v.view !== 'selection') return v
          return { view: 'selection', tab: loadedPending.length > 0 ? 'pending' : 'workflows' }
        })
      } catch (error) {
        console.error('Failed to load forms:', error)
      } finally {
        setLoading(false)
      }
    }

    loadForms()
  }, [])

  // Handle navigation state from FormManagement page
  useEffect(() => {
    const state = location.state as { action?: 'create' | 'edit'; formName?: string } | null
    if (state?.action === 'create') {
      openFormManagement('create')
      // Clear the state to prevent re-triggering on refresh
      navigate(location.pathname, { replace: true, state: {} })
    } else if (state?.action === 'edit' && state?.formName) {
      const formName = state.formName
      navigate(location.pathname, { replace: true, state: {} })
      void handleEditFormSelect(formName)
    }
  }, [location.state])

  // Filter forms based on current filters
  const userCanFillForm = (form: { accessLevels?: string[]; permissions?: string[] }) => {
    if (Array.isArray(form.accessLevels)) return form.accessLevels.includes(String(user?.accessLevel || ''))
    const required = Array.isArray(form.permissions) ? form.permissions.filter(Boolean) : []
    if (!required.length) return true
    return required.some((key) => hasPermission(key))
  }

  const filteredForms = forms.filter(form => {
    if (!userCanFillForm(form)) return false
    if (filters.category && form.category !== filters.category) return false
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      return (
        form.formName.toLowerCase().includes(searchTerm) ||
        form.description.toLowerCase().includes(searchTerm) ||
        form.category.toLowerCase().includes(searchTerm)
      )
    }
    return true
  })

  // Tab selector for selection container
  const handleTabChange = (tab: SelectionTab) => {
    setView({ view: 'selection', tab })
  }

  // Handle form/workflow selection
  const refreshPending = async () => {
    try {
      const loadedPending = await formService.getPendingAssignments()
      setPendingAssignments(loadedPending)
    } catch {}
    if (!hasPermission('formsManager')) {
      setAllPendingAssignments([])
      return
    }
    try {
      const all = await formService.getAllPendingAssignments()
      setAllPendingAssignments(Array.isArray(all) ? all : [])
    } catch {
      setAllPendingAssignments([])
    }
  }

  const openFormForFill = async (form: FormData, opts?: { instanceId?: string }) => {
    flushFormCache()
    let instance: any = null
    try {
      if (opts?.instanceId) {
        const res = await formService.getInstance(opts.instanceId)
        instance = res?.instance
      } else if (isMultiFillerForm(form)) {
        const res = await formService.startInstance(form.formName)
        instance = res?.instance
      }
    } catch (e: any) {
      setStageSubmitNotice(e?.message || 'Could not open this form instance')
      return
    }

    const cacheOpts = instance
      ? { instanceId: instance.id, currentStage: instance.currentStage }
      : undefined
    const cached = loadCachedAnswers(form.formName, user?._id, form, cacheOpts) || {}
    const merged = { ...cached }
    for (const [k, v] of Object.entries(instance?.answers || {})) {
      if (v !== undefined && v !== null && v !== '') merged[k] = v
    }

    setCurrentForm(form)
    setCurrentStep(0)
    setFormAnswers(merged)
    setFormErrors({})
    setPrefilledFieldIds(new Set())
    setConfirmedPrefillIds(new Set())
    setActiveInstance(instance
      ? {
          id: instance.id,
          currentStage: instance.currentStage,
          totalStages: instance.totalStages,
          fillers: instance.fillers || [],
          startedByLabel: instance.startedByLabel || null,
        }
      : null)
    setView({ view: 'form', formName: form.formName })
  }

  const answersForCurrentStage = () => {
    if (!currentForm || !activeInstance) return formAnswers
    const writable = collectWritableFieldIds(currentForm, activeInstance.currentStage)
    const payload: Record<string, any> = {}
    for (const id of writable) {
      if (Object.prototype.hasOwnProperty.call(formAnswers, id)) payload[id] = formAnswers[id]
    }
    return payload
  }

  const handleFormSelect = (formName: string) => {
    const form = formService.getFormById(formName)
    if (form) {
      setActiveWorkflowName(undefined)
      void openFormForFill(form)
    }
  }

  const handlePendingSelect = (item: PendingFormAssignment) => {
    const form = formService.getFormById(item.formName)
    if (form) void openFormForFill(form, { instanceId: item.id })
  }

  const downloadPendingForm = async (item: PendingFormAssignment) => {
    if (downloadingId) return
    setDownloadingId(item.id)
    try {
      const result = await formService.downloadInstancePdf(item.id)
      const builtBase64 = result?.pdfBase64
      if (!builtBase64) throw new Error('Failed to build PDF')
      const pdfData = atob(builtBase64)
      const pdfArray = new Uint8Array(pdfData.length)
      for (let i = 0; i < pdfData.length; i++) pdfArray[i] = pdfData.charCodeAt(i)
      const blob = new Blob([pdfArray], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = result.fileName || `${item.formName || 'form'}-in-progress.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e: any) {
      setStageSubmitNotice(e?.message || 'Could not download this form')
    } finally {
      setDownloadingId(null)
    }
  }

  const confirmCancelInstance = async () => {
    if (!cancelTarget || cancelling) return
    setCancelling(true)
    try {
      await formService.cancelInstance(cancelTarget.id)
      setCancelTarget(null)
      await refreshPending()
    } catch (e: any) {
      setCancelTarget(null)
      setStageSubmitNotice(e?.message || 'Could not cancel this agreement')
    } finally {
      setCancelling(false)
    }
  }

  const renderPendingCard = (item: PendingFormAssignment, { showCancel }: { showCancel: boolean }) => {
    const yourTurn = item.yourTurn !== false
    return (
      <div key={item.id} className={`card bg-base-100 shadow-xl ${yourTurn ? '' : 'opacity-80'}`}>
        <div className="card-body">
          <h2 className="card-title text-lg">
            <FontAwesomeIcon icon={faFileAlt} className={yourTurn ? 'text-primary' : 'text-base-content/40'} />
            {item.label || item.formName}
          </h2>
          {item.description && (
            <p className="text-sm text-base-content/70 mb-2">{item.description}</p>
          )}
          {(item.currentStage || item.assignedBy || !yourTurn) && (
            <div className="flex flex-wrap gap-2 text-xs text-base-content/60 mb-4">
              {item.currentStage && item.totalStages && (
                <span>Stage {item.currentStage} of {item.totalStages}</span>
              )}
              {item.assignedBy && (
                <span>Started by {item.assignedBy}</span>
              )}
              {!yourTurn && (
                <span className="badge badge-ghost badge-sm">
                  {item.waitingOn ? `Waiting on ${item.waitingOn}` : 'Waiting on the next person'}
                </span>
              )}
            </div>
          )}
          <div className="card-actions justify-end gap-2">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              title="Download the form as it is filled in so far"
              disabled={downloadingId !== null}
              onClick={() => { void downloadPendingForm(item) }}
            >
              <FontAwesomeIcon icon={downloadingId === item.id ? faSpinner : faDownload} className={downloadingId === item.id ? 'fa-spin' : ''} />
              Download
            </button>
            {showCancel && (
              <button
                type="button"
                className="btn btn-ghost btn-sm text-error"
                onClick={() => setCancelTarget(item)}
              >
                <FontAwesomeIcon icon={faTrash} />
                Cancel
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!yourTurn}
              onClick={() => { if (yourTurn) handlePendingSelect(item) }}
            >
              {yourTurn ? 'Complete' : 'Not your turn'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const handleWorkflowSelect = (workflowName: string) => {
    const workflow = formService.getWorkflowByName(workflowName)
    const offered = workflow ? { ...workflow, forms: workflow.forms.filter((form) => userCanFillForm(form)) } : undefined
    if (offered && offered.forms.length > 0) {
      // Check if there are any optional forms in this workflow
      const hasOptionalForms = offered.forms.some(f => f.workflowOptional)
      
      if (hasOptionalForms) {
        const allFormNames = new Set(offered.forms.map(f => f.formName))
        setWorkflowSelectionModal({
          open: true,
          workflowName,
          forms: offered.forms,
          selectedFormNames: allFormNames
        })
      } else {
        startWorkflowWithForms(workflowName, offered.forms)
      }
    }
  }

  // Start the workflow with the specified forms
  const startWorkflowWithForms = (workflowName: string, formsToShow: FormData[]) => {
    if (formsToShow.length === 0) return
    setWorkflowFormsToShow(formsToShow)
    const firstForm = formsToShow[0]
    setActiveWorkflowName(workflowName)
    void openFormForFill(firstForm)
  }

  // Handle workflow selection modal continue
  const handleWorkflowSelectionContinue = () => {
    const { workflowName, forms, selectedFormNames } = workflowSelectionModal
    // Filter to only mandatory forms + selected optional forms
    const formsToShow = forms.filter(f => !f.workflowOptional || selectedFormNames.has(f.formName))
    setWorkflowSelectionModal({ open: false, workflowName: '', forms: [], selectedFormNames: new Set() })
    startWorkflowWithForms(workflowName, formsToShow)
  }

  // Toggle optional form selection in the modal
  const toggleOptionalFormSelection = (formName: string) => {
    setWorkflowSelectionModal(prev => {
      const newSelected = new Set(prev.selectedFormNames)
      if (newSelected.has(formName)) {
        newSelected.delete(formName)
      } else {
        newSelected.add(formName)
      }
      return { ...prev, selectedFormNames: newSelected }
    })
  }

  // Handle form navigation within workflow
  const handleSwitchForm = (formName: string) => {
    const form = formService.getFormById(formName)
    if (form) {
      if (form.workflow) {
        const formsInWf = workflowFormsToShow.length > 0 ? workflowFormsToShow : formService.getFormsInWorkflow(form.workflow)
        setActiveWorkflowName(formsInWf.length > 1 ? form.workflow : undefined)
      } else {
        setActiveWorkflowName(undefined)
      }
      void openFormForFill(form)
    }
  }

  // Handle step navigation - allow clicking any step
  const handleStepClick = (stepIndex: number) => {
    flushFormCache()
    if (currentForm && stepIndex >= 0 && stepIndex < currentForm.steps.length) {
      setCurrentStep(stepIndex)
    }
  }

  // Handle form answer changes
  const handleAnswerChange = (questionId: string, value: any) => {
    let latestAnswers: { [key: string]: any } = {}
    
    setFormAnswers(prev => {
      const baseNext = { ...prev, [questionId]: value }
      propagateDuplicateAnswers(baseNext, questionId)
      const changedQuestion = collectLeafQuestions(currentForm).find(q => q.id === questionId)
      if (changedQuestion?.type === 'signature') {
        applySignatureDateAnswers(baseNext, currentForm, { sourceFieldId: questionId, overwrite: true })
      }
      // Store latest answers for debounced calls below
      latestAnswers = baseNext
      // Note: JobTread prefill is now only triggered via onSelectSuggestion (explicit selection)
      return baseNext
    })
    
    // Trigger weather side-effects with debouncing (outside state setter to avoid async issues)
    // Use a microtask to ensure latestAnswers is populated
    queueMicrotask(() => {
      debouncedApplyWeatherTools(questionId, latestAnswers)
    })
    
    // Clear error when user starts typing
    if (formErrors[questionId]) {
      setFormErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[questionId]
        return newErrors
      })
    }
  }

  // Debounced save to form cache when answers change
  const cacheSaveTimerRef = React.useRef<number | null>(null)
  const flushFormCache = () => {
    if (cacheSaveTimerRef.current) {
      window.clearTimeout(cacheSaveTimerRef.current)
      cacheSaveTimerRef.current = null
    }
    if (currentForm?.cache?.enabled && user?._id) {
      saveCachedAnswers(
        currentForm.formName,
        user._id,
        formAnswers,
        currentForm,
        activeInstance ? { instanceId: activeInstance.id, currentStage: activeInstance.currentStage } : undefined,
      )
    }
  }
  useEffect(() => {
    if (!currentForm?.cache?.enabled || !user?._id) return
    if (cacheSaveTimerRef.current) window.clearTimeout(cacheSaveTimerRef.current)
    cacheSaveTimerRef.current = window.setTimeout(() => {
      saveCachedAnswers(
        currentForm.formName,
        user._id,
        formAnswers,
        currentForm,
        activeInstance ? { instanceId: activeInstance.id, currentStage: activeInstance.currentStage } : undefined,
      )
      cacheSaveTimerRef.current = null
    }, 500)
    return () => {
      if (cacheSaveTimerRef.current) {
        window.clearTimeout(cacheSaveTimerRef.current)
      }
    }
  }, [formAnswers, currentForm, user?._id, activeInstance])

  const instanceSaveTimerRef = React.useRef<number | null>(null)
  useEffect(() => {
    if (!activeInstance || !currentForm) return
    if (instanceSaveTimerRef.current) window.clearTimeout(instanceSaveTimerRef.current)
    instanceSaveTimerRef.current = window.setTimeout(() => {
      void formService.saveStage(activeInstance.id, answersForCurrentStage()).catch(() => {})
      instanceSaveTimerRef.current = null
    }, 800)
    return () => {
      if (instanceSaveTimerRef.current) window.clearTimeout(instanceSaveTimerRef.current)
    }
  }, [formAnswers, activeInstance, currentForm])

  // Current Date tool: auto-fill date fields when step is shown (only if empty)
  useEffect(() => {
    if (!currentForm || currentStep < 0 || currentStep >= currentForm.steps.length) return
    const step = currentForm.steps[currentStep]
    if (!step?.questions) return
    const dateFields: Array<{ id: string; config?: { format?: string } }> = []
    for (const q of step.questions) {
      if (q.type === 'group' && q.questions) {
        for (const gq of q.questions) {
          if ((gq as any).tool?.type === 'date' && isFieldPrefillable(gq as any, activeInstance?.currentStage ?? null)) dateFields.push({ id: (gq as any).id, config: (gq as any).tool?.config })
        }
      } else if ((q as any).tool?.type === 'date' && isFieldPrefillable(q as any, activeInstance?.currentStage ?? null)) {
        dateFields.push({ id: (q as any).id, config: (q as any).tool?.config })
      }
    }
    if (!dateFields.length) return
    setFormAnswers(prev => {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      let changed = false
      const next = { ...prev }
      const idsAdded: string[] = []
      for (const f of dateFields) {
        const current = prev[f.id]
        if (current === undefined || current === null || String(current).trim() === '') {
          const format = f.config?.format || 'MM/DD/YYYY'
          const dateStr = format === 'YYYY-MM-DD' ? `${y}-${m}-${d}` : `${m}/${d}/${y}`
          next[f.id] = dateStr
          idsAdded.push(f.id)
          changed = true
        }
      }
      if (idsAdded.length) setPrefilledFieldIds(p => new Set([...p, ...idsAdded]))
      if (!changed) return prev
      propagateDuplicateAnswers(next, idsAdded[0])
      return next
    })
  }, [currentForm, currentStep])

  // Signature date fields: fill from parent signature when empty (cache restore / first paint)
  useEffect(() => {
    if (!currentForm) return
    setFormAnswers(prev => {
      const next = { ...prev }
      const updatedIds = applySignatureDateAnswers(next, currentForm, { overwrite: false })
      if (!updatedIds.length) return prev
      propagateDuplicateAnswers(next, updatedIds[0])
      return next
    })
  }, [currentForm])

  // Current User tool: fetch contact/employee data and auto-fill when step is shown (only if empty)
  const userToolCacheRef = React.useRef<{ employeeId: string; contact: any; profile?: any } | null>(null)
  useEffect(() => {
    if (!currentForm || currentStep < 0 || currentStep >= currentForm.steps.length || !user) return
    const step = currentForm.steps[currentStep]
    if (!step?.questions) return
    const userFields: Array<{ id: string; config?: { prefillData?: string } }> = []
    for (const q of step.questions) {
      if (q.type === 'group' && q.questions) {
        for (const gq of q.questions) {
          if ((gq as any).tool?.type === 'user' && isFieldPrefillable(gq as any, activeInstance?.currentStage ?? null)) userFields.push({ id: (gq as any).id, config: (gq as any).tool?.config })
        }
      } else if ((q as any).tool?.type === 'user' && isFieldPrefillable(q as any, activeInstance?.currentStage ?? null)) {
        userFields.push({ id: (q as any).id, config: (q as any).tool?.config })
      }
    }
    if (!userFields.length) return

    let cancelled = false
    const run = async () => {
      let contact: { name?: string; preferredName?: string | null; position?: string; phone?: string; email?: string; department?: string; supervisor?: string; employeeId?: string } | null = null
      let profile: { firstName?: string; lastName?: string; preferredName?: string; position?: string; department?: string; supervisor?: string } | null = null

      if (user.employeeId) {
        const cache = userToolCacheRef.current
        if (cache?.employeeId === user.employeeId) {
          contact = cache.contact ?? null
          profile = cache.profile ?? null
        } else {
          try {
            contact = await profileService.getContactByEmployeeId(user.employeeId)
          } catch {
            try {
              profile = await profileService.getMyProfile()
            } catch {}
          }
          if (!cancelled && user.employeeId) userToolCacheRef.current = { employeeId: user.employeeId, contact: contact || undefined, profile: profile || undefined }
        }
      }

      const getVal = (key: string): string => {
        if (key === 'name') return contact?.name || (profile ? [profile.firstName, profile.lastName].filter(Boolean).join(' ') : '') || user?.name || ''
        if (key === 'preferredName') return contact?.preferredName || profile?.preferredName || ''
        if (key === 'displayName') return contact?.preferredName || (contact?.name ? contact.name.trim().split(' ')[0] : '') || user?.displayName || user?.name?.trim().split(' ')[0] || user?.username || ''
        if (key === 'position') return contact?.position || profile?.position || user?.title || ''
        if (key === 'email') return contact?.email || ''
        if (key === 'phone') return contact?.phone || ''
        if (key === 'department') return contact?.department || profile?.department || ''
        if (key === 'supervisor') return contact?.supervisor || profile?.supervisor || ''
        if (key === 'employeeId') return user?.employeeId || ''
        if (key === 'username') return user?.username || ''
        return ''
      }

      if (!cancelled) {
        setFormAnswers(prev => {
          let changed = false
          const next = { ...prev }
          const idsAdded: string[] = []
          for (const f of userFields) {
            const current = prev[f.id]
            if (current === undefined || current === null || String(current).trim() === '') {
              const val = getVal(f.config?.prefillData || 'name')
              if (val) {
                next[f.id] = val
                idsAdded.push(f.id)
                changed = true
              }
            }
          }
          if (idsAdded.length) setPrefilledFieldIds(p => new Set([...p, ...idsAdded]))
          if (!changed) return prev
          propagateDuplicateAnswers(next, idsAdded[0])
          return next
        })
      }
    }
    run()
    return () => { cancelled = true }
  }, [currentForm, currentStep, user?.employeeId, user?.name, user?.displayName, user?.username, user?.title])

  const weatherCacheRef = React.useRef<{ key?: string; data?: any }>({})
  // Debounce and request tracking for weather/geocode to reduce API calls
  const weatherDebounceTimerRef = React.useRef<number | null>(null)
  const weatherInFlightRef = React.useRef<string | null>(null) // Track in-flight location query
  const WEATHER_DEBOUNCE_MS = 500 // Wait 500ms after typing stops
  const WEATHER_MIN_CHARS = 3 // Don't geocode until at least 3 characters
  
  const geocodeLocationToLatLon = async (text: string): Promise<{ lat: number; lon: number } | null> => {
    const trimmed = String(text || '').trim()
    // Don't geocode until we have enough characters for a meaningful search
    if (!trimmed || trimmed.length < WEATHER_MIN_CHARS) return null
    const baseUrl = API_BASE_URL
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    const capabilityRequest = {
      app: 'forms',
      capability: 'maps.geocodeAddress',
      input: { q: text },
    }
    const res = await apiFetch(`${baseUrl}/capabilities/execute`, {
      method: 'POST',
      headers,
      body: JSON.stringify(capabilityRequest),
    })
    if (!res.ok) return null
    const payload = await res.json()
    const first = payload?.data?.results?.[0]
    if (!first) return null
    return { lat: parseFloat(first.lat), lon: parseFloat(first.lon) }
  }
  const fetchWeather = async (lat: number, lon: number) => {
    const baseUrl = API_BASE_URL
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    const capabilityRequest = {
      app: 'forms',
      capability: 'weather.currentByCoords',
      input: { lat, lon },
    }
    const res = await apiFetch(`${baseUrl}/capabilities/execute`, {
      method: 'POST',
      headers,
      body: JSON.stringify(capabilityRequest),
    })
    if (!res.ok) throw new Error('weather failed')
    const payload = await res.json()
    return payload?.data
  }
  const evaluateWeatherToggle = (cfg: any, weather: any) => {
    const main = String(weather?.data?.weather?.[0]?.main || weather?.data?.weather?.[0]?.description || '').toLowerCase()
    const temp = Number(weather?.data?.main?.temp)
    const wind = Number(weather?.data?.wind?.speed)
    const precip = Number(weather?.data?.rain?.['1h'] || weather?.data?.snow?.['1h'] || 0)
    const conds: Record<string, boolean> = {
      condRain: main.includes('rain'),
      condSnow: main.includes('snow'),
      condThunder: main.includes('thunder'),
      condFog: main.includes('fog'),
      condIce: main.includes('ice'),
      condExtremeHeat: temp >= 95
    }
    const condKeys = ['condRain','condSnow','condThunder','condFog','condIce','condExtremeHeat']
    const anyCondChecked = condKeys.some(k => cfg?.[k] && conds[k])
    const thresholdsOk = (
      (cfg?.heatThreshold ? temp >= Number(cfg.heatThreshold) : true) &&
      (cfg?.coldThreshold ? temp <= Number(cfg.coldThreshold) : true) &&
      (cfg?.windThreshold ? wind >= Number(cfg.windThreshold) : true) &&
      (cfg?.precipThreshold ? precip >= Number(cfg.precipThreshold) : true)
    )
    return anyCondChecked || thresholdsOk
  }
  const maybeApplyWeatherTools = async (changedFieldId: string, allAnswers?: { [key: string]: any }) => {
    if (!currentForm) return
    const answers = allAnswers || formAnswers
    // Build map of fieldId -> tool config
    const fields: Array<any> = []
    const walk = (q: any) => {
      if (!q) return
      if (q.type === 'group') {
        for (const qq of q.questions || []) walk(qq)
      } else {
        fields.push(q)
      }
    }
    for (const step of (currentForm as any).steps || []) {
      for (const q of step.questions || []) walk(q)
    }
    // Find location source fields referenced by any weather tool
    const weatherFields = fields.filter(f => f.tool?.type === 'weather')
    if (!weatherFields.length) return
    // Determine which location field changed, or if changed field itself has weather
    const affected = weatherFields.filter(f => f.tool?.config?.locationField === changedFieldId || f.id === changedFieldId)
    if (!affected.length) {
      // If a location field changed that is referenced, apply to all weather fields referencing it
      const referencedBy = weatherFields.filter(f => f.tool?.config?.locationField === changedFieldId)
      if (!referencedBy.length) return
    }
    // For each distinct location field, geocode and fetch weather once, then apply to all fields referencing it
    const byLocation: Record<string, any[]> = {}
    for (const wf of weatherFields) {
      const loc = wf.tool?.config?.locationField || ''
      if (!loc) continue
      byLocation[loc] = byLocation[loc] || []
      byLocation[loc].push(wf)
    }
    for (const locFieldId of Object.keys(byLocation)) {
      const text = answers[locFieldId]
      if (!text) continue
      const cacheKey = `${locFieldId}:${text}`
      let data = undefined
      if (weatherCacheRef.current.key === cacheKey) {
        data = weatherCacheRef.current.data
      } else {
        const geo = await geocodeLocationToLatLon(String(text))
        if (!geo) continue
        data = await fetchWeather(geo.lat, geo.lon)
        weatherCacheRef.current = { key: cacheKey, data }
      }
      const idsToAdd: string[] = []
      for (const wf of byLocation[locFieldId]) {
        if (wf.tool?.config?.behavior === 'setText') {
          const summary = [] as string[]
          if (wf.tool?.config?.includeTemp) summary.push(`Temp ${Math.round(Number(data?.data?.main?.temp || 0))}°F`)
          if (wf.tool?.config?.includeConditions) summary.push(String(data?.data?.weather?.[0]?.main || ''))
          if (wf.tool?.config?.includeWind) summary.push(`Wind ${Math.round(Number(data?.data?.wind?.speed || 0))} mph`)
          if (wf.tool?.config?.includePrecip) summary.push(`Precip ${Number(data?.data?.rain?.['1h'] || 0)}mm`)
          // Avoid infinite loop: only update if changed
          if (answers[wf.id] !== summary.join(' · ')) {
            handleAnswerChange(wf.id, summary.join(' · '))
            idsToAdd.push(wf.id)
          }
        } else if (wf.tool?.config?.behavior === 'toggleCheckbox') {
          const shouldCheck = evaluateWeatherToggle(wf.tool?.config, data)
          const val = !!shouldCheck
          const newVal = wf.type === 'checkbox' ? val : (val ? 'Yes' : 'No')
          if (answers[wf.id] !== newVal) {
            handleAnswerChange(wf.id, newVal)
            idsToAdd.push(wf.id)
          }
        }
      }
      if (idsToAdd.length) setPrefilledFieldIds(p => new Set([...p, ...idsToAdd]))
    }
  }

  // Debounced version of maybeApplyWeatherTools to reduce API calls
  const debouncedApplyWeatherTools = (changedFieldId: string, allAnswers: { [key: string]: any }) => {
    // Clear any pending debounce timer
    if (weatherDebounceTimerRef.current !== null) {
      window.clearTimeout(weatherDebounceTimerRef.current)
    }
    
    // Build a key from relevant location fields to detect if this is a duplicate request
    const fields: Array<any> = []
    if (currentForm) {
      const walk = (q: any) => {
        if (!q) return
        if (q.type === 'group') {
          for (const qq of q.questions || []) walk(qq)
        } else fields.push(q)
      }
      for (const step of (currentForm as any).steps || []) {
        for (const q of step.questions || []) walk(q)
      }
    }
    const weatherFields = fields.filter(f => f.tool?.type === 'weather')
    const locationFieldIds = [...new Set(weatherFields.map(f => f.tool?.config?.locationField).filter(Boolean))]
    const locationKey = locationFieldIds.map(id => `${id}:${allAnswers[id] || ''}`).join('|')
    
    // Set new debounce timer
    weatherDebounceTimerRef.current = window.setTimeout(async () => {
      // Check if this request is still relevant (not superseded by a newer one)
      if (weatherInFlightRef.current === locationKey) {
        return // Already fetching this exact location, skip
      }
      
      // Check minimum character requirement for any location field
      const hasValidLocation = locationFieldIds.some(id => {
        const val = String(allAnswers[id] || '').trim()
        return val.length >= WEATHER_MIN_CHARS
      })
      
      if (!hasValidLocation) {
        return // No location field has enough characters yet
      }
      
      weatherInFlightRef.current = locationKey
      try {
        await maybeApplyWeatherTools(changedFieldId, allAnswers)
      } catch (err) {
        console.warn('Weather tools error:', err)
      } finally {
        // Clear in-flight tracker if it's still this request
        if (weatherInFlightRef.current === locationKey) {
          weatherInFlightRef.current = null
        }
      }
    }, WEATHER_DEBOUNCE_MS)
  }

  // JobTread runtime helpers
  type JTItem = { id?: string; name?: string; location?: { address?: string } } | any
  const [jtSuggest, setJtSuggest] = useState<Record<string, JTItem[]>>({})
  const jtJobByNameRef = React.useRef<Map<string, any>>(new Map())
  const [jtLoading, setJtLoading] = useState<Record<string, boolean>>({})
  const jtSelectedItemByFieldIdRef = React.useRef<Map<string, any>>(new Map())
  const getAllFormFieldsFlat = (): Array<any> => {
    const fields: Array<any> = []
    if (!currentForm) return fields
    for (const step of (currentForm as any).steps || []) {
      for (const q of step.questions || []) {
        const walk = (qq: any) => {
          if (!qq) return
          if (qq.type === 'group') {
            for (const inner of qq.questions || []) walk(inner)
          } else fields.push(qq)
        }
        walk(q)
      }
    }
    return fields
  }
  const computeJobtreadPrefillUpdates = (sourceFieldId: string, job: any, prev: { [key: string]: any }) => {
    const updates: Record<string, any> = {}
    if (!currentForm || !job) return updates
    const fields = getAllFormFieldsFlat()
    const dependents = fields.filter(f =>
      f.tool?.type === 'jobtread' &&
      f.tool?.config?.prefill &&
      f.tool?.config?.prefillSource === sourceFieldId &&
      isFieldPrefillable(f, activeInstance?.currentStage ?? null)
    )
    for (const dep of dependents) {
      const what = String(dep.tool?.config?.prefillData || '').toLowerCase()
      let val = ''
      if (what === 'name') val = job?.name || ''
      else if (what === 'firstword') {
        // Extract text before the first space from the job name
        const name = job?.name || ''
        const spaceIdx = name.indexOf(' ')
        val = spaceIdx > 0 ? name.substring(0, spaceIdx) : name
      }
      else if (what === 'location' || what === 'address' || what === 'location.address') val = job?.location?.address || job?.address || ''
      else if (what === 'number') val = job?.number || ''
      else if (what === 'id') val = job?.id || ''
      else if (what === 'status') val = job?.status || ''
      if ((val || val === '') && prev[dep.id] !== val) updates[dep.id] = val
    }
    return updates
  }

  const applyJobtreadPrefill = (sourceFieldId: string, job: any) => {
    if (!job) return
    setFormAnswers(prev => {
      const updates = computeJobtreadPrefillUpdates(sourceFieldId, job, prev)
      if (!Object.keys(updates).length) return prev
      const ids = Object.keys(updates)
      if (ids.length) setPrefilledFieldIds(p => new Set([...p, ...ids]))
      const next = { ...prev, ...updates }
      propagateDuplicateAnswers(next, ids[0])
      return next
    })
  }

  // Nearby Places state and helpers
  const [nearbyLoading, setNearbyLoading] = useState<Record<string, boolean>>({})
  const [fillAllNearbyLoading, setFillAllNearbyLoading] = useState(false)
  const nearbyPlacesCacheRef = React.useRef<Map<string, any>>(new Map())

  const fetchNearbyPlace = async (address: string, placeType: string): Promise<{ place?: any; error?: string }> => {
    const cacheKey = `${address}|${placeType}`
    const cached = nearbyPlacesCacheRef.current.get(cacheKey)
    if (cached) return { place: cached }

    const baseUrl = API_BASE_URL
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    
    try {
      const capabilityRequest = {
        app: 'forms',
        capability: 'maps.nearby',
        input: { address, type: placeType },
      }
      const res = await apiFetch(`${baseUrl}/capabilities/execute`, { method: 'POST', headers, body: JSON.stringify(capabilityRequest) })
      const payload = await res.json()
      if (!res.ok || !payload?.success) {
        return { error: payload?.message || 'Failed to search nearby places' }
      }
      const data = payload?.data
      if (data?.found && data?.place) {
        nearbyPlacesCacheRef.current.set(cacheKey, data.place)
        return { place: data.place }
      }
      if (!data?.found) {
        return { error: data?.message || 'No nearby places found' }
      }
      return { error: 'Unknown error' }
    } catch (e: any) {
      return { error: e.message || 'Network error' }
    }
  }

  const computeNearbyPlacesPrefillUpdates = (place: any, deps: Array<any>, prev: { [key: string]: any }) => {
    const updates: Record<string, any> = {}
    if (!place) return updates
    for (const dep of deps) {
      const what = String(dep.tool?.config?.nearbyPrefillData || '').toLowerCase()
      let val = ''
      if (what === 'name') val = place.name || ''
      else if (what === 'address') val = place.address || ''
      else if (what === 'phone') val = place.phone || ''
      else if (what === 'distance') val = place.distanceText || ''
      else if (what === 'rating') val = place.rating ? String(place.rating) : ''
      else if (what === 'website') val = place.website || ''
      else if (what === 'hours') {
        // Get today's hours
        const today = new Date().getDay()
        const hoursArr = place.hours || []
        val = hoursArr[today === 0 ? 6 : today - 1] || ''
      }
      if (hasAnswerValue(val) && prev[dep.id] !== val) updates[dep.id] = val
    }
    return updates
  }

  const applyNearbyPlacesPrefill = async (sourceFieldId: string) => {
    const sourceAddress = formAnswers[sourceFieldId]
    if (!sourceAddress) return

    // Find all fields that depend on this source for nearby places
    const fields = getAllFormFieldsFlat()
    const dependents = fields.filter(f => 
      f.tool?.type === 'maps' && 
      f.tool?.config?.nearbyPlaces && 
      f.tool?.config?.nearbySourceField === sourceFieldId
    )
    if (!dependents.length) return

    // Group by place type to minimize API calls
    const placeTypeGroups = new Map<string, typeof dependents>()
    for (const dep of dependents) {
      const type = dep.tool?.config?.nearbyPlaceType || ''
      if (!type) continue
      if (!placeTypeGroups.has(type)) placeTypeGroups.set(type, [])
      placeTypeGroups.get(type)!.push(dep)
    }

    // Set loading for all fields
    const loadingIds = dependents.map(d => d.id)
    setNearbyLoading(prev => {
      const next = { ...prev }
      for (const id of loadingIds) next[id] = true
      return next
    })

    try {
      // Fetch each place type and update fields
      for (const [placeType, _deps] of placeTypeGroups) {
        const result = await fetchNearbyPlace(String(sourceAddress), placeType)
        if (result.place) {
          setFormAnswers(prev => {
            const updates = computeNearbyPlacesPrefillUpdates(result.place, _deps, prev)
            if (!Object.keys(updates).length) return prev
            const ids = Object.keys(updates)
            if (ids.length) setPrefilledFieldIds(p => new Set([...p, ...ids]))
            const next = { ...prev, ...updates }
            propagateDuplicateAnswers(next, ids[0])
            return next
          })
        }
      }
    } finally {
      setNearbyLoading(prev => {
        const next = { ...prev }
        for (const id of loadingIds) next[id] = false
        return next
      })
    }
  }

  // Get current step's nearbyPlaces fields for "Fill all" button
  const getCurrentStepNearbyMeta = (): { count: number; sourceFields: string[] } => {
    if (!currentForm || currentStep >= currentForm.steps.length) return { count: 0, sourceFields: [] }
    const step = currentForm.steps[currentStep]
    const flat: any[] = []
    for (const q of step.questions || []) {
      if ((q as any).type === 'group') {
        for (const gq of (q as any).questions || []) {
          if (gq.tool?.type === 'maps' && gq.tool?.config?.nearbyPlaces && gq.tool?.config?.nearbySourceField) {
            flat.push(gq)
          }
        }
      } else if ((q as any).tool?.type === 'maps' && (q as any).tool?.config?.nearbyPlaces && (q as any).tool?.config?.nearbySourceField) {
        flat.push(q)
      }
    }
    const sourceFields = [...new Set(flat.map(f => f.tool.config.nearbySourceField).filter(Boolean))]
    return { count: flat.length, sourceFields }
  }

  const handleFillAllNearbyLocations = async () => {
    const { sourceFields } = getCurrentStepNearbyMeta()
    const sourcesWithValue = sourceFields.filter(id => {
      const v = formAnswers[id]
      return v && String(v).trim().length > 0
    })
    if (!sourcesWithValue.length) {
      const firstSource = sourceFields[0]
      const label = currentForm && getAllFormFieldsFlat().find(f => f.id === firstSource)?.label || firstSource
      alert(`Please fill in "${label}" first`)
      return
    }
    setFillAllNearbyLoading(true)
    try {
      for (const sourceId of sourcesWithValue) {
        await applyNearbyPlacesPrefill(sourceId)
      }
    } finally {
      setFillAllNearbyLoading(false)
    }
  }

  // Handle "Find Nearest" button click for a specific field
  const handleFindNearest = async (fieldId: string) => {
    const fields = getAllFormFieldsFlat()
    const field = fields.find(f => f.id === fieldId)
    if (!field?.tool?.config?.nearbySourceField) return

    const sourceAddress = formAnswers[field.tool.config.nearbySourceField]
    if (!sourceAddress) {
      alert('Please fill in the address field first')
      return
    }

    const placeType = field.tool?.config?.nearbyPlaceType
    if (!placeType) return

    setNearbyLoading(prev => ({ ...prev, [fieldId]: true }))
    try {
      const result = await fetchNearbyPlace(String(sourceAddress), placeType)
      if (result.error) {
        alert(result.error)
        return
      }
      if (result.place) {
        const place = result.place
        const what = String(field.tool?.config?.nearbyPrefillData || '').toLowerCase()
        let val = ''
        if (what === 'name') val = place.name || ''
        else if (what === 'address') val = place.address || ''
        else if (what === 'phone') val = place.phone || ''
        else if (what === 'distance') val = place.distanceText || ''
        else if (what === 'rating') val = place.rating ? String(place.rating) : ''
        else if (what === 'website') val = place.website || ''
        else if (what === 'hours') {
          const today = new Date().getDay()
          const hoursArr = place.hours || []
          val = hoursArr[today === 0 ? 6 : today - 1] || ''
        }
        if (val) handleAnswerChange(fieldId, val)
        else alert('No data available for this place')
      }
    } finally {
      setNearbyLoading(prev => ({ ...prev, [fieldId]: false }))
    }
  }
  const JT_CACHE_TTL_MS = 10 * 60 * 1000
  const jtSuggestCacheRef = React.useRef<Map<string, { results: JTItem[]; ts: number }>>(new Map())
  const jtFieldTimersRef = React.useRef<Map<string, number>>(new Map())
  const fetchJobtreadSuggestions = async (q: string): Promise<JTItem[]> => {
    const query = String(q || '').trim()
    if (query.length < 2) return []
    const now = Date.now()
    const cached = jtSuggestCacheRef.current.get(query)
    if (cached && (now - cached.ts) < JT_CACHE_TTL_MS) {
      // Warm name cache
      for (const j of cached.results) { if (j?.name) jtJobByNameRef.current.set(j.name, j) }
      return cached.results
    }
    const baseUrl = API_BASE_URL
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    try {
      const capabilityRequest = {
        app: 'forms',
        capability: 'jobtread.searchJobs',
        input: { q: query },
      }
      const res = await apiFetch(`${baseUrl}/capabilities/execute`, { method: 'POST', headers, body: JSON.stringify(capabilityRequest) })
      if (!res.ok) return []
      const payload = await res.json()
      const arr: JTItem[] = (payload?.data?.results || []).slice(0, 5)
      jtSuggestCacheRef.current.set(query, { results: arr, ts: now })
      for (const j of arr) { if (j?.name) jtJobByNameRef.current.set(j.name, j) }
      return arr
    } catch {
      return []
    }
  }

  const getResolvedJobForUpload = (): { jobId: string | null; jobNumber: string | null; job?: { name?: string; number?: string } } => {
    const jobFieldId = (currentForm as any)?.upload?.jobFieldId ||
      getAllFormFieldsFlat().find(f => f.tool?.type === 'jobtread' && f.tool?.config?.autocomplete)?.id
    const selected = jobFieldId ? jtSelectedItemByFieldIdRef.current.get(jobFieldId) : null
    if (selected?.id) return { jobId: selected.id, jobNumber: selected.number ?? null, job: selected }
    const typed = jobFieldId ? formAnswers[jobFieldId] : null
    return { jobId: null, jobNumber: typed ? String(typed).trim() : null }
  }

  const sanitizeFilenamePart = (s: string) => String(s ?? '').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '') || ''

  const getCompletionFileName = (jobOverride?: { id: string; number?: string; name?: string } | null): string => {
    const parts: string[] = []
    const formName = (currentForm as any)?.formName || 'form'
    const uploadCfg = (currentForm as any)?.upload
    const fileNameParts = uploadCfg?.fileNameParts as Array<{ type: string; fieldId?: string }> | undefined

    if (fileNameParts && Array.isArray(fileNameParts) && fileNameParts.length > 0) {
      const { job } = getResolvedJobForUpload()
      const jobInfo = jobOverride ?? (job ? { number: job.number, name: job.name } : null)

      for (const p of fileNameParts) {
        let val = ''
        if (p.type === 'formName') val = formName
        else if (p.type === 'user') val = user?.displayName || user?.name || user?.username || ''
        else if (p.type === 'field' && p.fieldId) val = formAnswers[p.fieldId] != null ? String(formAnswers[p.fieldId]) : ''
        else if (p.type === 'jobNumber' && jobInfo) val = jobInfo.name || jobInfo.number || ''
        else if (p.type === 'date') {
          const now = new Date()
          val = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`
        }
        if (val) parts.push(sanitizeFilenamePart(val))
      }
    } else {
      // Legacy: JobTread job + user + formName + date
      const jobtreadEnabled = !!uploadCfg?.jobtread
      const { job } = getResolvedJobForUpload()
      const jobInfo = jobOverride ?? (job ? { number: job.number, name: job.name } : null)
      if (jobtreadEnabled && jobInfo) {
        const site = jobInfo.name || jobInfo.number || ''
        if (site) parts.push(sanitizeFilenamePart(String(site)))
      }
      const userPart = user?.displayName || user?.name || user?.username || 'user'
      if (userPart) parts.push(sanitizeFilenamePart(String(userPart)))
      parts.push(sanitizeFilenamePart(formName))
      const now = new Date()
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`
      parts.push(dateStr)
    }
    return parts.filter(Boolean).join('-') || `form-${Date.now()}`
  }

  const doSubmitUpload = async (base64: string, jobOverride?: { id: string; number?: string } | null) => {
    const apiBase = API_BASE_URL
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    const resolved = jobOverride ?? getResolvedJobForUpload()
    const jobId = (resolved && 'id' in resolved ? resolved.id : (resolved && 'jobId' in resolved ? resolved.jobId : null)) ?? null
    const jobNumber = (resolved && 'number' in resolved ? resolved.number : (resolved && 'jobNumber' in resolved ? resolved.jobNumber : null)) ?? null
    const folder = ((currentForm as any)?.upload?.jtFolder || '').trim().replace(/^\/+|\/+$/g, '') || null
    const jobtreadEnabled = !!(currentForm as any)?.upload?.jobtread
    const payload = {
      formName: (currentForm as any)?.formName,
      fileName: getCompletionFileName(jobOverride),
      pdfBase64: base64,
      completionId: completionId || undefined,
      destinations: {
        drive: { enabled: !!(currentForm as any)?.upload?.drive, folder: (currentForm as any)?.upload?.driveFolder },
        jobtread: {
          enabled: jobtreadEnabled,
          folder,
          jobId: jobId || null,
          jobNumber: jobtreadEnabled && jobId ? (jobNumber || null) : null,
        },
      },
    }
    const capabilityRequest = {
      app: 'forms',
      capability: 'forms.submitPdf',
      input: payload,
    }
    const r = await apiFetch(`${apiBase}/capabilities/execute`, { method: 'POST', headers, body: JSON.stringify(capabilityRequest) })
    const data = await r.json()
    if (r.ok && data?.success) {
      return { res: r, data: data.data }
    }
    return { res: r, data }
  }

  const handleUploadClick = async (jobOverride?: { id: string; number?: string } | null) => {
    if (!completionBlobUrl) return
    const jobtreadEnabled = !!(currentForm as any)?.upload?.jobtread
    if (jobOverride) jtFallbackSelectedRef.current = jobOverride
    const { jobId } = jobOverride ? { jobId: jobOverride.id } : getResolvedJobForUpload()
    if (jobtreadEnabled && !jobOverride && !jobId) {
      jtFallbackSelectedRef.current = null
      setJtFallbackSuggest([])
      setJtFallbackQuery('')
      setJobSelectModalOpen(true)
      return
    }
    setCompletionBusy(true)
    setCompletionPhase('uploading')
    setCompletionError(null)
    setUploadResult(null)
    try {
      const res = await fetch(completionBlobUrl)
      const blob = await res.blob()
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(new Error('Failed to read PDF'))
        reader.onload = () => resolve(String(reader.result))
        reader.readAsDataURL(blob)
      })
      const effectiveJob = jobOverride ?? jtFallbackSelectedRef.current
      let result = await doSubmitUpload(base64, effectiveJob)
      if (result.res.status === 401 && result.data?.needsGoogleAuth) {
        const clientId = (import.meta as any).env.VITE_GOOGLE_DRIVE_CLIENT_ID || ''
        const token = localStorage.getItem('lts_jwt_token')
        await new Promise<void>((resolve, reject) => {
          const client = (window as any).google?.accounts?.oauth2?.initCodeClient({
            client_id: clientId,
            scope: 'https://www.googleapis.com/auth/drive.file',
            ux_mode: 'popup',
            prompt: 'consent',
            callback: async (resp: any) => {
              try {
                if (!resp || !resp.code) return reject(new Error('No authorization code returned'))
                const apiBase = API_BASE_URL
                const ex = await apiFetch(`${apiBase}/auth/google/exchange`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                  body: JSON.stringify({ code: resp.code }),
                })
                const exd = await ex.json()
                if (!ex.ok || !exd?.success) return reject(new Error(exd?.message || 'Failed to link Google account'))
                resolve()
              } catch (e) {
                reject(e)
              }
            },
          })
          if (!client) return reject(new Error('Google Identity Services not loaded'))
          client.requestCode()
        })
        result = await doSubmitUpload(base64, effectiveJob)
      }
      if (!result.res.ok || !result.data?.success) throw new Error(result.data?.message || 'Upload failed')
      setUploadResult(result.data?.result || null)
    } catch (e: any) {
      setCompletionError(e.message || 'Upload failed')
    } finally {
      setCompletionBusy(false)
      setCompletionPhase(null)
    }
  }

  // Validate all steps (for submit) - includes required fields and unconfirmed prefills
  const validateAllSteps = (): {
    valid: boolean
    errors: Record<string, string>
    missingRequired: Array<{ id: string; label: string; stepIndex: number; stepName?: string }>
    unconfirmedPrefills: Array<{ id: string; label: string; stepIndex: number; stepName?: string }>
  } => {
    if (!currentForm) return { valid: true, errors: {}, missingRequired: [], unconfirmedPrefills: [] }
    const errors: Record<string, string> = {}
    const missingRequired: Array<{ id: string; label: string; stepIndex: number; stepName?: string }> = []
    const unconfirmedPrefills: Array<{ id: string; label: string; stepIndex: number; stepName?: string }> = []
    const steps = (currentForm as any).steps || []
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
      const step = steps[stepIndex]
      const stepName = step?.name || `Step ${stepIndex + 1}`
      for (const question of step?.questions || []) {
        if (question.type === 'group') {
          const g = question as any
          const subQuestions = g.questions || []
            if (g.mode === 'radio') {
            if (isRequiredForCurrentStage(g, activeInstance?.currentStage ?? null)) {
              const val = formAnswers[g.id]
              const isEmpty = val === undefined || val === null || val === ''
              if (isEmpty) {
                errors[g.id] = 'This field is required'
                missingRequired.push({ id: g.id, label: g.name || 'Selection', stepIndex, stepName })
              }
            }
          } else {
            for (const q of subQuestions) {
              if (isRequiredForCurrentStage(q, activeInstance?.currentStage ?? null)) {
                const val = formAnswers[q.id]
                let isEmpty = val === undefined || val === null || val === '' || val === false
                if (q.type === 'signature') isEmpty = !isCompleteSignatureValue(val)
                if (isEmpty) {
                  errors[q.id] = 'This field is required'
                  missingRequired.push({ id: q.id, label: q.label || q.id, stepIndex, stepName })
                }
              }
              if (q.tool?.config?.requirePrefillConfirmation && prefilledFieldIds.has(q.id) && !confirmedPrefillIds.has(q.id) && !isFieldInputLocked(q, activeInstance?.currentStage ?? null)) {
                unconfirmedPrefills.push({ id: q.id, label: q.label || q.id, stepIndex, stepName })
              }
            }
          }
        } else {
          const q = question as any
          if (isRequiredForCurrentStage(q, activeInstance?.currentStage ?? null)) {
            const val = formAnswers[q.id]
            let isEmpty = val === undefined || val === null || val === '' || val === false
            if (q.type === 'signature') isEmpty = !isCompleteSignatureValue(val)
            if (isEmpty) {
              errors[q.id] = 'This field is required'
              missingRequired.push({ id: q.id, label: q.label || q.id, stepIndex, stepName })
            }
          }
          if (q.tool?.config?.requirePrefillConfirmation && prefilledFieldIds.has(q.id) && !confirmedPrefillIds.has(q.id) && !isFieldInputLocked(q, activeInstance?.currentStage ?? null)) {
            unconfirmedPrefills.push({ id: q.id, label: q.label || q.id, stepIndex, stepName })
          }
        }
      }
    }
    setFormErrors(errors)
    const valid = Object.keys(errors).length === 0 && unconfirmedPrefills.length === 0
    return { valid, errors, missingRequired, unconfirmedPrefills }
  }

  const handleNextStep = () => {
    if (!currentForm) return
    flushFormCache()
    if (currentStep < currentForm.steps.length - 1) {
      setCurrentStep(currentStep + 1)
      try { window.scrollTo({ top: 0, behavior: 'smooth' }) } catch {}
      return
    }
    const result = validateAllSteps()
    if (!result.valid) {
      setValidationFailedModal({
        open: true,
        missingRequired: result.missingRequired,
        unconfirmedPrefills: result.unconfirmedPrefills,
      })
      return
    }
    setPrefillReviewConfirmed(false)
    setPrefillReviewModalOpen(true)
  }

  const handlePrefillReviewContinue = () => {
    if (!prefillReviewConfirmed) return
    setPrefillReviewModalOpen(false)
    setPrefillReviewConfirmed(false)
    const continueFn = () => {
      if (activeWorkflowName && currentForm) {
        const wfForms = workflowFormsToShow.length > 0 ? workflowFormsToShow : formService.getFormsInWorkflow(activeWorkflowName)
        const currentIndex = wfForms.findIndex(f => f.formName === currentForm!.formName)
        if (currentIndex > -1 && currentIndex < wfForms.length - 1) {
          const nextForm = wfForms[currentIndex + 1]
          void openFormForFill(nextForm)
          try { window.scrollTo({ top: 0, behavior: 'smooth' }) } catch {}
          return
        }
      }
      setView({ view: 'selection', tab: activeWorkflowName ? 'workflows' : 'forms' })
      setActiveWorkflowName(undefined)
      setWorkflowFormsToShow([])
      setCurrentForm(null)
      setCurrentStep(0)
      setFormAnswers({})
      setFormErrors({})
      setPrefilledFieldIds(new Set())
      setConfirmedPrefillIds(new Set())
      setActiveInstance(null)
      void refreshPending()
    }
    setCompletionContinue(() => continueFn)
    if (activeInstance) {
      void (async () => {
        try {
          const res = await formService.submitStage(activeInstance.id, answersForCurrentStage())
          if (currentForm) {
            clearCachedAnswers(currentForm.formName, user?._id, activeInstance.id)
          }
          if (!res?.completed) {
            setStageSubmitNotice('Submitted. The next person will see this form on their Pending tab.')
            setActiveInstance(null)
            setView({ view: 'selection', tab: 'pending' })
            setActiveWorkflowName(undefined)
            setWorkflowFormsToShow([])
            setCurrentForm(null)
            setCurrentStep(0)
            setFormAnswers({})
            setFormErrors({})
            await refreshPending()
            return
          }
          setActiveInstance(null)
          await refreshPending()
          await openCompletionFromResult(res)
          setCompletionContinue(() => continueFn)
        } catch (e: any) {
          setStageSubmitNotice(e?.message || 'Could not submit this stage')
        }
      })()
      return
    }
    openCompletionModal()
  }

  const openCompletionFromResult = async (res: { pdfBase64?: string; completionId?: string | null }) => {
    setCompletionOpen(true)
    setCompletionBusy(false)
    setCompletionPhase(null)
    setCompletionError(null)
    setUploadResult(null)
    const builtBase64 = res?.pdfBase64
    if (!builtBase64) {
      setCompletionError('Failed to build PDF')
      setCompletionBlobUrl(null)
      setCompletionId(null)
      return
    }
    setCompletionId(res?.completionId ? String(res.completionId) : null)
    const pdfData = atob(builtBase64)
    const pdfArray = new Uint8Array(pdfData.length)
    for (let i = 0; i < pdfData.length; i++) pdfArray[i] = pdfData.charCodeAt(i)
    const blob = new Blob([pdfArray], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    setCompletionBlobUrl(url)
  }

  const openCompletionModal = async () => {
    setCompletionOpen(true)
    setCompletionBusy(true)
    setCompletionPhase('building')
    setCompletionError(null)
    setCompletionBlobUrl(null)
    setCompletionId(null)
    setUploadResult(null)
    try {
      const apiBase = API_BASE_URL
      const token = localStorage.getItem('lts_jwt_token')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      }

      const form = currentForm as any
      if (!form?.formName) throw new Error('No formName available for this form')

      const capabilityRequest = {
        app: 'forms',
        capability: 'forms.buildPdfBase64',
        input: {
          formName: form.formName,
          answers: formAnswers,
        },
      }

      const r = await apiFetch(`${apiBase}/capabilities/execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify(capabilityRequest),
      })
      const payload = await r.json()
      if (!r.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to build PDF')
      }

      const builtBase64 = payload?.data?.pdfBase64
      if (!builtBase64) throw new Error('Failed to build PDF')
      setCompletionId(payload?.data?.completionId ? String(payload.data.completionId) : null)

      const pdfData = atob(builtBase64)
      const pdfArray = new Uint8Array(pdfData.length)
      for (let i = 0; i < pdfData.length; i++) pdfArray[i] = pdfData.charCodeAt(i)
      const blob = new Blob([pdfArray], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      setCompletionBlobUrl(url)
    } catch (e: any) {
      setCompletionError(e.message || 'Failed to generate PDF')
    } finally {
      setCompletionBusy(false)
      setCompletionPhase(null)
    }
  }

  const generateFilledPdfBytes = async (
    pdfBase64: string,
    form: any,
    answers: Record<string, any>,
    hospitalAddresses?: { siteAddress: string; hospitalAddress: string }
  ): Promise<Uint8Array> => {
    const {
      PDFDocument,
      StandardFonts,
      PDFName,
      PDFTextField,
      PDFCheckBox,
      PDFRadioGroup,
      PDFDropdown,
      PDFOptionList,
    } = await import('pdf-lib')
    const binary = atob(pdfBase64)
    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
    const pdfFields = normalizeParsedPdfFields(form?.pdf?.fields || [])

    const appendHospitalRoutePages = async (pdfDoc: any) => {
      if (!(form?.hospitalMaps?.enabled && hospitalAddresses?.siteAddress && hospitalAddresses?.hospitalAddress)) return

      const baseUrl = API_BASE_URL
      const token = localStorage.getItem('lts_jwt_token')
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
      const size = '612x792'
      const PAGE_WIDTH = 612
      const PAGE_HEIGHT = 792
      const MARGIN = 36
      const LINE_HEIGHT = 12
      const FONT_SIZE = 9
      const TITLE_SIZE = 12
      const MAX_TEXT_WIDTH = PAGE_WIDTH - 2 * MARGIN
      const MAP_GAP = 16

      const wrapText = (font: any, text: string, fontSize: number, maxWidth: number): string[] => {
        const lines: string[] = []
        const words = text.replace(/\s+/g, ' ').trim().split(' ')
        let line = ''
        for (const w of words) {
          const candidate = line ? `${line} ${w}` : w
          const wd = font.widthOfTextAtSize(candidate, fontSize)
          if (wd <= maxWidth) line = candidate
          else {
            if (line) lines.push(line)
            line = w
          }
        }
        if (line) lines.push(line)
        return lines
      }

      const pairs: { origin: string; destination: string; label: string }[] = [
        { origin: hospitalAddresses.siteAddress, destination: hospitalAddresses.hospitalAddress, label: 'Site to Hospital' },
        { origin: hospitalAddresses.hospitalAddress, destination: hospitalAddresses.siteAddress, label: 'Hospital to Site' },
      ]

      try {
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
        for (const { origin, destination, label } of pairs) {
          const dataUrl = `${baseUrl}/integrations/maps/route-page-data?origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&size=${encodeURIComponent(size)}`
          const dataRes = await apiFetch(dataUrl, { headers })
          if (!dataRes.ok) continue
          const data = await dataRes.json()
          if (!data.success || !data.imageBase64) continue

          const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT])
          let y = PAGE_HEIGHT - MARGIN
          page.drawText(label, { x: MARGIN, y, size: TITLE_SIZE, font })
          y -= 20
          page.drawText('From:', { x: MARGIN, y, size: FONT_SIZE, font })
          y -= LINE_HEIGHT
          for (const ln of wrapText(font, data.startAddress || origin, FONT_SIZE, MAX_TEXT_WIDTH)) {
            page.drawText(ln, { x: MARGIN, y, size: FONT_SIZE, font }); y -= LINE_HEIGHT
          }
          y -= 4
          page.drawText('To:', { x: MARGIN, y, size: FONT_SIZE, font })
          y -= LINE_HEIGHT
          for (const ln of wrapText(font, data.endAddress || destination, FONT_SIZE, MAX_TEXT_WIDTH)) {
            page.drawText(ln, { x: MARGIN, y, size: FONT_SIZE, font }); y -= LINE_HEIGHT
          }
          y -= 8
          page.drawText('Directions:', { x: MARGIN, y, size: FONT_SIZE, font })
          y -= LINE_HEIGHT
          const steps = (data.steps || []) as { instruction?: string; distance?: string }[]
          for (let i = 0; i < steps.length; i++) {
            const s = steps[i]
            const stepText = `${i + 1}. ${(s.instruction || '').trim()}${s.distance ? ` (${s.distance})` : ''}`
            for (const ln of wrapText(font, stepText, FONT_SIZE, MAX_TEXT_WIDTH - 20)) {
              page.drawText(ln, { x: MARGIN + 12, y, size: FONT_SIZE, font }); y -= LINE_HEIGHT
            }
          }
          const mapTop = y - MAP_GAP
          const mapHeight = Math.max(mapTop - MARGIN, 0)
          const mapWidth = PAGE_WIDTH - 2 * MARGIN
          if (mapHeight > 20) {
            const pngBytes = Uint8Array.from(atob(data.imageBase64), c => c.charCodeAt(0))
            const png = await pdfDoc.embedPng(pngBytes)
            const scale = Math.min(mapWidth / png.width, mapHeight / png.height)
            page.drawImage(png, { x: MARGIN, y: mapTop - png.height * scale, width: png.width * scale, height: png.height * scale })
          }
        }
      } catch (e) {
        console.warn('Hospital maps append failed:', e)
      }
    }

    const countAnsweredLeafQuestions = (): number => {
      let count = 0
      const walk = (node: any) => {
        if (!node) return
        if (node.type === 'group') {
          for (const child of node.questions || []) walk(child)
          return
        }
        if (hasAnswerValue(answers[node.id])) count += 1
      }
      for (const step of form.steps || []) {
        for (const q of step.questions || []) walk(q)
      }
      return count
    }

    const throwIfUnresolved = (unresolvedIds: Set<string>) => {
      if (unresolvedIds.size <= 0) return
      const unresolved = Array.from(unresolvedIds)
      const unresolvedNote = ` Unresolved PDF fields: ${unresolved.slice(0, 10).join(', ')}${unresolved.length > 10 ? ' …' : ''}.`
      throw new Error(`Failed to finalize completed PDF.${unresolvedNote} This usually means the template field names do not match this form JSON.`)
    }

      const applyToDoc = async (pdfDoc: any, opts: { flatten: boolean }) => {
      const fillAnswers = { ...answers }
      applySignatureDateAnswers(fillAnswers, form, { overwrite: false })
      const pdfForm = pdfDoc.getForm()
      const acroFields = pdfForm.getFields()
      const acroNames = acroFields.map((f: any) => String(f?.getName?.() || '').trim()).filter(Boolean)
      const acroNamesByNormalized = new Map<string, string>()
      const acroFieldsByNormalized = new Map<string, any[]>()
      for (const nm of acroNames) {
        const key = normalizePdfLookupKey(nm)
        if (key && !acroNamesByNormalized.has(key)) acroNamesByNormalized.set(key, nm)
      }
      for (const f of acroFields as any[]) {
        const nm = String(f?.getName?.() || '').trim()
        const key = normalizePdfLookupKey(nm)
        if (!key) continue
        const list = acroFieldsByNormalized.get(key)
        if (list) list.push(f)
        else acroFieldsByNormalized.set(key, [f])
      }
      const unresolvedIds = new Set<string>()
      const pendingWrites = new Map<string, { pdfType: string; value: any }>()
      const resolvePdfFieldKey = (requested: string): string | null => {
        const raw = String(requested || '').trim()
        if (!raw) return null
        const normalized = normalizePdfLookupKey(raw)
        if (!normalized) return null
        if (acroFieldsByNormalized.has(normalized)) return normalized
        return null
      }

      const queueWrite = (resolvedKey: string, pdfType: string, value: any) => {
        const existing = pendingWrites.get(resolvedKey)
        if (!existing) {
          pendingWrites.set(resolvedKey, { pdfType, value })
          return
        }
        if (pdfType === 'checkbox' && typeof existing.value === 'boolean' && typeof value === 'boolean') {
          pendingWrites.set(resolvedKey, { pdfType, value: existing.value || value })
          return
        }
        if (!hasAnswerValue(existing.value) && hasAnswerValue(value)) {
          pendingWrites.set(resolvedKey, { pdfType, value })
        }
      }

      const applyField = (q: any, valueOverride?: any) => {
        const requestedId = q?.pdfField?.id || q?.pdfField?.acroName || q?.id
        const pdfType = (q?.pdfField?.type || q?.type || '').toLowerCase()
        if (!requestedId) return
        const value = valueOverride !== undefined ? valueOverride : fillAnswers[q.id]
        if (pdfType === 'signature' || String(q?.type || '').toLowerCase() === 'signature') return
        if (pdfType === 'radio' || q?.pdfField?.option) {
          const groupName = radioGroupNameFromStoredId(String(requestedId))
          const optionValue = valueOverride === true
            ? radioOptionFromQuestion(q)
            : valueOverride === false
              ? ''
              : String(value ?? radioOptionFromQuestion(q) ?? '')
          const result = applyRadioGroupValue(acroFields, {
            groupName,
            optionValue,
            PDFRadioGroup,
            PDFName,
          })
          if (!result.applied) unresolvedIds.add(String(requestedId))
          return
        }
        const resolvedKey = resolvePdfFieldKey(requestedId)
        if (!resolvedKey) {
          unresolvedIds.add(String(requestedId))
          console.warn(`PDF fill skipped unresolved field: ${requestedId}`)
          return
        }
        const writeValue = String(q?.type || '') === SIGNATURE_DATE_TYPE
          ? pdfTextForSignatureDate(q, value)
          : value
        queueWrite(resolvedKey, pdfType, writeValue)
      }

      const applyRadioModeGroup = (qq: any, selectedLabel: any) => {
        const leaves: any[] = []
        const walk = (node: any) => {
          if (!node) return
          if (node.type === 'group') {
            for (const inner of node.questions || []) walk(inner)
            return
          }
          leaves.push(node)
        }
        for (const child of qq.questions || []) walk(child)
        const selectedChild =
          leaves.find((c) => c?.label === selectedLabel) ||
          leaves.find((c) => radioOptionFromQuestion(c) === selectedLabel)
        const groupName = radioGroupNameFromStoredId(
          selectedChild?.pdfField?.id || leaves[0]?.pdfField?.id || qq.name || '',
        )
        const optionValue = selectedChild ? radioOptionFromQuestion(selectedChild) : ''
        if (!groupName) return
        const result = applyRadioGroupValue(acroFields, {
          groupName,
          optionValue,
          PDFRadioGroup,
          PDFName,
        })
        if (!result.applied) unresolvedIds.add(groupName)
      }

      const applyQuestion = (qq: any) => {
        if (!qq) return
        if (qq.type === 'group') {
          if (qq.mode === 'radio') {
            applyRadioModeGroup(qq, fillAnswers[qq.id])
          } else {
            for (const child of qq.questions || []) applyQuestion(child)
          }
        } else {
          applyField(qq)
        }
      }
      for (const step of form.steps || []) for (const q of step.questions || []) applyQuestion(q)

      // Apply one write per resolved PDF key to avoid duplicate rows clearing each other.
      for (const [resolvedKey, payload] of pendingWrites) {
        const pdfType = payload.pdfType
        const value = payload.value
        const matchedFields = acroFieldsByNormalized.get(resolvedKey) || []
        if (!matchedFields.length) {
          console.warn(`PDF fill skipped unresolved normalized key: ${resolvedKey}`)
          continue
        }
        for (const field of matchedFields) {
          try {
          if (field instanceof PDFCheckBox) {
            if (value) field.check(); else field.uncheck()
          } else if (field instanceof PDFRadioGroup) {
            if (value !== undefined && value !== null && value !== '') field.select(String(value))
          } else if (field instanceof PDFDropdown || field instanceof PDFOptionList) {
            if (value !== undefined && value !== null && value !== '') (field as any).select(String(value))
          } else if (field instanceof PDFTextField) {
            field.setText(value == null ? '' : String(value))
          } else {
            const anyField = field as any
            if (typeof anyField.setText === 'function') anyField.setText(value == null ? '' : String(value))
          }
          } catch (e) {
            const fieldName = String((field as any)?.getName?.() || resolvedKey)
            console.warn(`PDF fill failed for field ${fieldName} (type=${pdfType}):`, e)
          }
        }
      }

      const stampSignatures = async () => {
        const signatureNodes: any[] = []
        const walkSignatures = (qq: any) => {
          if (!qq) return
          if (qq.type === 'group') {
            for (const child of qq.questions || []) walkSignatures(child)
            return
          }
          if (qq?.type === 'signature' && qq?.pdfField?.id) signatureNodes.push(qq)
        }
        for (const step of form.steps || []) for (const q of step.questions || []) walkSignatures(q)

        for (const qq of signatureNodes) {
          const sigValue = fillAnswers[qq.id]
          if (!sigValue?.dataUrl) continue
          const requestedPdfId = String(qq?.pdfField?.id || '').trim()
          const pdfField = pdfFields.find((f: any) =>
            f.name === requestedPdfId ||
            f.acroName === requestedPdfId ||
            normalizePdfLookupKey(f.name) === normalizePdfLookupKey(requestedPdfId) ||
            normalizePdfLookupKey(f.acroName || '') === normalizePdfLookupKey(requestedPdfId)
          )
          if (!pdfField?.rect) {
            console.warn(`Signature stamp skipped, no widget rect for ${requestedPdfId}`)
            continue
          }
          const { page: pageIndex = 0, rect } = pdfField
          try {
            const base64 = sigValue.dataUrl.includes(',') ? sigValue.dataUrl.split(',')[1] : sigValue.dataUrl
            const pngBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
            const png = await pdfDoc.embedPng(pngBytes)
            pdfDoc.getPage(pageIndex).drawImage(png, { x: rect.x, y: rect.y, width: rect.width, height: rect.height })
          } catch (e) {
            console.warn(`Signature stamp failed for ${requestedPdfId}:`, e)
          }
        }
      }

      refreshButtonAppearances(acroFields, { PDFCheckBox, PDFRadioGroup })
      try {
        pdfForm.updateFieldAppearances()
      } catch (e) {
        console.warn('PDF text appearance update skipped:', e)
      }

      let flattenFailed = false
      if (opts.flatten) {
        try {
          pdfForm.flatten({ updateFieldAppearances: false })
        } catch (e) {
          flattenFailed = true
          console.warn('PDF flatten skipped due to template incompatibility:', e)
        }
      }

      await stampSignatures()

      await appendHospitalRoutePages(pdfDoc)
      const diagnostics = {
        answeredLeafQuestions: countAnsweredLeafQuestions(),
        queuedWrites: pendingWrites.size,
        unresolvedCount: unresolvedIds.size,
        unresolvedSample: Array.from(unresolvedIds).slice(0, 10),
        flatten: opts.flatten,
        flattenFailed,
      }
      if (import.meta.env.DEV) {
        console.info('PDF export diagnostics:', diagnostics)
      }
      return { unresolvedIds, diagnostics, flattenFailed }
    }

    const finalizeErrors: string[] = []
    const pushError = (label: string, e: any) => {
      const msg = e?.message ? String(e.message) : String(e)
      finalizeErrors.push(`${label}: ${msg}`)
    }

    // Attempt A: fill + flatten + save.
    try {
      const docA = await PDFDocument.load(bytes)
      const { unresolvedIds: unresolvedA, flattenFailed: flattenFailedA } = await applyToDoc(docA, { flatten: true })
      throwIfUnresolved(unresolvedA)
      if (flattenFailedA) {
        throw new Error('flatten-incompatible-template')
      }
      try {
        return await docA.save()
      } catch (e) {
        pushError('flattened-save', e)
      }
    } catch (e) {
      // If mismatch already identified, do not mask it.
      if (String((e as any)?.message || '').includes('Unresolved PDF fields')) throw e
      pushError('attempt-a', e)
    }

    // Attempt B: re-open + fill + no flatten + save.
    try {
      const docB = await PDFDocument.load(bytes)
      const { unresolvedIds: unresolvedB } = await applyToDoc(docB, { flatten: false })
      throwIfUnresolved(unresolvedB)
      try {
        return await docB.save()
      } catch (e) {
        pushError('no-flatten-save', e)
      }
    } catch (e) {
      if (String((e as any)?.message || '').includes('Unresolved PDF fields')) throw e
      pushError('attempt-b', e)
    }

    // Attempt C: re-open + fill + no flatten + conservative save options.
    try {
      const docC = await PDFDocument.load(bytes)
      const { unresolvedIds: unresolvedC } = await applyToDoc(docC, { flatten: false })
      throwIfUnresolved(unresolvedC)
      return await docC.save({ useObjectStreams: false })
    } catch (e: any) {
      pushError('conservative-save', e)
      const structural = finalizeErrors.some(msg => /PDFDict|Expected instance/i.test(msg))
      if (structural) {
        throw new Error(
          `Failed to finalize completed PDF due to template structure incompatibility (${(e?.message || 'pdf-lib save error')}). Try re-uploading/flattening the source PDF in Acrobat and run Audit Form again.`,
        )
      }
      throw new Error(`Failed to finalize completed PDF. ${finalizeErrors.join(' | ')}`)
    }
  }

  const openMapModalForField = (fieldId: string, config: any) => {
    const initialValue = String(formAnswers[fieldId] || '').trim()
    setMapModal({ open: true, fieldId, config })
    setMapModalSearch(initialValue)
    setMapModalResults([])
    setMapModalSelected(null)
  }

  const searchMapLocation = React.useCallback(async (query: string) => {
    const trimmed = String(query || '').trim()
    if (trimmed.length < 2) {
      setMapModalResults([])
      setMapModalSelected(null)
      return
    }
    setMapModalLoading(true)
    try {
      const baseUrl = API_BASE_URL
      const token = localStorage.getItem('lts_jwt_token')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      }
      const capabilityRequest = {
        app: 'forms',
        capability: 'maps.geocodeAddress',
        input: { q: trimmed },
      }
      const res = await apiFetch(`${baseUrl}/capabilities/execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify(capabilityRequest),
      })
      if (!res.ok) throw new Error('Geocode failed')
      const payload = await res.json()
      const results = Array.isArray(payload?.data?.results) ? payload.data.results : []
      setMapModalResults(results)
      setMapModalSelected(results[0] ? { lat: parseFloat(results[0].lat), lon: parseFloat(results[0].lon), display_name: results[0].display_name } : null)
    } catch {
      setMapModalResults([])
      setMapModalSelected(null)
    } finally {
      setMapModalLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (!mapModal.open) return
    if (mapModalSearchTimerRef.current) window.clearTimeout(mapModalSearchTimerRef.current)
    if (mapModalSearch.trim().length >= 2) {
      mapModalSearchTimerRef.current = window.setTimeout(() => searchMapLocation(mapModalSearch), 400) as unknown as number
      return () => { if (mapModalSearchTimerRef.current) window.clearTimeout(mapModalSearchTimerRef.current) }
    } else {
      setMapModalResults([])
      setMapModalSelected(null)
    }
  }, [mapModal.open, mapModalSearch, searchMapLocation])

  // Handle previous step
  const handlePrevStep = () => {
    if (currentStep <= 0) return
    flushFormCache()
    setCurrentStep(currentStep - 1)
    try { window.scrollTo({ top: 0, behavior: 'smooth' }) } catch {}
  }

  // Handle back navigation
  const handleBack = () => {
    flushFormCache()
    const currentTab = view.view === 'form' ? (activeWorkflowName ? 'workflows' : 'forms') : (view.view === 'formManagement' ? 'management' : 'forms')
    setView({ view: 'selection', tab: currentTab })
    setCurrentForm(null)
    setCurrentStep(0)
    setFormAnswers({})
    setFormErrors({})
    setActiveWorkflowName(undefined)
    setWorkflowFormsToShow([])
    setShowEditFormList(false)
    setActiveInstance(null)
    void refreshPending()
  }



  // Breadcrumbs
  const iconMap = {
    Workflow: faList,
    Form: faFileAlt,
  }

  const Breadcrumb: React.FC<{ root: 'Workflow' | 'Form'; label: string; onRootClick: () => void }> = ({ root, label, onRootClick }) => (
  <div className="flex justify-center mb-8">
    <div className="breadcrumbs text-base">
      <ul>
        <li>
          <a className="flex items-center gap-1" onClick={onRootClick} style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={iconMap[root]} className="h-4 w-4" />
            <span>{root}</span>
          </a>
        </li>
        <li>
          <span className="inline-flex items-center gap-1">
            {label}
          </span>
        </li>
      </ul>
    </div>
  </div>
)
  // Handle selecting a form to edit from the management tab
  const handleEditFormSelect = async (formName: string) => {
    setOpeningFormEditor(true)
    try {
    // Prefer disk so we don't edit a stale in-memory copy after import/save.
    const formData = ((await formService.fetchFormByName(formName)) || formService.getFormById(formName)) as any
    if (!formData) {
      console.error('Form not found:', formName)
      alert('Form not found')
      return
    }
    
    setEditingSourceFormName(formData.formName || formName)
    setManagementDetails({
      formName: formData.formName || '',
      workflow: formData.workflow || '',
      workflowOrder: formData.workflowOrder || 1,
      workflowOptional: formData.workflowOptional || false,
      category: formData.category || '',
      estimatedTime: formData.estimatedTime || '',
      description: formData.description || '',
      accessLevels: Array.isArray(formData.accessLevels) ? formData.accessLevels : [],
      notifications: normalizeFormNotifications(formData.notifications),
      pdfFile: null,
      pdfBase64: formData.pdf?.base64 || '',
      pdfDataUrl: formData.pdf?.base64 ? `data:application/pdf;base64,${formData.pdf.base64}` : '',
      upload: formData.upload || { drive: false, jobtread: false, driveFolder: '', jtFolder: '', jobFieldId: '' },
      cache: formData.cache || { enabled: false, expiryHours: 24 },
      hospitalMaps: formData.hospitalMaps || { enabled: false, siteAddressField: '', hospitalAddressField: '' },
      fillers: normalizeFillers(formData.fillers),
    })
    
    // Reconstruct pages, groups, and field configs from steps
    const reconstructedPages: PageDef[] = []
    const reconstructedGroups: FieldGroup[] = []
    const reconstructedGroupConfigs: Record<string, any> = {}
    const reconstructedPageConfigs: Record<string, any> = {}
    const configs: Record<string, any> = {}
    const allFieldNames: string[] = []

    // Used to avoid re-creating nested groups if they appear more than once.
    const builtGroupIds = new Set<string>()

    const collectLeafFieldIdsFromGroupQuestion = (groupQ: any, out: Set<string>) => {
      for (const child of groupQ?.questions || []) {
        if (child?.type === 'group') {
          collectLeafFieldIdsFromGroupQuestion(child, out)
        } else if (child?.id) {
          out.add(child.id)
        }
      }
    }

    const buildGroupRecursively = (groupQ: any): string => {
      const groupId = groupQ?.id?.replace(/^group_/, '') || `group_${Date.now()}`
      if (builtGroupIds.has(groupId)) return groupId

      const directFieldNames: string[] = []
      const children: Array<any> = []

      for (const child of groupQ?.questions || []) {
        if (child?.type === 'group') {
          const childGroupId = buildGroupRecursively(child)
          children.push({ type: 'group', groupId: childGroupId })
        } else if (child?.id) {
          directFieldNames.push(child.id)
          children.push({ type: 'field', fieldName: child.id })

          allFieldNames.push(child.id)
          configs[child.id] = {
            label: child.label || child.id,
            type: child.type || 'text',
            description: child.description || '',
            required: child.readonly ? false : child.required !== false,
            readonly: !!child.readonly,
            fillerStage: Number(child.fillerStage) >= 1 ? Number(child.fillerStage) : 1,
            options: child.options,
            tool: child.tool,
            showDescription: !!child.description,
            excludeFromCache: child.excludeFromCache,
            signatureSource: child.signatureSource,
            format: child.format,
          }
        }
      }

      reconstructedGroups.push({
        id: groupId,
        name: groupQ.name || 'Unnamed Group',
        fieldNames: directFieldNames,
        // Runtime-only: PdfFieldsTree uses this to render nested groups.
        children,
      } as any)

      reconstructedGroupConfigs[groupId] = {
        description: groupQ.description || '',
        layout: groupQ.layout || 'stack',
        showHeading: groupQ.showHeading ?? true,
        showDescription: groupQ.showDescription ?? false,
        mode: groupQ.mode || 'fields',
        fillerStage: Number(groupQ.fillerStage) >= 1 ? Number(groupQ.fillerStage) : 1,
        readonly: !!groupQ.readonly,
        required: groupQ.required !== false,
      }

      builtGroupIds.add(groupId)
      return groupId
    }
    
    formData.steps?.forEach((step: any, stepIdx: number) => {
      let pageId = step.id || `step_${stepIdx + 1}`
      let pageName = step.title || `Page ${stepIdx + 1}`
      // Legacy saves wrote leftover fields as id "unassigned" which the editor used to drop.
      if (pageId === 'unassigned') {
        pageId = `recovered_${stepIdx + 1}`
        if (!pageName || pageName === 'Unassigned') pageName = `Page ${stepIdx + 1}`
      }
      const pageItems: Array<{ type: 'field' | 'group'; key: string }> = []
      
      // Store page config
      reconstructedPageConfigs[pageId] = {
        description: step.description || ''
      }

      // Pre-pass: collect field IDs that belong to groups (skip adding them as standalone)
      const fieldsInGroupsInStep = new Set<string>()
      step.questions?.forEach((q: any) => {
        if (q?.type === 'group') collectLeafFieldIdsFromGroupQuestion(q, fieldsInGroupsInStep)
      })
      
      step.questions?.forEach((q: any) => {
        if (q.type === 'group' && q.questions) {
          // This is a group
          const groupId = buildGroupRecursively(q)
          pageItems.push({ type: 'group', key: groupId })
        } else {
          // This is a standalone field - skip if already inside a group (avoids duplication)
          if (fieldsInGroupsInStep.has(q.id)) return
          allFieldNames.push(q.id)
          pageItems.push({ type: 'field', key: q.id })
          
          configs[q.id] = {
            label: q.label || q.id,
            type: q.type || 'text',
            description: q.description || '',
              required: q.readonly ? false : q.required !== false,
            readonly: !!q.readonly,
            fillerStage: Number(q.fillerStage) >= 1 ? Number(q.fillerStage) : 1,
            options: q.options,
            tool: q.tool,
            showDescription: !!q.description,
            excludeFromCache: q.excludeFromCache,
            signatureSource: q.signatureSource,
            format: q.format,
          }
        }
      })
      
      reconstructedPages.push({
        id: pageId,
        name: pageName,
        items: pageItems
      })
    })
    
    // Set reconstructed state
    setPages(reconstructedPages)
    setFieldGroups(reconstructedGroups)
    setGroupConfigs(reconstructedGroupConfigs)
    setPageConfigs(reconstructedPageConfigs)
    setFieldConfigs(configs)
    setExpandedPages(new Set(reconstructedPages.map(p => p.id)))
    setExpandedGroups(new Set(reconstructedGroups.map(g => g.id)))

    const savedPdfFields = formData.pdf?.fields
      ? normalizeParsedPdfFields(formData.pdf.fields)
      : []
    if (savedPdfFields.length) {
      setParsedPdfFields(savedPdfFields)
      ensureFieldConfigsForParsedFields(savedPdfFields)
    }

    setShowEditFormList(false)
    setManagementMode('edit')
    setManagementStep(0)
    setLegacyFormAccess(!Array.isArray(formData.accessLevels))
    setView({ view: 'formManagement', mode: 'edit' })
    if (levelNames.length === 0) {
      setLevelsLoading(true)
      permissionService.listLevelNames()
        .then((names) => setLevelNames(names))
        .catch(() => setLevelNames([]))
        .finally(() => setLevelsLoading(false))
    }
    setOpeningFormEditor(false)
    if (formData.pdf?.base64) {
      renderPdfWithHighlight(formData.pdf.base64)
    }

    // Refresh widget positions after details are on screen so metadata edits are not blocked.
    if (formData.pdf?.base64) {
      try {
        const pdfData = atob(formData.pdf.base64)
        const pdfArray = new Uint8Array(pdfData.length)
        for (let i = 0; i < pdfData.length; i++) {
          pdfArray[i] = pdfData.charCodeAt(i)
        }
        const blob = new Blob([pdfArray], { type: 'application/pdf' })
        const file = new File([blob], 'form.pdf', { type: 'application/pdf' })
        const fieldsWithPositions = await extractPdfFields(file)
        const normalizedFields = normalizeParsedPdfFields(fieldsWithPositions)
        setParsedPdfFields(normalizedFields)
        ensureFieldConfigsForParsedFields(normalizedFields)
        renderPdfWithHighlight(formData.pdf.base64)
      } catch (e) {
        console.error('Failed to extract field positions:', e)
        if (savedPdfFields.length) {
          setParsedPdfFields(savedPdfFields)
          ensureFieldConfigsForParsedFields(savedPdfFields)
        }
      }
    }
    } catch (e) {
      console.error('Failed to open form for editing:', e)
      alert('Failed to open form for editing')
    } finally {
      setOpeningFormEditor(false)
    }
  }

  // Open Form Management view
  const openFormManagement = (mode: 'create' | 'edit') => {
    setManagementMode(mode)
    setManagementStep(0)
    // Reset state for create mode to avoid leaking previous edit data
    if (mode === 'create') {
      setEditingSourceFormName('')
      setManagementDetails({
        formName: '',
        workflow: '',
        workflowOrder: 1,
        workflowOptional: false,
        category: '',
        estimatedTime: '',
        description: '',
        accessLevels: [],
        notifications: DEFAULT_FORM_NOTIFICATIONS,
        pdfFile: null,
        pdfBase64: '',
        pdfDataUrl: '',
        upload: { drive: false, jobtread: false, driveFolder: '', jtFolder: '', jobFieldId: '' },
        cache: { enabled: false, expiryHours: 24 },
        hospitalMaps: { enabled: false, siteAddressField: '', hospitalAddressField: '' },
        fillers: DEFAULT_FILLERS as FormFiller[],
      })
      setParsedPdfFields([])
      setSelectedPdfField(null)
      setFieldConfigs({})
      setFieldGroups([])
      setPages([])
      setGroupConfigs({})
      setPageConfigs({})
      setExpandedGroups(new Set())
      setExpandedPages(new Set())
      setSelectedKeys([])
      setSelectedGroup(null)
      setSelectedPage(null)
      setOptionsDraft({})
      // Reset PDF viewer state
      setPdfPages([])
      setPdfDoc(null)
      setPdfRenderedPages(new Set())
      pdfCanvasRefs.current.clear()
      pdfRenderTasks.current.clear()
    }
    setLegacyFormAccess(false)
    setView({ view: 'formManagement', mode })
    if (levelNames.length === 0) {
      setLevelsLoading(true)
      permissionService.listLevelNames()
        .then((names) => setLevelNames(names))
        .catch(() => setLevelNames([]))
        .finally(() => setLevelsLoading(false))
    }
  }

  // Tool Management state
  const [toolTab, setToolTab] = useState<'weather' | 'maps' | 'drive' | 'jobtread' | 'linxup'>('weather')
  const openToolManagement = () => {
    if (!hasPermission('adminPage')) return
    setToolTab('weather')
    setView({ view: 'toolManagement' })
  }

  const fileToBase64 = (file: File): Promise<{ base64: string; dataUrl: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        if (result.startsWith('data:')) {
          const base64 = result.split(',')[1] || ''
          resolve({ base64, dataUrl: result })
        } else {
          resolve({ base64: result, dataUrl: `data:${file.type};base64,${result}` })
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const extractPdfFields = async (file: File): Promise<PdfField[]> => {
    // Use pdf-lib to read AcroForm fields without workers
    const arrayBuffer = await file.arrayBuffer()
    const { PDFDocument, PDFName, PDFCheckBox, PDFTextField, PDFRadioGroup, PDFDropdown, PDFOptionList, PDFButton, PDFSignature } = await import('pdf-lib')
    const { buildAnnotPageIndex, kidObjectNumbers, resolveWidgetPage } = await import('../utils/pdfWidgetPages')
    const pdfDoc = await PDFDocument.load(arrayBuffer)
    const form = pdfDoc.getForm()
    const fields = form.getFields()
    const pages = pdfDoc.getPages()
    const annotPageIndex = buildAnnotPageIndex(pages)
    
    const resolveWidgetRect = (widget: any): PdfField['rect'] | undefined => {
      try {
        if (typeof widget?.getRectangle === 'function') {
          const rectangle = widget.getRectangle()
          if (rectangle) {
            return {
              x: rectangle.x,
              y: rectangle.y,
              width: rectangle.width,
              height: rectangle.height,
            }
          }
        }
      } catch {}
      try {
        const rectArray = widget?.dict?.get?.(PDFName.of('Rect'))
        const rectArrayAny = rectArray as any
        if (rectArray && typeof rectArrayAny.asArray === 'function') {
          const arr = rectArrayAny.asArray()
          if (arr.length === 4) {
            const x1 = arr[0]?.asNumber?.() ?? 0
            const y1 = arr[1]?.asNumber?.() ?? 0
            const x2 = arr[2]?.asNumber?.() ?? 0
            const y2 = arr[3]?.asNumber?.() ?? 0
            return {
              x: Math.min(x1, x2),
              y: Math.min(y1, y2),
              width: Math.abs(x2 - x1),
              height: Math.abs(y2 - y1),
            }
          }
        }
      } catch {}
      return undefined
    }

    const pageOfWidget = (widget: any, kidObjectNumber?: number): number | undefined =>
      resolveWidgetPage(widget, { pages, annotPageIndex, kidObjectNumber })

    // 1) Expand widget types into a normalized flat list.
    // 2) Group by AcroForm name and retain all widget instances.
    const expanded: PdfField[] = []

    for (const f of fields as any[]) {
      const rawName: string = String(f.getName() || '').trim()
      const acroName = rawName || normalizePdfFieldName(rawName)
      const name = normalizePdfFieldName(rawName) || acroName
      let type = 'text'
      let options: string[] | undefined
      let rect: PdfField['rect'] = undefined
      let page: number | undefined = pages.length === 1 ? 0 : undefined
      let widgets: PdfFieldWidget[] = []
      let rawWidgets: any[] = []

      // Use instanceof instead of constructor.name - survives production minification (pdf-lib#933)
      if (f instanceof PDFCheckBox) {
        type = 'checkbox'
      } else if (f instanceof PDFRadioGroup) {
        type = 'radio'
        try {
          const raw = f.getOptions() ?? []
          options = raw.map((o: any, i: number) => {
            const s = o != null && o !== undefined ? String(o) : null
            return s && s !== 'undefined' ? s : `Option ${i + 1}`
          })
        } catch {}
      } else if (f instanceof PDFDropdown || f instanceof PDFOptionList) {
        type = 'select'
        try {
          const raw = f.getOptions() ?? []
          options = raw.map((o: any, i: number) => {
            const s = o != null && o !== undefined ? String(o) : null
            return s && s !== 'undefined' ? s : `Option ${i + 1}`
          })
        } catch {}
      } else if (f instanceof PDFTextField) {
        type = 'text'
      } else if (f instanceof PDFButton) {
        type = 'button'
      } else if (f instanceof PDFSignature) {
        type = 'signature'
      }

      // Try to get field position using multiple methods
      try {
        // Method 1: Try acroField.getWidgets()
        const acroField = f.acroField
        if (acroField) {
          rawWidgets = []

          // Try getWidgets method
          if (typeof acroField.getWidgets === 'function') {
            rawWidgets = acroField.getWidgets() || []
          }

          // If no widgets, try to get from Kids array or directly from acroField
          if (rawWidgets.length === 0 && acroField.dict) {
            const kids = acroField.dict.get(PDFName.of('Kids'))
            const kidsAny = kids as any
            if (kids && Array.isArray(kidsAny.asArray?.())) {
              rawWidgets = kidsAny.asArray()
            }
          }

          if (rawWidgets.length > 0) {
            const kidNums = kidObjectNumbers(acroField)
            const widget = rawWidgets[0]

            // Try getRectangle method first
            if (typeof widget.getRectangle === 'function') {
              const rectangle = widget.getRectangle()
              if (rectangle) {
                rect = {
                  x: rectangle.x,
                  y: rectangle.y,
                  width: rectangle.width,
                  height: rectangle.height,
                }
              }
            }

            // If getRectangle didn't work, try getting Rect from dict
            if (!rect && widget.dict) {
              const rectArray = widget.dict.get(PDFName.of('Rect'))
              const rectArrayAny = rectArray as any
              if (rectArray && typeof rectArrayAny.asArray === 'function') {
                const arr = rectArrayAny.asArray()
                if (arr.length === 4) {
                  const x1 = arr[0]?.asNumber?.() ?? 0
                  const y1 = arr[1]?.asNumber?.() ?? 0
                  const x2 = arr[2]?.asNumber?.() ?? 0
                  const y2 = arr[3]?.asNumber?.() ?? 0
                  rect = {
                    x: Math.min(x1, x2),
                    y: Math.min(y1, y2),
                    width: Math.abs(x2 - x1),
                    height: Math.abs(y2 - y1),
                  }
                }
              }
            }

            widgets = rawWidgets.map((w: any, idx: number) => {
              const resolvedPage = pageOfWidget(w, kidNums[idx])
              return {
                widgetId: `${acroName || name}::${idx + 1}`,
                rect: resolveWidgetRect(w),
                page: typeof resolvedPage === 'number' ? resolvedPage : (pages.length === 1 ? 0 : undefined),
              }
            })

            const primary = widgets.find(w => !!w.rect) || widgets[0]
            if (primary?.rect) rect = primary.rect
            if (typeof primary?.page === 'number') page = primary.page
          }
        }

        // Method 2: If still no rect, try getting directly from field dict
        if (!rect && f.acroField?.dict) {
          const rectArray = f.acroField.dict.get(PDFName.of('Rect'))
          const rectArrayAny = rectArray as any
          if (rectArray && typeof rectArrayAny.asArray === 'function') {
            const arr = rectArrayAny.asArray()
            if (arr.length === 4) {
              const x1 = arr[0]?.asNumber?.() ?? 0
              const y1 = arr[1]?.asNumber?.() ?? 0
              const x2 = arr[2]?.asNumber?.() ?? 0
              const y2 = arr[3]?.asNumber?.() ?? 0
              rect = {
                x: Math.min(x1, x2),
                y: Math.min(y1, y2),
                width: Math.abs(x2 - x1),
                height: Math.abs(y2 - y1),
              }
            }
          }
        }
      } catch (e) {
        // Field position extraction failed, continue without it
        console.debug('Could not extract position for field:', rawName, e)
      }

      // Radio groups: one designer member per option, tagged with the Acrobat parent group name.
      if (f instanceof PDFRadioGroup) {
        const groupName = resolveRadioGroupName(f, PDFName) || name || 'Radio'
        const opts = options && options.length ? options : ['Option 1']
        for (let i = 0; i < opts.length; i++) {
          const optionValue = String(opts[i])
          const raw = rawWidgets[i] || (opts.length === 1 ? rawWidgets[0] : undefined)
          const memberRect = raw ? resolveWidgetRect(raw) : (opts.length === 1 ? rect : undefined)
          const memberWidgetEntry = widgets[i] || (opts.length === 1 ? widgets[0] : undefined)
          const memberPage = typeof memberWidgetEntry?.page === 'number'
            ? memberWidgetEntry.page
            : (pages.length === 1 ? 0 : undefined)
          const memberWidget = (memberRect || rect)
            ? [{
                widgetId: `${groupName}::${optionValue}`,
                rect: memberRect || rect,
                page: memberPage,
              }]
            : undefined
          expanded.push({
            name: makeRadioMemberName(groupName, optionValue),
            acroName: groupName,
            type: 'checkbox',
            options: [optionValue],
            radioGroup: groupName,
            optionValue,
            rect: memberRect || rect,
            page: memberPage,
            widgets: memberWidget,
            instanceCount: 1,
          })
        }
      } else {
        expanded.push({
          name,
          acroName,
          type,
          options,
          rect,
          page,
          widgets,
          instanceCount: widgets.length > 0 ? widgets.length : 1,
        })
      }
    }

    const ensureUniqueLogicalName = (base: string, used: Set<string>): string => {
      let candidate = String(base || 'field').trim() || 'field'
      if (!used.has(candidate)) {
        used.add(candidate)
        return candidate
      }
      let n = 2
      while (used.has(`${candidate}__dup_${n}`)) n += 1
      const next = `${candidate}__dup_${n}`
      used.add(next)
      return next
    }

    // Do NOT merge duplicates: each extracted occurrence stays visible in the editor.
    const usedLogicalNames = new Set<string>()
    const logical = expanded.map((item, idx) => {
      const acroKey = String(item.acroName || item.name || '').trim() || `field_${idx + 1}`
      const uniqueLogicalName = ensureUniqueLogicalName(item.name || acroKey, usedLogicalNames)
      const widgets = item.widgets && item.widgets.length > 0
        ? item.widgets.map((w, widx) => ({ ...w, widgetId: `${uniqueLogicalName}::${widx + 1}` }))
        : (item.rect ? [{ widgetId: `${uniqueLogicalName}::1`, rect: item.rect, page: item.page }] : [])

      return {
        ...item,
        name: uniqueLogicalName,
        acroName: acroKey,
        widgets,
        rect: item.rect || widgets.find(w => !!w.rect)?.rect,
        page: typeof item.page === 'number' ? item.page : widgets.find(w => typeof w.page === 'number')?.page || 0,
        instanceCount: widgets.length > 0 ? widgets.length : (item.instanceCount || 1),
      } as PdfField
    })

    if (import.meta.env.DEV) {
      console.log('Extracted PDF fields (raw -> logical):', expanded.length, '->', logical.length)
      const radioMembers = logical.filter(f => !!f.radioGroup)
      const radioGroups = new Set(radioMembers.map(f => f.radioGroup || ''))
      console.log('PDF radio groups expanded:', radioGroups.size, 'radio members:', radioMembers.length)
      console.log(
        'Extracted PDF fields (logical sample):',
        logical.slice(0, 30).map(f => ({
          name: f.name,
          acroName: f.acroName || f.name,
          widgets: f.widgets?.length || 0,
          type: f.type,
          hasRect: !!f.rect,
          page: f.page,
          rect: f.rect ? `${Math.round(f.rect.x)},${Math.round(f.rect.y)} ${Math.round(f.rect.width)}x${Math.round(f.rect.height)}` : 'none',
        })),
      )
    }

    return logical
  }

  // Render PDF pages using PDF.js
  const renderPdfWithHighlight = async (base64: string) => {
    try {
      const pdfjsLib = await import('pdfjs-dist')
      
      // Inline worker as Blob URL to avoid MIME type issues in production (application/octet-stream)
      if (pdfWorkerBlobUrlRef.current) {
        URL.revokeObjectURL(pdfWorkerBlobUrlRef.current)
        pdfWorkerBlobUrlRef.current = null
      }
      const workerModule = await import('pdfjs-dist/build/pdf.worker.min.mjs?raw')
      const blob = new Blob([workerModule.default], { type: 'application/javascript' })
      pdfWorkerBlobUrlRef.current = URL.createObjectURL(blob)
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerBlobUrlRef.current
      
      const pdfData = atob(base64)
      const pdfArray = new Uint8Array(pdfData.length)
      for (let i = 0; i < pdfData.length; i++) {
        pdfArray[i] = pdfData.charCodeAt(i)
      }
      
      const pdf = await pdfjsLib.getDocument({ data: pdfArray, useWorkerFetch: false, isEvalSupported: false, useSystemFonts: true }).promise
      
      // Reset state for new PDF
      setPdfRenderedPages(new Set())
      pdfCanvasRefs.current.clear()
      pdfRenderTasks.current.clear()
      pdfRenderLock.current = false
      
      setPdfDoc(pdf) // Store for re-rendering
      
      const pageInfos: { pageNum: number; width: number; height: number }[] = []
      
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum)
        const viewport = page.getViewport({ scale: 1.0 })
        pageInfos.push({ pageNum, width: viewport.width, height: viewport.height })
      }
      
      setPdfPages(pageInfos)
      
      // Calculate scale to fit container
      if (pdfContainerRef.current && pageInfos.length > 0) {
        const containerWidth = pdfContainerRef.current.clientWidth - 48 // padding
        const firstPageWidth = pageInfos[0].width
        if (containerWidth > 0 && firstPageWidth > 0) {
          const scale = Math.max(0.5, Math.min(containerWidth / firstPageWidth, 1.5))
          setPdfScale(scale)
        } else {
          setPdfScale(1.0)
        }
      } else {
        setPdfScale(1.0)
      }
    } catch (e) {
      console.error('Failed to render PDF:', e)
    }
  }

  const onPdfSelected = async (file: File) => {
    try {
      const { base64, dataUrl } = await fileToBase64(file)
      setManagementDetails(d => ({ ...d, pdfFile: file, pdfBase64: base64, pdfDataUrl: dataUrl }))
      const fields = await extractPdfFields(file)
      const normalizedFields = normalizeParsedPdfFields(fields)
      setParsedPdfFields(normalizedFields)
      setSelectedPdfField(normalizedFields[0] || null)
      applyAutoRadioGroups(normalizedFields, 'seed')
      
      // Render PDF with PDF.js for field highlighting
      await renderPdfWithHighlight(base64)
      // Initialize default fieldConfigs for any newly detected fields
      ensureFieldConfigsForParsedFields(normalizedFields)
    } catch (e) {
      console.error('Failed to read PDF:', e)
    }
  }

  const simulateParsePdfFields = () => {
    // If file already selected, just proceed; otherwise, create stub fields
    if (managementDetails.pdfFile) {
      setManagementStep(1)
      return
    }
    const sample: PdfField[] = [
      { name: 'employeeName', type: 'text' },
      { name: 'employeeId', type: 'text' },
      { name: 'department', type: 'select' },
    ]
    const normalizedSample = normalizeParsedPdfFields(sample)
    setParsedPdfFields(normalizedSample)
    setSelectedPdfField(normalizedSample[0] || null)
    setFieldConfigs({
      employeeName: { label: 'Employee Name', type: 'text', required: true, description: '', tool: undefined },
      employeeId: { label: 'Employee ID', type: 'text', required: true, description: '', tool: undefined },
      department: { label: 'Department', type: 'select', required: true, description: '', options: ['HR','Finance'], tool: undefined },
    })
    setManagementStep(1)
  }

  // Build JSON payload compatible with our enhanced schema
  const buildFormJson = (finalize: boolean) => {
    const fieldOptions = (fname: string, cfgOptions?: string[]) => (
      optionsDraft[fname] != null ? parseOptionsInput(optionsDraft[fname]) : cfgOptions
    )

    const buildPdfFieldRef = (pdf: PdfField | undefined, fname: string, cfgType?: string) => ({
      id: pdf?.radioGroup || pdf?.acroName || fname,
      type: pdf?.radioGroup ? 'radio' : (pdf?.type || cfgType || 'text'),
      ...(pdf?.optionValue || pdf?.options?.[0]
        ? { option: pdf.optionValue || pdf.options?.[0] }
        : {}),
    })

    const buildFieldQuestion = (fname: string, memberIdx: number): any => {
      const pdf = parsedPdfFields.find(f => f.name === fname)
      const cfg = fieldConfigs[fname]
      const rawLabel = cfg?.label || fname
      const label = (rawLabel && !String(rawLabel).includes('undefined')) ? rawLabel : `Option ${memberIdx + 1}`
      return cfg ? {
        id: fname,
        type: cfg.type,
        label,
        required: cfg.readonly ? false : cfg.required !== false,
        readonly: !!cfg.readonly,
        fillerStage: Number(cfg.fillerStage) >= 1 ? Number(cfg.fillerStage) : 1,
        description: cfg.showDescription ? cfg.description : undefined,
        options: fieldOptions(fname, cfg.options),
        pdfField: buildPdfFieldRef(pdf, fname, cfg.type),
        tool: cfg.tool,
        excludeFromCache: cfg.excludeFromCache,
        ...(cfg.type === SIGNATURE_DATE_TYPE ? {
          signatureSource: cfg.signatureSource,
          format: cfg.format || 'MM/DD/YYYY',
        } : {}),
      } : null
    }

    const buildGroupQuestion = (g: any): any => {
      const gCfg = groupConfigs[g.id] || {}
      const children = groupChildren(g)
      const questions: any[] = []
      let memberIdx = 0
      for (const ch of children) {
        if (ch?.type === 'field') {
          const q = buildFieldQuestion(ch.fieldName, memberIdx)
          if (q) questions.push(q)
          memberIdx += 1
        } else if (ch?.type === 'group') {
          const childGroup = fieldGroups.find(gr => gr.id === ch.groupId)
          if (childGroup) questions.push(buildGroupQuestion(childGroup))
        }
      }
      return {
        id: `group_${g.id}`,
        type: 'group',
        name: g.name,
        layout: gCfg.layout || 'stack',
        showHeading: !!gCfg.showHeading,
        showDescription: !!gCfg.showDescription,
        description: gCfg.description,
        mode: gCfg.mode || 'fields',
        required: gCfg.mode === 'radio' ? (!gCfg.readonly && gCfg.required !== false) : undefined,
        readonly: gCfg.mode === 'radio' ? !!gCfg.readonly : undefined,
        fillerStage: Number(gCfg.fillerStage) >= 1 ? Number(gCfg.fillerStage) : 1,
        questions,
      }
    }

    const itemsToStep = (items: Array<{ type: 'field' | 'group'; key: string }>, stepId: string, stepName: string) => {
      const groupIdsInItems = new Set(items.filter(it => it.type === 'group').map(it => it.key))
      const fieldsInGroupsInItems = new Set(
        fieldGroups.filter(g => groupIdsInItems.has(g.id)).flatMap(g => collectLeafFieldNames(fieldGroups, g.id))
      )
      const dedupedItems = items.filter(it =>
        it.type === 'group' || !fieldsInGroupsInItems.has(it.key)
      )
      const questions = dedupedItems.flatMap((itm: any): any[] => {
        if (itm.type === 'group') {
          const g = fieldGroups.find(gr => gr.id === itm.key)
          if (!g) return []
          return [buildGroupQuestion(g)]
        } else {
          const fname = itm.key
          const pdf = parsedPdfFields.find(f => f.name === fname)
          const cfg = fieldConfigs[fname]
          const rawLabel = cfg?.label || fname
          const label = (rawLabel && !String(rawLabel).includes('undefined')) ? rawLabel : fname.replace(/\.undefined$/i, '') || fname
          return cfg ? [{
            id: fname,
            type: cfg.type,
            label,
            required: cfg.readonly ? false : cfg.required !== false,
            readonly: !!cfg.readonly,
            fillerStage: Number(cfg.fillerStage) >= 1 ? Number(cfg.fillerStage) : 1,
            description: cfg.showDescription ? cfg.description : undefined,
            options: fieldOptions(fname, cfg.options),
            pdfField: buildPdfFieldRef(pdf, fname, cfg.type),
            tool: cfg.tool,
            excludeFromCache: cfg.excludeFromCache,
            ...(cfg.type === SIGNATURE_DATE_TYPE ? {
              signatureSource: cfg.signatureSource,
              format: cfg.format || 'MM/DD/YYYY',
            } : {}),
          }] : []
        }
      })
      return { id: stepId, title: stepName, description: pageConfigs[stepId]?.description, questions }
    }

    const pagesToSave = persistPages({ pages, fieldGroups }, parsedFieldNames)
    const steps: Array<{ id: string; title: string; description?: string; questions: any[] }> =
      pagesToSave.map((p, idx) => itemsToStep(p.items, p.id || `step_${idx + 1}`, p.name || `Step ${idx + 1}`))


    const details = managementDetailsRef.current
    const payload = {
      formName: details.formName,
      workflow: details.workflow || null,
      workflowOrder: details.workflowOrder,
      workflowOptional: details.workflow ? details.workflowOptional : false,
      description: details.description,
      category: details.category,
      estimatedTime: details.estimatedTime,
      // Store per-form tools at top-level if needed later (kept for compatibility)
      tools: [],
      cache: details.cache?.enabled ? details.cache : undefined,
      steps,
      fillers: normalizeFillers(details.fillers),
      accessLevels: details.accessLevels,
      notifications: details.notifications,
      pdf: {
        base64: details.pdfBase64,
        fields: parsedPdfFields,
      },
      upload: details.upload,
      hospitalMaps: details.hospitalMaps?.enabled ? details.hospitalMaps : undefined,
      status: finalize ? 'published' : 'draft',
      version: 2,
    }
    return payload
  }




  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex flex-col">
        <Navbar pageTitle="FORMS" news={news} recentActivity={recentActivity} />
        <main className="container mx-auto px-4 py-8 flex-1 flex items-center justify-center">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="fa-spin text-4xl text-primary mb-4" />
            <p className="text-lg">Loading forms...</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Navbar pageTitle="FORMS" news={news} recentActivity={recentActivity} />
      <main className="container mx-auto px-4 py-8 flex-1">
        {/* Selection Container */}
        <div className={view.view === 'selection' ? '' : 'hidden'}>
          {/* Tabs */}
          <div role="tablist" className="tabs tabs-lift mb-4">
            <button
              type="button"
              role="tab"
              className={`tab ${getCurrentTab() === 'pending' ? 'tab-active' : ''}`}
              onClick={() => handleTabChange('pending')}
            >
              Pending
              {pendingAssignments.filter((item) => item.yourTurn !== false).length > 0 && (
                <span className="badge badge-primary badge-sm ml-2">
                  {(() => {
                    const n = pendingAssignments.filter((item) => item.yourTurn !== false).length
                    return n > 99 ? '99+' : n
                  })()}
                </span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              className={`tab ${getCurrentTab() === 'workflows' ? 'tab-active' : ''}`}
              onClick={() => handleTabChange('workflows')}
            >
              Workflows
            </button>
            <button
              type="button"
              role="tab"
              className={`tab ${getCurrentTab() === 'forms' ? 'tab-active' : ''}`}
              onClick={() => handleTabChange('forms')}
            >
              All Forms
            </button>
            {hasPermission('formsManager') && (
              <button
                type="button"
                role="tab"
                className={`tab ${getCurrentTab() === 'management' ? 'tab-active' : ''}`}
                onClick={() => handleTabChange('management')}
              >
                Form Management
              </button>
            )}
          </div>
          {/* Pending Tab */}
          <div className={getCurrentTab() === 'pending' ? '' : 'hidden'}>
            {hasPermission('formsManager') && pendingAssignments.length > 0 && (
              <div className="mb-4">
                <h2 className="text-xl font-semibold mb-1">Your pending</h2>
                <p className="text-sm text-base-content/60">
                  Forms you started or still need to complete. You can cancel an agreement only if you started it.
                </p>
              </div>
            )}
            {pendingAssignments.length === 0 ? (
              <div className={`card bg-base-100 shadow-xl ${hasPermission('formsManager') ? 'mb-8' : 'max-w-xl mx-auto'}`}>
                <div className="card-body items-center text-center py-12">
                  <FontAwesomeIcon icon={faInbox} className="text-4xl text-base-content/30 mb-3" />
                  <h2 className="card-title">You're all caught up</h2>
                  <p className="text-base-content/70">
                    No forms are waiting for you right now. Assigned forms will show up here.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pendingAssignments.map((item) => renderPendingCard(item, { showCancel: !!item.canCancel }))}
              </div>
            )}
            {hasPermission('formsManager') && (
              <div className={pendingAssignments.length === 0 ? '' : 'mt-10'}>
                <h2 className="text-xl font-semibold mb-1">All pending agreements</h2>
                <p className="text-sm text-base-content/60 mb-4">
                  Every in-progress form, regardless of who started it. You can download the current PDF even if you are not a filler, and cancel any of these.
                </p>
                {allPendingAssignments.length === 0 ? (
                  <div className="card bg-base-100 shadow-xl">
                    <div className="card-body py-8">
                      <p className="text-base-content/70">There are no in-progress agreements right now.</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {allPendingAssignments.map((item) => renderPendingCard(item, { showCancel: true }))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Workflows Tab */}
          <div className={getCurrentTab() === 'workflows' ? '' : 'hidden'}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workflows.filter((workflow) => workflow.forms.some((form) => userCanFillForm(form))).map(workflow => (
                <div key={workflow.name} className="card bg-base-100 shadow-xl">
                  <div className="card-body">
                    <h2 className="card-title">
                      <FontAwesomeIcon icon={faList} className="text-primary" />
                      {workflow.name}
                    </h2>
                    <ul className="space-y-1 text-sm text-base-content/70 mb-2">
                      {workflow.forms.filter((form) => userCanFillForm(form)).map(form => (
                        <li key={form.formName} className="flex items-center gap-2">
                          <span className={form.workflowOptional ? 'opacity-70' : ''}>{form.formName}</span>
                          {form.workflowOptional && (
                            <span className="badge badge-xs badge-ghost">Optional</span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {workflow.forms.some(f => f.workflowOptional) && (
                      <p className="text-xs text-base-content/50 mb-2">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                        This workflow has optional forms you can skip
                      </p>
                    )}
                    <div className="card-actions justify-end mt-4">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleWorkflowSelect(workflow.name)}
                      >
                        Start Workflow
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* All Forms Tab */}
          <div className={getCurrentTab() === 'forms' ? '' : 'hidden'}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredForms.map(form => (
                <div key={form.formName} className="card bg-base-100 shadow-xl">
                  <div className="card-body">
                    <div className="flex justify-between items-start mb-2">
                      <h2 className="card-title text-lg">
                        <FontAwesomeIcon icon={faFileAlt} className="text-primary" />
                        {form.formName}
                      </h2>
                    </div>
                    <p className="text-sm text-base-content/70 mb-2">{form.description}</p>
                    <div className="flex justify-between items-center text-xs text-base-content/60 mb-4">
                      <span>{form.category}</span>
                      <span>{form.estimatedTime}</span>
                    </div>
                    <div className="card-actions justify-end">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleFormSelect(form.formName)}
                      >
                        Fill Out
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Management Tab */}
          <div className={getCurrentTab() === 'management' ? '' : 'hidden'}>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-2">Form Management</h2>
              <p className="text-base-content/70 mb-6">Create new forms or edit existing ones.</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <button 
                  onClick={() => openFormManagement('create')}
                  className="card bg-base-100 shadow hover:shadow-lg transition-shadow cursor-pointer text-left border-2 border-transparent hover:border-primary/20"
                >
                  <div className="card-body">
                    <h3 className="card-title">
                      <FontAwesomeIcon icon={faPlus} className="text-primary" />
                      Create New Form
                    </h3>
                    <p className="text-base-content/70">Start a new form from a PDF template.</p>
                  </div>
                </button>
                <button 
                  onClick={() => setShowEditFormList(true)}
                  className="card bg-base-100 shadow hover:shadow-lg transition-shadow cursor-pointer text-left border-2 border-transparent hover:border-secondary/20"
                >
                  <div className="card-body">
                    <h3 className="card-title">
                      <FontAwesomeIcon icon={faEdit} className="text-secondary" />
                      Edit Existing Form
                    </h3>
                    <p className="text-base-content/70">Modify fields and settings of an existing form.</p>
                  </div>
                </button>
                <button
                  onClick={() => importFormFileInputRef.current?.click()}
                  disabled={importFormBusy}
                  className="card bg-base-100 shadow hover:shadow-lg transition-shadow cursor-pointer text-left border-2 border-transparent hover:border-accent/20"
                >
                  <div className="card-body">
                    <h3 className="card-title">
                      <FontAwesomeIcon icon={faUpload} className="text-accent" />
                      Import Form JSON
                    </h3>
                    <p className="text-base-content/70">Upload a previously exported form JSON.</p>
                  </div>
                </button>

                <input
                  ref={importFormFileInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null
                    if (!f) return
                    e.currentTarget.value = ''
                    setImportFormBusy(true)
                    try {
                      const text = await f.text()
                      const parsed = JSON.parse(text)
                      const candidate = parsed?.data ? parsed.data : parsed
                      const rawImportedFormName = candidate?.formName || parsed?.formName
                      if (!rawImportedFormName || typeof rawImportedFormName !== 'string') {
                        throw new Error('Invalid import: missing `formName` in JSON')
                      }

                      const migrated = formService.migrateToV2(candidate)
                      const existingByName = formService
                        .getForms()
                        .some(frm => String(frm.formName).toLowerCase() === String(rawImportedFormName).toLowerCase())

                      let finalFormName = String(rawImportedFormName).trim()
                      if (existingByName) {
                        const makeUniqueName = (base: string, existing: string[]) => {
                          const existingSet = new Set(existing.map(n => n.toLowerCase()))
                          if (!existingSet.has(base.toLowerCase())) return base
                          let idx = 2
                          let candidateName = `${base} Copy`
                          while (existingSet.has(candidateName.toLowerCase())) {
                            candidateName = `${base} Copy ${idx}`
                            idx += 1
                          }
                          return candidateName
                        }
                        const existingNames = formService.getForms().map(frm => String(frm.formName))
                        const suggested = makeUniqueName(finalFormName, existingNames)
                        const shouldOverwrite = window.confirm(
                          `A form named "${finalFormName}" already exists.\n\n` +
                          `Press OK to overwrite it.\n` +
                          `Press Cancel to import as a copy.`,
                        )
                        if (!shouldOverwrite) {
                          const entered = await promptName('Import as a copy', suggested, 'Form name')
                          if (!entered || !entered.trim()) {
                            throw new Error('Import canceled: no copy name provided')
                          }
                          finalFormName = entered.trim()
                        }
                      }

                      const finalPayload = { ...migrated, formName: finalFormName }
                      await formService.saveForm(finalFormName, finalPayload)
                      await formService.loadForms(true)
                      const loadedForms = formService.getForms()
                      const loadedWorkflows = formService.getWorkflows()
                      const loadedCategories = formService.getCategories()
                      setForms(loadedForms)
                      setWorkflows(loadedWorkflows)
                      setCategories(loadedCategories)

                      alert(`Imported form "${finalFormName}"`)

                      // Immediately open the imported form so it's obvious import worked
                      // (and not mistaken as "nothing happened" when list order/filtering hides it).
                      const importedExists = loadedForms.some(
                        f => String(f.formName).toLowerCase() === String(finalFormName).toLowerCase(),
                      )
                      if (importedExists) {
                        await handleEditFormSelect(finalFormName)
                      } else {
                        // Fallback: show selection list if we couldn't locate it in memory.
                        setShowEditFormList(true)
                      }
                    } catch (err: any) {
                      alert(err?.message || 'Failed to import form')
                    } finally {
                      setImportFormBusy(false)
                    }
                  }}
                />
              </div>

              {/* Edit Form List */}
              {showEditFormList && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Select a Form to Edit</h3>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowEditFormList(false)}>
                      <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                      Back
                    </button>
                  </div>
                  {openingFormEditor && (
                    <div className="flex items-center gap-2 text-sm text-base-content/70 mb-4">
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                      Opening form…
                    </div>
                  )}
                  
                  {forms.length === 0 ? (
                    <div className="text-center py-8 text-base-content/70 bg-base-200 rounded-lg">
                      No forms found. Create a new form first.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {forms.map(form => (
                        <button
                          key={form.formName}
                          disabled={openingFormEditor}
                          onClick={() => handleEditFormSelect(form.formName)}
                          className="card bg-base-100 shadow hover:shadow-md transition-shadow cursor-pointer text-left"
                        >
                          <div className="card-body py-4">
                            <h4 className="card-title text-base">
                              <FontAwesomeIcon icon={faFileAlt} className="text-primary" />
                              {form.formName}
                            </h4>
                            <p className="text-sm text-base-content/70 line-clamp-2">{form.description}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className="badge badge-sm">{form.category}</span>
                              {form.workflow && (
                                <span className="badge badge-sm badge-outline">{form.workflow}</span>
                              )}
                              {form.workflowOptional && (
                                <span className="badge badge-sm badge-ghost">Optional</span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Form Container */}
        <div className={view.view === 'form' ? '' : 'hidden'}>
          {currentForm && (
            <>
              {activeWorkflowName ? (
                <div className="flex justify-center mb-8">
                  <div className="breadcrumbs text-base">
                    <ul>
                      <li>
                        <a className="flex items-center gap-1" onClick={() => setView({ view: 'selection', tab: 'workflows' })} style={{ cursor: 'pointer' }}>
                          <FontAwesomeIcon icon={faList} className="h-4 w-4" />
                          <span>Workflow</span>
                        </a>
                      </li>
                      <li>
                        <span className="inline-flex items-center gap-1">{activeWorkflowName}</span>
                      </li>
                      <li>
                        <span className="inline-flex items-center gap-1">{currentForm.formName}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
              <Breadcrumb
                root="Form"
                label={currentForm.formName}
                onRootClick={() => setView({ view: 'selection', tab: 'forms' })}
              />
              )}
              <InternalNavbar
                workflowName={activeWorkflowName}
                formName={currentForm.formName}
                formsInWorkflow={activeWorkflowName ? (workflowFormsToShow.length > 0 ? workflowFormsToShow : formService.getFormsInWorkflow(activeWorkflowName)) : undefined}
                currentFormName={currentForm.formName}
                onSwitchForm={handleSwitchForm}
                steps={currentForm.steps.map(step => step.title)}
                currentStep={currentStep}
                onStepClick={handleStepClick}
                tools={currentForm.tools}
                onToolClick={(tool) => console.log('Tool clicked:', tool)}
              />
              
              {/* Form Content */}
              <div className="max-w-2xl mx-auto">
                <div className="card bg-base-100 shadow-xl">
                  <div className="card-body">
                    <h2 className="card-title text-xl mb-4">{currentForm.steps[currentStep].title}</h2>
                    <p className="text-base-content/70 mb-6">{currentForm.steps[currentStep].description}</p>
                    {activeInstance && (
                      <div className="alert mb-6">
                        <FontAwesomeIcon icon={faUser} />
                        <span>
                          Stage {activeInstance.currentStage} of {activeInstance.totalStages}
                          {activeInstance.currentStage === 1
                            ? ' — you can fill the first-stage fields. Later fields are visible but locked.'
                            : ` — assigned to you${activeInstance.startedByLabel ? ` by ${activeInstance.startedByLabel}` : ''}. Earlier answers are read only.`}
                        </span>
                      </div>
                    )}
                    
                    {(() => {
                      const stepQuestions = currentForm.steps[currentStep].questions
                      const nearbyFields = stepQuestions.flatMap((q: any) =>
                        (q as any).type === 'group' && (q as any).mode !== 'radio'
                          ? ((q as any).questions || []).filter((f: any) => f.tool?.type === 'maps' && f.tool?.config?.nearbyPlaces)
                          : (q as any).tool?.type === 'maps' && (q as any).tool?.config?.nearbyPlaces ? [q] : []
                      )
                      const sourceFields = [...new Set(nearbyFields.map((f: any) => f.tool?.config?.nearbySourceField).filter(Boolean))]
                      const sourcesWithValue = sourceFields.filter((sid: string) => formAnswers[sid])
                      const canFillAll = nearbyFields.length >= 2 && sourcesWithValue.length > 0
                      return canFillAll ? (
                        <div className="mb-6 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline btn-primary btn-sm"
                            onClick={async () => {
                              setFillAllNearbyLoading(true)
                              try {
                                for (const sid of sourcesWithValue) {
                                  await applyNearbyPlacesPrefill(sid)
                                }
                              } finally {
                                setFillAllNearbyLoading(false)
                              }
                            }}
                            disabled={fillAllNearbyLoading}
                          >
                            {fillAllNearbyLoading ? (
                              <span className="loading loading-spinner loading-sm" />
                            ) : (
                              <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4" />
                            )}
                            <span>{fillAllNearbyLoading ? 'Finding locations…' : 'Fill all locations'}</span>
                          </button>
                          <span className="text-sm text-base-content/60">
                            Uses {sourcesWithValue.map(sid => {
                              const f = getAllFormFieldsFlat().find(x => x.id === sid)
                              return f?.label || sid
                            }).join(', ')} as starting address
                          </span>
                        </div>
                      ) : null
                    })()}
                    
                    <div className="space-y-6">
                      {currentForm.steps[currentStep].questions.map(question => {
                        // Support group containers in V2 schema
                        if ((question as any).type === 'group') {
                          const renderGroupQuestion = (gr: any): React.ReactNode => {
                            const layout = gr.layout || 'stack'
                            const gridClass = layout === 'two-columns'
                              ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
                              : layout === 'three-columns'
                                ? 'grid grid-cols-1 md:grid-cols-3 gap-4'
                                : 'grid grid-cols-1 gap-4'

                            return (
                              <div key={gr.id} className="w-full">
                                {gr.showHeading && (
                                  <div className="mb-2">
                                    <div className="text-lg font-semibold">
                                      {gr.name}
                                      {gr.mode === 'radio' && isRequiredForCurrentStage(gr, activeInstance?.currentStage ?? null) && <span className="text-error ml-1">*</span>}
                                    </div>
                                    {gr.showDescription && gr.description && (
                                      <div className="text-sm text-base-content/60">{gr.description}</div>
                                    )}
                                  </div>
                                )}
                                {!gr.showHeading && gr.showDescription && gr.description && (
                                  <div className="text-sm text-base-content/60 mb-2">{gr.description}</div>
                                )}

                                {gr.mode === 'radio' ? (
                                  <>
                                    <div className={gridClass}>
                                      {gr.questions
                                        .filter((q: any) => q?.type !== 'group')
                                        .map((q: any) => (
                                          <label
                                            key={q.id}
                                            className="label cursor-pointer justify-start gap-3"
                                          >
                                            <input
                                              type="radio"
                                              className="radio radio-primary"
                                              checked={formAnswers[gr.id] === q.label}
                                              disabled={isFieldInputLocked(gr, activeInstance?.currentStage ?? null)}
                                              onChange={() => handleAnswerChange(gr.id, q.label)}
                                            />
                                            <span className="label-text">{q.label}</span>
                                          </label>
                                        ))}
                                    </div>
                                    {formErrors[gr.id] && <div className="text-error text-xs mt-1">{formErrors[gr.id]}</div>}
                                  </>
                                ) : (
                                  <div className={gridClass}>
                                    {gr.questions.map((q: any) => {
                                      if (q?.type === 'group') return renderGroupQuestion(q)

                                      const Block = (FORM_BLOCKS as any)[q.type] || FORM_BLOCKS.text
                                      const jtAuto = q.tool?.type === 'jobtread' && q.tool?.config?.autocomplete

                                      // Prefill confirm button - for fields with requirePrefillConfirmation that are prefilled but not confirmed
                                      const fieldLocked = isFieldInputLocked(q, activeInstance?.currentStage ?? null)
                                      const needsPrefillConfirm =
                                        !fieldLocked &&
                                        q.tool?.config?.requirePrefillConfirmation &&
                                        prefilledFieldIds.has(q.id) &&
                                        !confirmedPrefillIds.has(q.id)
                                      const prefillConfirmBtn = needsPrefillConfirm ? (
                                        <button
                                          type="button"
                                          className="btn btn-square btn-warning join-item rounded-r-full"
                                          onClick={() => setConfirmedPrefillIds(prev => new Set([...prev, q.id]))}
                                          title="Confirm prefilled value"
                                          aria-label="Tap to confirm this prefilled value"
                                        >
                                          <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                                        </button>
                                      ) : null

                                      // Map button (showMapButton)
                                      const mapBtn =
                                        !fieldLocked && q.tool?.type === 'maps' && q.tool?.config?.showMapButton ? (
                                          <button
                                            type="button"
                                            className={`btn btn-square btn-primary join-item ${
                                              !(q.tool?.config?.nearbyPlaces) && !prefillConfirmBtn
                                                ? 'rounded-r-full'
                                                : ''
                                            }`}
                                            onClick={() => openMapModalForField(q.id, q.tool)}
                                            title="Open map"
                                            aria-label="Open map to search for this location"
                                          >
                                            <FontAwesomeIcon icon={faMapLocationDot} className="h-4 w-4" />
                                          </button>
                                        ) : null

                                      // Nearby Places button
                                      const nearbyBtn =
                                        !fieldLocked && q.tool?.type === 'maps' && q.tool?.config?.nearbyPlaces ? (
                                          <button
                                            type="button"
                                            className={`btn btn-square btn-outline btn-primary join-item ${
                                              !prefillConfirmBtn ? 'rounded-r-full' : ''
                                            }`}
                                            onClick={() => handleFindNearest(q.id)}
                                            title={`Find nearest ${q.tool?.config?.nearbyPlaceType || 'place'}`}
                                            aria-label={`Find nearest ${q.tool?.config?.nearbyPlaceType || 'place'}`}
                                            disabled={isFieldInputLocked(q, activeInstance?.currentStage ?? null) || nearbyLoading[q.id]}
                                          >
                                            {nearbyLoading[q.id] ? (
                                              <span className="loading loading-spinner loading-xs" />
                                            ) : (
                                              <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4" />
                                            )}
                                          </button>
                                        ) : null

                                      return (
                                        <div key={q.id}>
                                          <Block
                                            label={q.label}
                                            description={q.description}
                                            required={isRequiredForCurrentStage(q, activeInstance?.currentStage ?? null)}
                                            options={q.options}
                                            value={formAnswers[q.id]}
                                            error={formErrors[q.id]}
                                            disabled={isFieldInputLocked(q, activeInstance?.currentStage ?? null)}
                                            onChange={async (value: any) => {
                                              handleAnswerChange(q.id, value)
                                              if (jtAuto) {
                                                const val = String(value || '')
                                                if (!val) {
                                                  setJtSuggest(prev => ({ ...prev, [q.id]: [] }))
                                                  return
                                                }
                                                // Debounce per field
                                                const prevTimer = jtFieldTimersRef.current.get(q.id)
                                                if (prevTimer) window.clearTimeout(prevTimer)
                                                const tid = window.setTimeout(async () => {
                                                  setJtLoading(prev => ({ ...prev, [q.id]: true }))
                                                  const list = await fetchJobtreadSuggestions(val)
                                                  setJtSuggest(prev => ({ ...prev, [q.id]: list }))
                                                  setJtLoading(prev => ({ ...prev, [q.id]: false }))
                                                }, 300)
                                                jtFieldTimersRef.current.set(q.id, tid as unknown as number)
                                              }
                                            }}
                                            suggestions={
                                              jtAuto
                                                ? (jtSuggest[q.id] || [])
                                                    .map((it: any) => ({ label: String(it?.name || ''), data: it }))
                                                    .filter((s: any) => s.label)
                                                : undefined
                                            }
                                            loading={jtAuto ? !!jtLoading[q.id] : false}
                                            onSelectSuggestion={(label: string, data?: any) => {
                                              handleAnswerChange(q.id, label)
                                              if (data) jtSelectedItemByFieldIdRef.current.set(q.id, data)
                                              const job = data || jtJobByNameRef.current.get(label)
                                              if (job) applyJobtreadPrefill(q.id, job)
                                              setJtSuggest(prev => ({ ...prev, [q.id]: [] }))
                                            }}
                                          >
                                            {mapBtn}
                                            {nearbyBtn}
                                            {prefillConfirmBtn}
                                          </Block>
                                          {needsPrefillConfirm && (
                                            <p className="text-xs text-base-content/60 mt-1">Tap to confirm this prefilled value.</p>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            )
                          }

                          return renderGroupQuestion(question as any)
                        }
                        // Default single field rendering
                        const q: any = question
                        const Block = (FORM_BLOCKS as any)[q.type] || FORM_BLOCKS.text
                        const tool = (q as any).tool
                        // Prefill confirm button - for fields with requirePrefillConfirmation that are prefilled but not confirmed
                        const fieldLocked = isFieldInputLocked(q, activeInstance?.currentStage ?? null)
                        const needsPrefillConfirmSingle = !fieldLocked && tool?.config?.requirePrefillConfirmation && prefilledFieldIds.has(q.id) && !confirmedPrefillIds.has(q.id)
                        const prefillConfirmButton = needsPrefillConfirmSingle ? (
                          <button
                            type="button"
                            className="btn btn-square btn-warning join-item rounded-r-full"
                            onClick={() => setConfirmedPrefillIds(prev => new Set([...prev, q.id]))}
                            title="Confirm prefilled value"
                            aria-label="Tap to confirm this prefilled value"
                          >
                            <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                          </button>
                        ) : null
                        // Map tool-specific UI: add map button for maps tool (DaisyUI join pattern)
                        const hasNearby = !fieldLocked && tool?.type === 'maps' && tool.config?.nearbyPlaces
                        const mapButton = !fieldLocked && tool?.type === 'maps' && tool.config?.showMapButton ? (
                          <button
                            type="button"
                            className={`btn btn-square btn-primary join-item ${!hasNearby && !prefillConfirmButton ? 'rounded-r-full' : ''}`}
                            onClick={() => openMapModalForField(q.id, tool)}
                            title="Open map"
                            aria-label="Open map to search for this location"
                          >
                            <FontAwesomeIcon icon={faMapLocationDot} className="h-4 w-4" />
                          </button>
                        ) : null
                        // Nearby Places button (always last when present, so gets rounded-r-full)
                        const nearbyButton = hasNearby ? (
                          <button
                            type="button"
                            className={`btn btn-square btn-outline btn-primary join-item ${!prefillConfirmButton ? 'rounded-r-full' : ''}`}
                            onClick={() => handleFindNearest(q.id)}
                            title={`Find nearest ${tool?.config?.nearbyPlaceType || 'place'}`}
                            aria-label={`Find nearest ${tool?.config?.nearbyPlaceType || 'place'}`}
                            disabled={nearbyLoading[q.id]}
                          >
                            {nearbyLoading[q.id] ? (
                              <span className="loading loading-spinner loading-xs" />
                            ) : (
                              <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4" />
                            )}
                          </button>
                        ) : null
                        const jtAuto = tool?.type === 'jobtread' && tool?.config?.autocomplete
                        return (
                          <div key={q.id}>
                          <Block
                            key={q.id}
                            label={q.label}
                            description={q.description}
                            required={isRequiredForCurrentStage(q, activeInstance?.currentStage ?? null)}
                            options={q.options}
                            value={formAnswers[q.id]}
                            error={formErrors[q.id]}
                            disabled={isFieldInputLocked(q, activeInstance?.currentStage ?? null)}
                            onChange={async (value: any) => {
                              handleAnswerChange(q.id, value)
                              if (jtAuto) {
                                // Typing over a previously-selected job invalidates that selection.
                                // onSelectSuggestion will re-set it if the user picks a new one.
                                jtSelectedItemByFieldIdRef.current.delete(q.id)
                                const val = String(value || '')
                                if (!val) { setJtSuggest(prev => ({ ...prev, [q.id]: [] })); return }
                                const prevTimer = jtFieldTimersRef.current.get(q.id)
                                if (prevTimer) window.clearTimeout(prevTimer)
                                const tid = window.setTimeout(async () => {
                                  setJtLoading(prev => ({ ...prev, [q.id]: true }))
                                  const list = await fetchJobtreadSuggestions(val)
                                  setJtSuggest(prev => ({ ...prev, [q.id]: list }))
                                  setJtLoading(prev => ({ ...prev, [q.id]: false }))
                                }, 300)
                                jtFieldTimersRef.current.set(q.id, tid as unknown as number)
                              }
                            }}
                            suggestions={jtAuto ? ((jtSuggest[q.id] || []).map(it => ({ label: String(it?.name || ''), data: it })).filter(s => s.label)) : undefined}
                            loading={jtAuto ? !!jtLoading[q.id] : false}
                            onSelectSuggestion={(label: string, data?: any) => {
                              handleAnswerChange(q.id, label)
                              if (data) jtSelectedItemByFieldIdRef.current.set(q.id, data)
                              const job = data || jtJobByNameRef.current.get(label)
                              if (job) applyJobtreadPrefill(q.id, job)
                              setJtSuggest(prev => ({ ...prev, [q.id]: [] }))
                            }}
                          >
                            {mapButton}
                            {nearbyButton}
                            {prefillConfirmButton}
                          </Block>
                          {needsPrefillConfirmSingle && <p className="text-xs text-base-content/60 mt-1">Tap to confirm this prefilled value.</p>}
                        </div>
                        )
                      })}
                    </div>

                    <div className="flex justify-between mt-8">
                      <button
                        className="btn btn-outline"
                        onClick={handlePrevStep}
                        disabled={currentStep === 0}
                      >
                        Previous
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleNextStep}
                      >
                        {currentStep === currentForm.steps.length - 1
                          ? (activeInstance && activeInstance.currentStage < activeInstance.totalStages ? 'Submit stage' : 'Submit')
                          : 'Next'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Form Management Container */}
        <div className={view.view === 'formManagement' ? '' : 'hidden'}>
          <div className="w-full mx-auto">
            <div className="mb-6 flex justify-center">
              <div className="breadcrumbs text-base">
                <ul>
                  <li>
                    <a className="flex items-center gap-1" onClick={() => setView({ view: 'selection', tab: 'management' })} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faCog} className="h-4 w-4" />
                      <span>Form Management</span>
                    </a>
                  </li>
                  <li><span className="capitalize">{managementMode}</span></li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center mb-6">
              <ul className="steps w-full md:w-96">
                <li
                  className={`step ${managementStep >= 0 ? 'step-primary' : ''} cursor-pointer`}
                  onClick={() => handleManagementStepClick(0)}
                  title="Details"
                >
                  Details
                </li>
                <li
                  className={`step ${managementStep >= 1 ? 'step-primary' : ''} cursor-pointer`}
                  onClick={() => handleManagementStepClick(1)}
                  title="Edit"
                >
                  Edit
                </li>
              </ul>
            </div>

            {/* Step 1: Details */}
            <div className={managementStep === 0 ? '' : 'hidden'}>
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label"><span className="label-text font-medium">Form Name</span></label>
                      <input className="input input-bordered w-full" value={managementDetails.formName} onChange={e => setManagementDetails(d => ({ ...d, formName: e.target.value }))} placeholder="e.g., Direct Deposit Authorization" />
                    </div>
                    <div>
                      <label className="label"><span className="label-text font-medium">Workflow (optional)</span></label>
                      <input className="input input-bordered w-full" value={managementDetails.workflow} onChange={e => setManagementDetails(d => ({ ...d, workflow: e.target.value }))} placeholder="e.g., HR Onboarding" />
                    </div>
                    <div>
                      <label className="label"><span className="label-text font-medium">Workflow Order</span></label>
                      <input type="number" className="input input-bordered w-full" value={managementDetails.workflowOrder} min={1} onChange={e => setManagementDetails(d => ({ ...d, workflowOrder: parseInt(e.target.value || '1', 10) }))} />
                    </div>
                    {/* Optional checkbox - only show when workflow is specified */}
                    {managementDetails.workflow && (
                      <div className="flex items-center">
                        <label className="label cursor-pointer gap-3 justify-start">
                          <input 
                            type="checkbox" 
                            className="checkbox checkbox-primary" 
                            checked={managementDetails.workflowOptional} 
                            onChange={e => setManagementDetails(d => ({ ...d, workflowOptional: e.target.checked }))} 
                          />
                          <div>
                            <span className="label-text font-medium">Optional Form</span>
                            <p className="text-xs text-base-content/60">When checked, users can opt out of this form when starting the workflow</p>
                          </div>
                        </label>
                      </div>
                    )}
                    <div>
                      <label className="label"><span className="label-text font-medium">Category</span></label>
                      <input className="input input-bordered w-full" value={managementDetails.category} onChange={e => setManagementDetails(d => ({ ...d, category: e.target.value }))} placeholder="e.g., HR, Finance, Safety" />
                    </div>
                    <div>
                      <label className="label"><span className="label-text font-medium">Estimated Time</span></label>
                      <input className="input input-bordered w-full" value={managementDetails.estimatedTime} onChange={e => setManagementDetails(d => ({ ...d, estimatedTime: e.target.value }))} placeholder="e.g., 3-5 minutes" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label"><span className="label-text font-medium">Description</span></label>
                      <textarea className="textarea textarea-bordered w-full" rows={3} value={managementDetails.description} onChange={e => setManagementDetails(d => ({ ...d, description: e.target.value }))} placeholder="Brief description" />
                    </div>
                    <FormAccessAndNotifications
                      accessLevels={managementDetails.accessLevels}
                      levelNames={levelNames}
                      levelsLoading={levelsLoading}
                      legacyAccess={legacyFormAccess}
                      notifications={managementDetails.notifications}
                      jtFolder={managementDetails.upload?.jtFolder || ''}
                      onAccessLevels={(accessLevels) => setManagementDetails((d) => ({ ...d, accessLevels }))}
                      onNotifications={(notifications) => setManagementDetails((d) => ({ ...d, notifications }))}
                    />
                    <div className="md:col-span-2">
                      <label className="label"><span className="label-text font-medium">Fillers</span></label>
                      <div className="p-3 rounded-lg border border-base-300 space-y-3">
                        <p className="text-xs text-base-content/60">
                          Stage 1 is always anyone who can use this form. Add later stages to assign specific employees. In-progress forms keep the filler list from when they were started.
                        </p>
                        {normalizeFillers(managementDetails.fillers).map((filler) => (
                          <div key={filler.stage} className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <span className="badge badge-sm">Stage {filler.stage}</span>
                            {filler.kind === 'anyone' ? (
                              <span className="text-sm">Anyone</span>
                            ) : (
                              <div className="flex-1">
                                <EmployeeTypeahead
                                  selected={filler.employeeId ? { employeeId: filler.employeeId, label: fillerLabel(filler) } : null}
                                  onSelect={(emp) => {
                                    const label = String(emp.preferredName || emp.name || emp.employeeId)
                                    setManagementDetails(d => ({
                                      ...d,
                                      fillers: normalizeFillers(d.fillers).map((f) =>
                                        f.stage === filler.stage
                                          ? { stage: f.stage, kind: 'employee' as const, employeeId: emp.employeeId, label }
                                          : f
                                      ),
                                    }))
                                  }}
                                  onClear={() => setManagementDetails(d => ({
                                    ...d,
                                    fillers: normalizeFillers(d.fillers).map((f) =>
                                      f.stage === filler.stage
                                        ? { stage: f.stage, kind: 'employee' as const, employeeId: '', label: '' }
                                        : f
                                    ),
                                  }))}
                                />
                              </div>
                            )}
                            {filler.stage > 1 && filler.stage === normalizeFillers(managementDetails.fillers).length && (
                              <button
                                type="button"
                                className="btn btn-ghost btn-xs"
                                onClick={() => {
                                  const nextFillers = normalizeFillers(managementDetails.fillers).slice(0, -1)
                                  const maxStage = nextFillers.length
                                  setManagementDetails(d => ({ ...d, fillers: nextFillers }))
                                  setFieldConfigs(cfg => {
                                    const next = { ...cfg }
                                    for (const [k, v] of Object.entries(next)) {
                                      const stage = Number(v?.fillerStage) >= 1 ? Number(v.fillerStage) : 1
                                      if (stage > maxStage) next[k] = { ...v, fillerStage: 1 }
                                    }
                                    return next
                                  })
                                  setGroupConfigs(cfg => {
                                    const next = { ...cfg }
                                    for (const [k, v] of Object.entries(next)) {
                                      const stage = Number(v?.fillerStage) >= 1 ? Number(v.fillerStage) : 1
                                      if (stage > maxStage) next[k] = { ...v, fillerStage: 1 }
                                    }
                                    return next
                                  })
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => setManagementDetails(d => {
                            const current = normalizeFillers(d.fillers)
                            return {
                              ...d,
                              fillers: [...current, { stage: current.length + 1, kind: 'employee', employeeId: '', label: '' }],
                            }
                          })}
                        >
                          <FontAwesomeIcon icon={faPlus} className="mr-1" />
                          Add filler
                        </button>
                      </div>
                    </div>
                    {/* Upload behavior (form-level) */}
                    <div className="md:col-span-2">
                      <div className="mt-4 p-3 rounded-lg border border-base-300">
                        <div className="font-semibold mb-2">On Submit: Upload</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" className="checkbox checkbox-sm" checked={!!managementDetails.upload?.drive} onChange={e => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), drive: e.target.checked } }))} />
                            <span className="text-sm">Google Drive</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" className="checkbox checkbox-sm" checked={!!managementDetails.upload?.jobtread} onChange={e => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), jobtread: e.target.checked } }))} />
                            <span className="text-sm">JobTread</span>
                          </label>
                          {managementDetails.upload?.drive && (
                            <div className="md:col-span-2">
                              <label className="label"><span className="label-text">Drive Folder</span></label>
                              <input className="input input-bordered w-full" placeholder="e.g., Forms/Submissions" value={managementDetails.upload?.driveFolder || ''} onChange={e => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), driveFolder: e.target.value } }))} />
                            </div>
                          )}
                          {managementDetails.upload?.jobtread && (
                            <div className="md:col-span-2 space-y-3">
                              <div>
                                <label className="label"><span className="label-text">Folder inside job (e.g., JSA)</span></label>
                                <input className="input input-bordered w-full" placeholder="e.g., JSA" value={managementDetails.upload?.jtFolder || ''} onChange={e => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), jtFolder: e.target.value } }))} />
                                <div className="text-xs text-base-content/60 mt-1">The completed PDF will be uploaded to this folder within the selected job. The folder will be created if it does not exist.</div>
                              </div>
                              <div>
                                <label className="label"><span className="label-text">Job field</span></label>
                                <select className="select select-bordered w-full" value={managementDetails.upload?.jobFieldId || ''} onChange={e => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), jobFieldId: e.target.value } }))}>
                                  <option value="">Select field...</option>
                                  {Object.entries(fieldConfigs)
                                    .filter(([, cfg]) => cfg?.tool?.type === 'jobtread' && cfg?.tool?.config?.autocomplete)
                                    .map(([fid, cfg]) => (
                                      <option key={fid} value={fid}>{cfg?.label || fid}</option>
                                    ))}
                                </select>
                                {managementDetails.upload?.jobtread && Object.entries(fieldConfigs).filter(([, cfg]) => cfg?.tool?.type === 'jobtread' && cfg?.tool?.config?.autocomplete).length === 0 && (
                                  <div className="text-xs text-warning mt-1">Add a JobTread autocomplete field in the Edit step to enable upload.</div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* File name builder - drag to reorder */}
                          <div className="md:col-span-2 mt-4 pt-3 border-t border-base-300">
                            <div className="font-medium mb-2">File name</div>
                            <div className="text-xs text-base-content/60 mb-2">Order the parts that make up the downloaded/uploaded PDF filename. Leave empty to use the default (JobTread job, user, form name, date).</div>
                            <div className="min-h-[2.5rem] rounded-lg border border-base-300 bg-base-200/50 p-2 space-y-1.5">
                              {(managementDetails.upload?.fileNameParts || []).map((p: { type: string; fieldId?: string }, idx: number) => {
                                const label = p.type === 'formName' ? 'Form name' : p.type === 'user' ? 'User name' : p.type === 'jobNumber' ? 'Job number' : p.type === 'date' ? 'Date/time' : p.type === 'field' && p.fieldId ? (fieldConfigs[p.fieldId]?.label || p.fieldId) : p.type
                                return (
                                  <div
                                    key={`${p.type}-${p.fieldId || ''}-${idx}`}
                                    draggable
                                    onDragStart={e => { e.dataTransfer.setData('text/plain', String(idx)); e.dataTransfer.effectAllowed = 'move' }}
                                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
                                    onDrop={e => {
                                      e.preventDefault()
                                      const fromIdx = parseInt(e.dataTransfer.getData('text/plain') ?? '', 10)
                                      if (Number.isNaN(fromIdx) || fromIdx === idx) return
                                      const arr = [...(managementDetails.upload?.fileNameParts || [])]
                                      const [removed] = arr.splice(fromIdx, 1)
                                      arr.splice(idx, 0, removed)
                                      setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), fileNameParts: arr } }))
                                    }}
                                    className="flex items-center gap-2 py-1.5 px-2 rounded bg-base-100 border border-base-300 cursor-grab active:cursor-grabbing"
                                  >
                                    <FontAwesomeIcon icon={faGripVertical} className="text-base-content/50 text-sm" />
                                    <span className="badge badge-sm badge-ghost">{label}</span>
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-xs btn-circle ml-auto"
                                      onClick={() => {
                                        const arr = (managementDetails.upload?.fileNameParts || []).filter((_: unknown, i: number) => i !== idx)
                                        setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), fileNameParts: arr } }))
                                      }}
                                      aria-label="Remove"
                                    >
                                      <FontAwesomeIcon icon={faTimes} className="text-sm" />
                                    </button>
                                  </div>
                                )
                              })}
                              {(managementDetails.upload?.fileNameParts || []).length === 0 && (
                                <div className="text-sm text-base-content/50 py-2 text-center">No parts configured — default naming used</div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-sm">Add part:</span>
                              <div className="dropdown dropdown-end">
                                <label tabIndex={0} className="btn btn-sm btn-outline">
                                  <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add
                                </label>
                                <div tabIndex={0} className="dropdown-content p-0 shadow-lg bg-base-100 rounded-box border border-base-300 z-50 w-64 max-h-60 overflow-hidden flex flex-col">
                                  <div className="overflow-y-auto overflow-x-hidden p-2 flex flex-col gap-0.5 min-h-0">
                                    <button type="button" className="btn btn-ghost btn-sm justify-start text-left normal-case w-full min-w-0" onClick={() => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), fileNameParts: [...(d.upload?.fileNameParts || []), { type: 'formName' }] } }))}>Form name</button>
                                    <button type="button" className="btn btn-ghost btn-sm justify-start text-left normal-case w-full min-w-0" onClick={() => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), fileNameParts: [...(d.upload?.fileNameParts || []), { type: 'user' }] } }))}>User name</button>
                                    {Object.entries(fieldConfigs).map(([fid, cfg]) => (
                                      <button key={fid} type="button" className="btn btn-ghost btn-sm justify-start text-left normal-case w-full min-w-0 break-words whitespace-normal" onClick={() => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), fileNameParts: [...(d.upload?.fileNameParts || []), { type: 'field', fieldId: fid }] } }))}>Field: {cfg?.label || fid}</button>
                                    ))}
                                    {managementDetails.upload?.jobtread && (
                                      <button type="button" className="btn btn-ghost btn-sm justify-start text-left normal-case w-full min-w-0" onClick={() => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), fileNameParts: [...(d.upload?.fileNameParts || []), { type: 'jobNumber' }] } }))}>Job number</button>
                                    )}
                                    <button type="button" className="btn btn-ghost btn-sm justify-start text-left normal-case w-full min-w-0" onClick={() => setManagementDetails(d => ({ ...d, upload: { ...(d.upload||{}), fileNameParts: [...(d.upload?.fileNameParts || []), { type: 'date' }] } }))}>Date/time</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Caching (form-level) */}
                    <div className="md:col-span-2">
                      <div className="mt-4 p-3 rounded-lg border border-base-300">
                        <div className="font-semibold mb-2">Caching</div>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" className="checkbox checkbox-sm" checked={!!managementDetails.cache?.enabled} onChange={e => setManagementDetails(d => ({ ...d, cache: { ...(d.cache || { enabled: false, expiryHours: 24 }), enabled: e.target.checked } }))} />
                          <span className="text-sm">Cache answers on user's device for easier filling next time</span>
                        </label>
                        {managementDetails.cache?.enabled && (
                          <div className="mt-3">
                            <label className="label"><span className="label-text">Cache valid for (hours)</span></label>
                            <input type="number" className="input input-bordered w-full max-w-xs" min={1} max={720} value={managementDetails.cache?.expiryHours ?? 24} onChange={e => setManagementDetails(d => ({ ...d, cache: { ...(d.cache || { enabled: true, expiryHours: 24 }), expiryHours: Math.max(1, Math.min(720, parseInt(e.target.value, 10) || 24)) } }))} />
                            <div className="text-xs text-base-content/60 mt-1">Users must refill from scratch after this many hours. Max 720 (30 days).</div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Optional Integrations */}
                    <div className="md:col-span-2">
                      <div className="mt-4 p-3 rounded-lg border border-base-300">
                        <div className="font-semibold mb-2">Optional Integrations</div>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" className="checkbox checkbox-sm" checked={!!managementDetails.hospitalMaps?.enabled} onChange={e => setManagementDetails(d => ({ ...d, hospitalMaps: { ...(d.hospitalMaps || { enabled: false, siteAddressField: '', hospitalAddressField: '' }), enabled: e.target.checked } }))} />
                          <span className="text-sm">Append hospital route maps to PDF</span>
                        </label>
                        <div className="text-xs text-base-content/60 mt-1 mb-2">Adds two pages with driving directions: Site to Hospital and Hospital to Site. Requires a location field.</div>
                        {managementDetails.hospitalMaps?.enabled && (
                          <div className="mt-3 space-y-3">
                            <div>
                              <label className="label"><span className="label-text">Site/Location field</span></label>
                              <select className="select select-bordered w-full" value={managementDetails.hospitalMaps?.siteAddressField || ''} onChange={e => setManagementDetails(d => ({ ...d, hospitalMaps: { ...(d.hospitalMaps || { enabled: true, siteAddressField: '', hospitalAddressField: '' }), siteAddressField: e.target.value } }))}>
                                <option value="">Select field...</option>
                                {Object.entries(fieldConfigs)
                                  .filter(([, cfg]) => (cfg?.type === 'text' || cfg?.type === 'textarea') && cfg?.label)
                                  .sort(([a, ac], [b, bc]) => {
                                    const aHasMaps = ac?.tool?.type === 'maps' ? 1 : 0
                                    const bHasMaps = bc?.tool?.type === 'maps' ? 1 : 0
                                    if (bHasMaps !== aHasMaps) return bHasMaps - aHasMaps
                                    return (ac?.label || a).localeCompare(bc?.label || b)
                                  })
                                  .map(([fid, cfg]) => (
                                    <option key={fid} value={fid}>{cfg?.label || fid}</option>
                                  ))}
                              </select>
                              {Object.keys(fieldConfigs).length === 0 && (
                                <div className="text-xs text-warning mt-1">Add form fields in the Edit step, then return here to configure.</div>
                              )}
                              {Object.keys(fieldConfigs).length > 0 && (
                                <div className="text-xs text-base-content/60 mt-1">The field containing the job site address.</div>
                              )}
                            </div>
                            <div>
                              <label className="label"><span className="label-text">Hospital address field (optional)</span></label>
                              <select className="select select-bordered w-full" value={managementDetails.hospitalMaps?.hospitalAddressField || ''} onChange={e => setManagementDetails(d => { const prev = d.hospitalMaps; return { ...d, hospitalMaps: { ...(prev || { enabled: true, siteAddressField: '', hospitalAddressField: '' }), hospitalAddressField: e.target.value } }; })}>
                                <option value="">Select field... (or leave blank)</option>
                                {Object.entries(fieldConfigs)
                                  .filter(([, cfg]) => (cfg?.type === 'text' || cfg?.type === 'textarea') && cfg?.label)
                                  .sort(([a, ac], [b, bc]) => (ac?.label || a).localeCompare(bc?.label || b))
                                  .map(([fid, cfg]) => (
                                    <option key={fid} value={fid}>{cfg?.label || fid}</option>
                                  ))}
                              </select>
                              <div className="text-xs text-base-content/60 mt-1">If the form has a hospital address field, select it. Otherwise the nearest hospital will be found when the PDF is generated.</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                      <div className="md:col-span-2">
                      <label className="label"><span className="label-text font-medium">Upload Fillable PDF</span></label>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="file-input file-input-bordered w-full"
                          onClick={e => {
                            // Allow selecting the exact same file again after external edits.
                            ;(e.currentTarget as HTMLInputElement).value = ''
                          }}
                          onChange={async e => {
                            const f = e.target.files && e.target.files[0] ? e.target.files[0] : null
                            if (f) {
                              await onPdfSelected(f)
                            } else {
                              setManagementDetails(d => ({ ...d, pdfFile: null, pdfBase64: '', pdfDataUrl: '' }))
                              setParsedPdfFields([])
                              setSelectedPdfField(null)
                            }
                          }}
                        />
                      <div className="text-xs text-base-content/60 mt-1">This will be used as the base for the form.</div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-between">
                    <div className="flex gap-2">
                      <button className="btn" onClick={handleBack}><FontAwesomeIcon icon={faArrowLeft} className="mr-2" />Back</button>
                      {managementMode === 'edit' && managementDetails.formName && (
                        <button
                          className="btn btn-outline"
                          onClick={() => {
                            try {
                              const payload = buildFormJson(false)
                              const safeName = String(payload?.formName || managementDetails.formName || 'form').trim()
                              const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
                              const url = URL.createObjectURL(blob)
                              const a = document.createElement('a')
                              a.href = url
                              a.download = `${safeName}.json`
                              document.body.appendChild(a)
                              a.click()
                              a.remove()
                              setTimeout(() => URL.revokeObjectURL(url), 1500)
                            } catch (e: any) {
                              alert(e?.message || 'Failed to export form')
                            }
                          }}
                          title="Download this form definition as JSON"
                        >
                          <FontAwesomeIcon icon={faCloudDownloadAlt} className="mr-2" />
                          Export Form
                        </button>
                      )}
                      {managementMode === 'edit' && managementDetails.formName && (
                        <button
                          className="btn btn-error btn-outline"
                          onClick={async () => {
                            if (!window.confirm(`Delete form "${managementDetails.formName}"? This cannot be undone.`)) return
                            try {
                              await formService.deleteForm(managementDetails.formName)
                              const loadedForms = formService.getForms()
                              setForms(loadedForms)
                              const loadedWorkflows = formService.getWorkflows()
                              setWorkflows(loadedWorkflows)
                              setView({ view: 'selection', tab: 'forms' })
                            } catch (e: any) {
                              alert(e.message || 'Failed to delete form')
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} className="mr-2" />
                          Delete Form
                        </button>
                      )}
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        if (managementDetails.pdfFile) simulateParsePdfFields(); else setManagementStep(1)
                      }}
                      disabled={!managementDetails.formName || (!managementDetails.pdfFile && !managementDetails.pdfBase64 && parsedPdfFields.length === 0)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Edit */}
            <div className={managementStep === 1 ? '' : 'hidden'}>
              <div className="grid grid-cols-12 gap-4 min-h-[60vh] md:min-h-[70vh]">
                {/* Left Column (1/4) */}
                <div className="col-span-12 md:col-span-3">
                  <div className="card bg-base-100 shadow-xl h-full">
                    <div className="card-body h-[60vh] md:h-[70vh] flex flex-col">
                      <div className="shrink-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="card-title text-base shrink-0">PDF Fields</h3>
                          <div className="dropdown dropdown-end shrink-0">
                            <label tabIndex={0} className="btn btn-ghost btn-xs btn-circle text-base-content/60 hover:text-base-content" title="Field markers legend">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </label>
                            <div tabIndex={0} className="dropdown-content z-[1] p-3 shadow-lg bg-base-100 rounded-box w-44 border border-base-300 mt-1">
                              <div className="text-xs font-semibold text-base-content/80 mb-2">Field markers</div>
                              <div className="space-y-2 text-xs">
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon icon={faAsterisk} className="text-error text-xs" />
                                  <span>Required</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon icon={faDatabase} className="text-success text-xs" />
                                  <span>Cache enabled</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon icon={faBan} className="text-base-content/50 text-xs" />
                                  <span>Excluded from cache</span>
                                </div>
                                <div className="flex items-center gap-2 mt-1 pt-1 border-t border-base-300">
                                  <FontAwesomeIcon icon={faFont} className="text-base-content/60 text-xs" />
                                  <span>Field type (text, checkbox, etc.)</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-1 justify-end flex-wrap">
                            <button
                              className="btn btn-xs btn-ghost"
                              onClick={handleAuditForm}
                              disabled={
                                auditingForm ||
                                reExtractingPdfFields ||
                                fixingFormJson ||
                                !managementDetails.pdfBase64 ||
                                (managementMode === 'edit' && !managementDetails.formName)
                              }
                              title="Audit and repair this form: re-extract PDF fields, upgrade duplicates, and normalize JSON"
                            >
                              {(auditingForm || reExtractingPdfFields || fixingFormJson)
                                ? <span className="loading loading-spinner loading-xs" />
                                : 'Audit Form'}
                            </button>
                            <button className="btn btn-xs btn-primary" onClick={createGroupFromSelection} disabled={!(selectedKeys.length > 1 && selectedKeys.every(k => k.startsWith('field:')))}>Group</button>
                            <button className="btn btn-xs btn-error" onClick={deleteSelected} disabled={!(selectedKeys.length >= 1 && (selectedKeys.every(k => k.startsWith('group:')) || selectedKeys.every(k => k.startsWith('page:'))))}>Delete</button>
                            <button className="btn btn-xs" onClick={groupSelectionIntoPage} disabled={selectedKeys.length === 0}>Add to Page</button>
                          </div>
                        </div>
                        <div className="divider my-2"></div>
                      </div>
                      <div ref={fieldListContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden pr-2">
                        <PdfFieldsTree
                          pages={pages}
                          fieldGroups={fieldGroups}
                          setPages={setPages}
                          setFieldGroups={setFieldGroups}
                          parsedPdfFields={parsedPdfFields}
                          fieldConfigs={fieldConfigs}
                          managementDetails={managementDetails}
                          selectedKeys={selectedKeys}
                          onItemClick={handleItemClick}
                          fieldListRefs={fieldListRefs}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Center Column (1/2) */}
                <div className="col-span-12 md:col-span-6">
                  <div className="card bg-base-100 shadow-xl h-full">
                    <div className="card-body">
                      <div className="flex items-center justify-between mb-4">
                        <div className="tabs">
                          <button type="button" className={`tab tab-bordered${centerTab === 'pdf' ? ' tab-active' : ''}`} onClick={() => setCenterTab('pdf')}>PDF Preview</button>
                          <button type="button" className={`tab tab-bordered${centerTab === 'builder' ? ' tab-active' : ''}`} onClick={() => setCenterTab('builder')}>Preview</button>
                        </div>
                        {centerTab === 'pdf' && pdfPages.length > 0 && (
                          <div className="flex items-center gap-2">
                            <button 
                              className="btn btn-xs btn-ghost" 
                              onClick={() => {
                                setPdfRenderedPages(new Set())
                                pdfCanvasRefs.current.clear()
                                setPdfScale(s => Math.max(0.5, s - 0.25))
                              }}
                              title="Zoom out"
                            >
                              −
                            </button>
                            <span className="text-xs w-12 text-center">{Math.round(pdfScale * 100)}%</span>
                            <button 
                              className="btn btn-xs btn-ghost" 
                              onClick={() => {
                                setPdfRenderedPages(new Set())
                                pdfCanvasRefs.current.clear()
                                setPdfScale(s => Math.min(2.0, s + 0.25))
                              }}
                              title="Zoom in"
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                      <div className={centerTab === 'pdf' ? '' : 'hidden'}>
                        {managementDetails.pdfDataUrl ? (
                          <div 
                            ref={pdfContainerRef}
                            className="w-full h-[50vh] md:h-[65vh] rounded-lg border border-base-300 overflow-auto bg-base-200 p-4"
                          >
                            {pdfPages.length > 0 ? (
                              <div className="flex flex-col items-center gap-4">
                                {pdfPages.map((pageInfo) => (
                                  <div 
                                    key={`${pageInfo.pageNum}-${pdfScale}`} 
                                    className="relative shadow-lg bg-white"
                                    style={{ width: pageInfo.width * pdfScale, height: pageInfo.height * pdfScale }}
                                  >
                                    <canvas
                                      ref={(el) => {
                                        if (el) pdfCanvasRefs.current.set(pageInfo.pageNum, el)
                                      }}
                                      className="block"
                                      style={{ width: pageInfo.width * pdfScale, height: pageInfo.height * pdfScale }}
                                    />
                                    {/* Loading overlay for unrendered pages */}
                                    {!pdfRenderedPages.has(pageInfo.pageNum) && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-base-200/50">
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
                                      </div>
                                    )}
                                    {/* Field highlight overlays */}
                                    {parsedPdfFields.flatMap(field => {
                                      const widgets = (field.widgets && field.widgets.length > 0)
                                        ? field.widgets
                                        : (field.rect
                                            ? [{ widgetId: `${field.name}::legacy`, rect: field.rect, page: field.page }]
                                            : [])
                                      return widgets
                                        .filter(w => {
                                          const widgetPage = typeof w.page === 'number' ? w.page : 0
                                          return !!w.rect && widgetPage === pageInfo.pageNum - 1
                                        })
                                        .map((w, widx) => ({ field, widget: w, widgetIndex: widx }))
                                    }).map(({ field, widget, widgetIndex }) => {
                                      const isSelected = selectedPdfField?.name === field.name
                                      const rect = widget.rect!
                                      // PDF coordinates are bottom-left origin, need to flip Y
                                      const top = (pageInfo.height - (rect.y + rect.height)) * pdfScale
                                      const left = rect.x * pdfScale
                                      const width = rect.width * pdfScale
                                      const height = rect.height * pdfScale

                                      return (
                                        <div
                                          key={`${field.name}:${widget.widgetId}:${widgetIndex}`}
                                          ref={(el) => {
                                            if (
                                              el &&
                                              typeof widget.page === 'number' &&
                                              widget.page === (typeof field.page === 'number' ? field.page : widget.page)
                                            ) {
                                              pdfFieldRefs.current.set(field.name, el)
                                            }
                                          }}
                                          className={`absolute border-2 transition-all cursor-pointer ${
                                            isSelected
                                              ? 'border-primary bg-primary/30 ring-4 ring-primary/50 z-10 animate-pulse'
                                              : 'border-secondary/50 bg-secondary/10 hover:bg-secondary/20'
                                          }`}
                                          style={{
                                            top: `${top}px`,
                                            left: `${left}px`,
                                            width: `${width}px`,
                                            height: `${height}px`,
                                            minWidth: '20px',
                                            minHeight: '10px',
                                          }}
                                          onClick={() => setSingleSelection(`field:${field.name}`)}
                                          title={field.name}
                                        >
                                          {isSelected && widgetIndex === 0 && (
                                            <div className="absolute -top-6 left-0 bg-primary text-primary-content text-xs px-2 py-0.5 rounded whitespace-nowrap shadow z-20">
                                              {field.name}
                                            </div>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-base-content/60">
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl mb-2" />
                                <span>Loading PDF...</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-full h-[50vh] md:h-[65vh] bg-base-200 rounded-lg flex items-center justify-center text-base-content/60">
                            Upload a PDF to see preview with field highlighting
                          </div>
                        )}
                      </div>
                      <div className={centerTab === 'builder' ? '' : 'hidden'}>
                        <div className="w-full h-[50vh] md:h-[65vh] overflow-auto p-4">
                          {selectedPdfField ? (
                            (() => {
                              const cfg = fieldConfigs[selectedPdfField.name]
                              const type = cfg?.type || 'text'
                              const Block = FORM_BLOCKS[type] || FORM_BLOCKS.text
                              const tool = cfg?.tool
                              const hasNearby = tool?.type === 'maps' && tool?.config?.nearbyPlaces
                              const needsPrefillConfirm = tool?.config?.requirePrefillConfirmation
                              const prefillConfirmBtn = needsPrefillConfirm ? (
                                <button type="button" disabled className="btn btn-square btn-warning join-item rounded-r-full" title="Confirm prefilled value (preview)">
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                                </button>
                              ) : null
                              const mapBtn = tool?.type === 'maps' && tool?.config?.showMapButton ? (
                                <button type="button" disabled className={`btn btn-square btn-primary join-item ${!hasNearby && !prefillConfirmBtn ? 'rounded-r-full' : ''}`} title="Open map">
                                  <FontAwesomeIcon icon={faMapLocationDot} className="h-4 w-4" />
                                </button>
                              ) : null
                              const nearbyBtn = hasNearby ? (
                                <button type="button" disabled className={`btn btn-square btn-outline btn-primary join-item ${!prefillConfirmBtn ? 'rounded-r-full' : ''}`} title={`Find nearest ${tool?.config?.nearbyPlaceType || 'place'}`}>
                                  <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4" />
                                </button>
                              ) : null
                              return (
                                <div>
                                  <Block
                                    label={cfg?.label || selectedPdfField.name}
                                    description={cfg?.description}
                                    required={cfg?.required !== false}
                                    options={cfg?.options}
                                  >
                                    {mapBtn}
                                    {nearbyBtn}
                                    {prefillConfirmBtn}
                                  </Block>
                                  {needsPrefillConfirm && <p className="text-xs text-base-content/60 mt-1">Tap to confirm this prefilled value.</p>}
                                </div>
                              )
                            })()
                          ) : selectedGroup ? (
                            (() => {
                              const normalizeChildren = (g: any): Array<{ type: 'field'; fieldName: string } | { type: 'group'; groupId: string }> => {
                                return groupChildren(g)
                              }

                              const renderFieldPreview = (fname: string, idx: number, key: string) => {
                                const fCfg = fieldConfigs[fname]
                                const type = fCfg?.type || 'text'
                                const Block = FORM_BLOCKS[type] || FORM_BLOCKS.text
                                const rawLabel = fCfg?.label || fname
                                const label = (rawLabel && !String(rawLabel).includes('undefined')) ? rawLabel : `Option ${idx + 1}`
                                const tool = fCfg?.tool
                                const hasNearby = tool?.type === 'maps' && tool?.config?.nearbyPlaces
                                const needsPrefillConfirm = tool?.config?.requirePrefillConfirmation
                                const prefillConfirmBtn = needsPrefillConfirm ? (
                                  <button type="button" disabled className="btn btn-square btn-warning join-item rounded-r-full" title="Confirm prefilled value (preview)">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                                  </button>
                                ) : null
                                const mapBtn = tool?.type === 'maps' && tool?.config?.showMapButton ? (
                                  <button type="button" disabled className={`btn btn-square btn-primary join-item ${!hasNearby && !prefillConfirmBtn ? 'rounded-r-full' : ''}`} title="Open map">
                                    <FontAwesomeIcon icon={faMapLocationDot} className="h-4 w-4" />
                                  </button>
                                ) : null
                                const nearbyBtn = hasNearby ? (
                                  <button type="button" disabled className={`btn btn-square btn-outline btn-primary join-item ${!prefillConfirmBtn ? 'rounded-r-full' : ''}`} title={`Find nearest ${tool?.config?.nearbyPlaceType || 'place'}`}>
                                    <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4" />
                                  </button>
                                ) : null
                                return (
                                  <div key={key}>
                                    <Block
                                      label={label}
                                      description={fCfg?.showDescription ? fCfg?.description : undefined}
                                      required={fCfg?.required !== false}
                                      options={fCfg?.options}
                                    >
                                      {mapBtn}
                                      {nearbyBtn}
                                      {prefillConfirmBtn}
                                    </Block>
                                    {needsPrefillConfirm && <p className="text-xs text-base-content/60 mt-1">Tap to confirm this prefilled value.</p>}
                                  </div>
                                )
                              }

                              const renderGroupPreview = (group: any, keyPrefix: string): React.ReactNode => {
                                const cfg = groupConfigs[group.id] || {}
                                const layout = cfg.layout || 'stack'
                                const gridClass = layout === 'two-columns'
                                  ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
                                  : layout === 'three-columns'
                                    ? 'grid grid-cols-1 md:grid-cols-3 gap-4'
                                    : 'grid grid-cols-1 gap-4'
                                const children = normalizeChildren(group)

                                return (
                                  <div key={`${keyPrefix}:${group.id}`} className="w-full">
                                    {cfg.showHeading && (
                                      <div className="mb-2">
                                        <div className="text-lg font-semibold">{group.name}</div>
                                        {cfg.showDescription && cfg.description && (
                                          <div className="text-sm text-base-content/60">{cfg.description}</div>
                                        )}
                                      </div>
                                    )}
                                    {!cfg.showHeading && cfg.showDescription && cfg.description && (
                                      <div className="text-sm text-base-content/60 mb-2">{cfg.description}</div>
                                    )}

                                    {cfg.mode === 'radio' ? (
                                      <div className={gridClass}>
                                        {children
                                          .filter(ch => ch.type === 'field')
                                          .map((ch, idx) => {
                                            const fname = (ch as any).fieldName as string
                                            const fCfg = fieldConfigs[fname]
                                            const rawLabel = fCfg?.label || fname
                                            const label = (rawLabel && !String(rawLabel).includes('undefined')) ? rawLabel : `Option ${idx + 1}`
                                            return (
                                              <label key={`${keyPrefix}:${group.id}:radio:${fname}`} className="label cursor-pointer justify-start gap-3">
                                                <input type="radio" className="radio radio-primary" disabled />
                                                <span className="label-text">{label}</span>
                                              </label>
                                            )
                                          })}
                                      </div>
                                    ) : (
                                      <div className={gridClass}>
                                        {children.map((ch, idx) => {
                                          if (ch.type === 'field') {
                                            return renderFieldPreview(ch.fieldName, idx, `${keyPrefix}:${group.id}:field:${ch.fieldName}`)
                                          }
                                          const childGroup = fieldGroups.find(gr => gr.id === ch.groupId)
                                          if (!childGroup) return null
                                          return (
                                            <div key={`${keyPrefix}:${group.id}:group:${ch.groupId}`} className="md:col-span-full border border-base-300 rounded-lg p-3">
                                              {renderGroupPreview(childGroup as any, `${keyPrefix}:${group.id}`)}
                                            </div>
                                          )
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )
                              }

                              return renderGroupPreview(selectedGroup as any, 'selected-group')
                            })()
                          ) : selectedPage ? (
                            (() => {
                              const pCfg = pageConfigs[selectedPage.id] || {}
                              return (
                                <div className="w-full space-y-6">
                                  {pCfg.description && (
                                    <div className="text-sm text-base-content/70">{pCfg.description}</div>
                                  )}
                                  {selectedPage.items.map((itm, idx) => {
                                    if (itm.type === 'group') {
                                      const g = fieldGroups.find(gr => gr.id === itm.key)
                                      if (!g) return null
                                      const gCfg = groupConfigs[g.id] || {}
                                      const layout = gCfg.layout || 'stack'
                                      const gridClass = layout === 'two-columns'
                                        ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
                                        : layout === 'three-columns'
                                          ? 'grid grid-cols-1 md:grid-cols-3 gap-4'
                                          : 'grid grid-cols-1 gap-4'
                                      return (
                                        <div key={`pg-${idx}`} className="w-full">
                                          {gCfg.showHeading && (
                                            <div className="mb-2">
                                              <div className="text-lg font-semibold">{g.name}</div>
                                              {gCfg.showDescription && gCfg.description && <div className="text-sm text-base-content/60">{gCfg.description}</div>}
                                            </div>
                                          )}
                                          {!gCfg.showHeading && gCfg.showDescription && gCfg.description && (
                                            <div className="text-sm text-base-content/60 mb-2">{gCfg.description}</div>
                                          )}
                                          <div className={gridClass}>
                                            {collectLeafFieldNames(fieldGroups, g.id).map(fname => {
                                              const fCfg = fieldConfigs[fname]
                                              const type = fCfg?.type || 'text'
                                              const Block = FORM_BLOCKS[type] || FORM_BLOCKS.text
                                              const tool = fCfg?.tool
                                              const hasNearby = tool?.type === 'maps' && tool?.config?.nearbyPlaces
                                              const needsPrefillConfirm = tool?.config?.requirePrefillConfirmation
                                              const prefillConfirmBtn = needsPrefillConfirm ? (
                                                <button type="button" disabled className="btn btn-square btn-warning join-item rounded-r-full" title="Confirm prefilled value (preview)">
                                                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                                                </button>
                                              ) : null
                                              const mapBtn = tool?.type === 'maps' && tool?.config?.showMapButton ? (
                                                <button type="button" disabled className={`btn btn-square btn-primary join-item ${!hasNearby && !prefillConfirmBtn ? 'rounded-r-full' : ''}`} title="Open map">
                                                  <FontAwesomeIcon icon={faMapLocationDot} className="h-4 w-4" />
                                                </button>
                                              ) : null
                                              const nearbyBtn = hasNearby ? (
                                                <button type="button" disabled className={`btn btn-square btn-outline btn-primary join-item ${!prefillConfirmBtn ? 'rounded-r-full' : ''}`} title={`Find nearest ${tool?.config?.nearbyPlaceType || 'place'}`}>
                                                  <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4" />
                                                </button>
                                              ) : null
                                              return (
                                                <div key={fname}>
                                                  <Block
                                                    label={fCfg?.label || fname}
                                                    description={fCfg?.showDescription ? fCfg?.description : undefined}
                                                    required={fCfg?.required !== false}
                                                    options={fCfg?.options}
                                                  >
                                                    {mapBtn}
                                                    {nearbyBtn}
                                                    {prefillConfirmBtn}
                                                  </Block>
                                                  {needsPrefillConfirm && <p className="text-xs text-base-content/60 mt-1">Tap to confirm this prefilled value.</p>}
                                                </div>
                                              )
                                            })}
                                          </div>
                                        </div>
                                      )
                                    }
                                    // Field
                                    const f = parsedPdfFields.find(p => p.name === itm.key)
                                    if (!f) return null
                                    const fCfg = fieldConfigs[f.name]
                                    const type = fCfg?.type || 'text'
                                    const Block = FORM_BLOCKS[type] || FORM_BLOCKS.text
                                    const tool = fCfg?.tool
                                    const hasNearby = tool?.type === 'maps' && tool?.config?.nearbyPlaces
                                    const needsPrefillConfirm = tool?.config?.requirePrefillConfirmation
                                    const prefillConfirmBtn = needsPrefillConfirm ? (
                                      <button type="button" disabled className="btn btn-square btn-warning join-item rounded-r-full" title="Confirm prefilled value (preview)">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                                      </button>
                                    ) : null
                                    const mapBtn = tool?.type === 'maps' && tool?.config?.showMapButton ? (
                                      <button type="button" disabled className={`btn btn-square btn-primary join-item ${!hasNearby && !prefillConfirmBtn ? 'rounded-r-full' : ''}`} title="Open map">
                                        <FontAwesomeIcon icon={faMapLocationDot} className="h-4 w-4" />
                                      </button>
                                    ) : null
                                    const nearbyBtn = hasNearby ? (
                                      <button type="button" disabled className={`btn btn-square btn-outline btn-primary join-item ${!prefillConfirmBtn ? 'rounded-r-full' : ''}`} title={`Find nearest ${tool?.config?.nearbyPlaceType || 'place'}`}>
                                        <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4" />
                                      </button>
                                    ) : null
                                    return (
                                      <div key={`pf-${idx}`}>
                                        <Block
                                          label={fCfg?.label || f.name}
                                          description={fCfg?.showDescription ? fCfg?.description : undefined}
                                          required={fCfg?.required !== false}
                                          options={fCfg?.options}
                                        >
                                          {mapBtn}
                                          {nearbyBtn}
                                          {prefillConfirmBtn}
                                        </Block>
                                        {needsPrefillConfirm && <p className="text-xs text-base-content/60 mt-1">Tap to confirm this prefilled value.</p>}
                                      </div>
                                    )
                                  })}
                                </div>
                              )
                            })()
                          ) : (
                            <div className="w-full h-full bg-base-200 rounded-lg flex items-center justify-center text-base-content/60">Select an item to preview</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column (1/4) */}
                <div className="col-span-12 md:col-span-3">
                  <div className="card bg-base-100 shadow-xl h-full">
                    <div className="card-body h-[60vh] md:h-[70vh] flex flex-col">
                      <div className="shrink-0">
                        <h3 className="card-title text-base">Field Options</h3>
                        <div className="divider my-2"></div>
                      </div>
                      {selectedPage ? (
                        <div className="space-y-3 flex-1 overflow-y-auto overflow-x-hidden pr-2">
                          <div>
                            <label className="label"><span className="label-text">Page Name</span></label>
                            <input
                              className="input input-bordered w-full"
                              value={selectedPage.name}
                              onChange={e => {
                                const name = e.target.value
                                setPages(prev => prev.map(p => p.id === selectedPage.id ? { ...p, name } : p))
                                setSelectedPage({ ...selectedPage, name })
                              }}
                              placeholder="Page name"
                            />
                          </div>
                          <div>
                            <label className="label"><span className="label-text">Page Description</span></label>
                            <textarea
                              className="textarea textarea-bordered w-full"
                              rows={2}
                              value={pageConfigs[selectedPage.id]?.description || ''}
                              onChange={e => setPageConfigs(cfg => ({
                                ...cfg,
                                [selectedPage.id]: {
                                  ...(cfg[selectedPage.id] || {}),
                                  description: e.target.value,
                                }
                              }))}
                              placeholder="Optional description shown at the top of the page"
                            />
                          </div>
                          <div className="divider"></div>
                          <div className="text-sm font-medium text-base-content/80">Apply to all fields in this page</div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={getFieldNamesUnderPage(selectedPage).length > 0 && getFieldNamesUnderPage(selectedPage).every(fn => fieldConfigs[fn]?.required !== false && !fieldConfigs[fn]?.readonly)}
                              onChange={e => {
                                const checked = e.target.checked
                                const fnames = getFieldNamesUnderPage(selectedPage)
                                setFieldConfigs(cfg => {
                                  const next = { ...cfg }
                                  for (const fn of fnames) {
                                    next[fn] = applyRequiredFlag(next[fn] || { label: fn, type: 'text' }, checked)
                                  }
                                  return next
                                })
                              }}
                            />
                            <span className="text-sm">Required</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={getFieldNamesUnderPage(selectedPage).length > 0 && getFieldNamesUnderPage(selectedPage).every(fn => !!fieldConfigs[fn]?.readonly)}
                              onChange={e => {
                                const checked = e.target.checked
                                const fnames = getFieldNamesUnderPage(selectedPage)
                                setFieldConfigs(cfg => {
                                  const next = { ...cfg }
                                  for (const fn of fnames) {
                                    next[fn] = applyReadonlyFlag(next[fn] || { label: fn, type: 'text', required: true }, checked)
                                  }
                                  return next
                                })
                              }}
                            />
                            <span className="text-sm">Read only</span>
                          </div>
                          {managementDetails.cache?.enabled && (
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                className="checkbox checkbox-sm"
                                checked={getFieldNamesUnderPage(selectedPage).length > 0 && getFieldNamesUnderPage(selectedPage).every(fn => !!fieldConfigs[fn]?.excludeFromCache)}
                                onChange={e => {
                                  const checked = e.target.checked
                                  const fnames = getFieldNamesUnderPage(selectedPage)
                                  setFieldConfigs(cfg => {
                                    const next = { ...cfg }
                                    for (const fn of fnames) {
                                      next[fn] = { ...(next[fn] || { label: fn, type: 'text' }), excludeFromCache: checked }
                                    }
                                    return next
                                  })
                                }}
                              />
                              <span className="text-sm">Exclude from cache</span>
                            </div>
                          )}
                        </div>
                      ) : selectedGroup ? (
                        <div className="space-y-3 flex-1 overflow-y-auto overflow-x-hidden pr-2">
                          <div>
                            <label className="label"><span className="label-text">Group Name</span></label>
                            <input
                              className="input input-bordered w-full"
                              value={selectedGroup.name}
                              onChange={e => {
                                const name = e.target.value
                                setFieldGroups(prev => prev.map(g => g.id === selectedGroup.id ? { ...g, name } : g))
                                setSelectedGroup({ ...selectedGroup, name })
                              }}
                              placeholder="Group name"
                            />
                          </div>
                          <div>
                            <label className="label"><span className="label-text">Layout</span></label>
                            <select
                              className="select select-bordered w-full"
                              value={groupConfigs[selectedGroup.id]?.layout || 'stack'}
                              onChange={e => setGroupConfigs(cfg => ({
                                ...cfg,
                                [selectedGroup.id]: {
                                  ...(cfg[selectedGroup.id] || {}),
                                  layout: e.target.value as any,
                                }
                              }))}
                            >
                              <option value="stack">Stack (one per row)</option>
                              <option value="two-columns">Two Columns</option>
                              <option value="three-columns">Three Columns</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={!!groupConfigs[selectedGroup.id]?.showHeading}
                              onChange={e => setGroupConfigs(cfg => ({
                                ...cfg,
                                [selectedGroup.id]: {
                                  ...(cfg[selectedGroup.id] || {}),
                                  showHeading: e.target.checked,
                                }
                              }))}
                            />
                            <span className="text-sm">Show group heading in form</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={!!groupConfigs[selectedGroup.id]?.showDescription}
                              onChange={e => setGroupConfigs(cfg => ({
                                ...cfg,
                                [selectedGroup.id]: {
                                  ...(cfg[selectedGroup.id] || {}),
                                  showDescription: e.target.checked,
                                }
                              }))}
                            />
                            <span className="text-sm">Show description in form</span>
                          </div>
                          <div>
                            <label className="label"><span className="label-text">Group Description</span></label>
                            <textarea
                              className="textarea textarea-bordered w-full"
                              rows={2}
                              value={groupConfigs[selectedGroup.id]?.description || ''}
                              onChange={e => setGroupConfigs(cfg => ({
                                ...cfg,
                                [selectedGroup.id]: {
                                  ...(cfg[selectedGroup.id] || {}),
                                  description: e.target.value,
                                }
                              }))}
                              placeholder="Optional description for this group"
                            />
                          </div>
                          <div>
                            <label className="label"><span className="label-text">Group Mode</span></label>
                            <select
                              className="select select-bordered w-full"
                              value={groupConfigs[selectedGroup.id]?.mode || 'fields'}
                              onChange={e => setGroupConfigs(cfg => ({
                                ...cfg,
                                [selectedGroup.id]: {
                                  ...(cfg[selectedGroup.id] || {}),
                                  mode: e.target.value as any,
                                }
                              }))}
                            >
                              <option value="fields">Individual Fields</option>
                              <option value="radio">Radio (single choice)</option>
                            </select>
                            <div className="text-xs text-base-content/60 mt-1">Radio mode is recommended only if all items are checkboxes.</div>
                          </div>
                          {groupConfigs[selectedGroup.id]?.mode === 'radio' && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-primary"
                                  checked={!groupConfigs[selectedGroup.id]?.readonly && groupConfigs[selectedGroup.id]?.required !== false}
                                  onChange={e => setGroupConfigs(cfg => ({
                                    ...cfg,
                                    [selectedGroup.id]: applyRequiredFlag(
                                      { ...(cfg[selectedGroup.id] || {}), required: true },
                                      e.target.checked,
                                    ),
                                  }))}
                                />
                                <span className="text-sm">Required</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-primary"
                                  checked={!!groupConfigs[selectedGroup.id]?.readonly}
                                  onChange={e => setGroupConfigs(cfg => ({
                                    ...cfg,
                                    [selectedGroup.id]: applyReadonlyFlag(cfg[selectedGroup.id] || {}, e.target.checked),
                                  }))}
                                />
                                <span className="text-sm">Read only</span>
                              </div>
                              {normalizeFillers(managementDetails.fillers).length >= 2 && (
                                <div>
                                  <label className="label"><span className="label-text">Filler stage</span></label>
                                  <select
                                    className="select select-bordered w-full"
                                    value={Number(groupConfigs[selectedGroup.id]?.fillerStage) >= 1 ? Number(groupConfigs[selectedGroup.id]?.fillerStage) : 1}
                                    onChange={e => {
                                      const stage = parseInt(e.target.value, 10)
                                      setGroupConfigs(cfg => ({
                                        ...cfg,
                                        [selectedGroup.id]: { ...(cfg[selectedGroup.id] || {}), fillerStage: stage },
                                      }))
                                      setFieldConfigs(cfg => {
                                        const next = { ...cfg }
                                        for (const fn of collectLeafFieldNames(fieldGroups, selectedGroup.id)) {
                                          next[fn] = { ...(next[fn] || { label: fn, type: 'text', required: true }), fillerStage: stage }
                                        }
                                        return next
                                      })
                                    }}
                                  >
                                    {normalizeFillers(managementDetails.fillers).map((f) => (
                                      <option key={f.stage} value={f.stage}>
                                        Stage {f.stage} — {fillerLabel(f)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="divider"></div>
                          <div className="text-sm font-medium text-base-content/80">Apply to all fields in this group</div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={collectLeafFieldNames(fieldGroups, selectedGroup.id).length > 0 && collectLeafFieldNames(fieldGroups, selectedGroup.id).every(fn => fieldConfigs[fn]?.required !== false && !fieldConfigs[fn]?.readonly)}
                              onChange={e => {
                                const checked = e.target.checked
                                const fnames = collectLeafFieldNames(fieldGroups, selectedGroup.id)
                                setFieldConfigs(cfg => {
                                  const next = { ...cfg }
                                  for (const fn of fnames) {
                                    next[fn] = applyRequiredFlag(next[fn] || { label: fn, type: 'text' }, checked)
                                  }
                                  return next
                                })
                              }}
                            />
                            <span className="text-sm">Required</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={collectLeafFieldNames(fieldGroups, selectedGroup.id).length > 0 && collectLeafFieldNames(fieldGroups, selectedGroup.id).every(fn => !!fieldConfigs[fn]?.readonly)}
                              onChange={e => {
                                const checked = e.target.checked
                                const fnames = collectLeafFieldNames(fieldGroups, selectedGroup.id)
                                setFieldConfigs(cfg => {
                                  const next = { ...cfg }
                                  for (const fn of fnames) {
                                    next[fn] = applyReadonlyFlag(next[fn] || { label: fn, type: 'text', required: true }, checked)
                                  }
                                  return next
                                })
                              }}
                            />
                            <span className="text-sm">Read only</span>
                          </div>
                          {managementDetails.cache?.enabled && (
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                className="checkbox checkbox-sm"
                                checked={collectLeafFieldNames(fieldGroups, selectedGroup.id).length > 0 && collectLeafFieldNames(fieldGroups, selectedGroup.id).every(fn => !!fieldConfigs[fn]?.excludeFromCache)}
                                onChange={e => {
                                  const checked = e.target.checked
                                  const fnames = collectLeafFieldNames(fieldGroups, selectedGroup.id)
                                  setFieldConfigs(cfg => {
                                    const next = { ...cfg }
                                    for (const fn of fnames) {
                                      next[fn] = { ...(next[fn] || { label: fn, type: 'text' }), excludeFromCache: checked }
                                    }
                                    return next
                                  })
                                }}
                              />
                              <span className="text-sm">Exclude from cache</span>
                            </div>
                          )}
                        </div>
                      ) : selectedPdfField ? (
                        <div className="space-y-3 flex-1 overflow-y-auto overflow-x-hidden pr-2">
                          <div>
                            <label className="label"><span className="label-text">Selected Field</span></label>
                            <input className="input input-bordered w-full bg-base-200 text-base-content/70" value={selectedPdfField.name} readOnly />
                          </div>
                          <div>
                            <label className="label"><span className="label-text">PDF Field Type</span></label>
                            <input className="input input-bordered w-full bg-base-200 text-base-content/70" value={selectedPdfField.type} readOnly />
                          </div>
                          <div className="divider"></div>
                          <div>
                            <label className="label"><span className="label-text">Form Field Name</span></label>
                            <input
                              className="input input-bordered w-full"
                              value={fieldConfigs[selectedPdfField.name]?.label || ''}
                              onChange={e => setFieldConfigs(cfg => ({
                                ...cfg,
                                [selectedPdfField.name]: {
                                  ...(cfg[selectedPdfField.name] || { type: 'text', required: true }),
                                  label: e.target.value,
                                }
                              }))}
                              placeholder="Label to show in form"
                            />
                          </div>
                          <div>
                            <label className="label"><span className="label-text">Form Field Type</span></label>
                            <select
                              className="select select-bordered w-full"
                              value={fieldConfigs[selectedPdfField.name]?.type || 'text'}
                              onChange={e => {
                                const nextType = e.target.value as FormQuestion['type']
                                setFieldConfigs(cfg => {
                                  const prev = cfg[selectedPdfField.name] || { label: selectedPdfField.name, required: true, type: 'text' as FormQuestion['type'] }
                                  const next: FieldConfig = { ...prev, type: nextType }
                                  if (nextType === SIGNATURE_DATE_TYPE) {
                                    const sigFields = Object.entries(cfg).filter(([k, c]) => k !== selectedPdfField.name && c.type === 'signature')
                                    if (!next.signatureSource || cfg[next.signatureSource]?.type !== 'signature') {
                                      next.signatureSource = sigFields.length === 1 ? sigFields[0][0] : ''
                                    }
                                    if (!next.format) next.format = 'MM/DD/YYYY'
                                    next.tool = undefined
                                  } else {
                                    next.signatureSource = undefined
                                  }
                                  return { ...cfg, [selectedPdfField.name]: next }
                                })
                              }}
                            >
                              {([
                                ['text', 'text'],
                                ['number', 'number'],
                                ['date', 'date'],
                                ['select', 'select'],
                                ['checkbox', 'checkbox'],
                                ['textarea', 'textarea'],
                                ['email', 'email'],
                                ['tel', 'tel'],
                                ['password', 'password'],
                                ['radio', 'radio'],
                                ['signature', 'signature'],
                                ['signatureDate', 'signature date'],
                              ] as const).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>
                          {fieldConfigs[selectedPdfField.name]?.type === SIGNATURE_DATE_TYPE && (
                            <div className="p-3 rounded-lg border border-base-300 space-y-2 bg-base-200/30">
                              <div className="font-semibold text-sm">Signature date</div>
                              <div>
                                <label className="label"><span className="label-text">Signature field</span></label>
                                <select
                                  className="select select-bordered w-full"
                                  value={fieldConfigs[selectedPdfField.name]?.signatureSource || ''}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: SIGNATURE_DATE_TYPE, required: true }),
                                      signatureSource: e.target.value,
                                    }
                                  }))}
                                >
                                  <option value="">Select a signature field...</option>
                                  {Object.entries(fieldConfigs)
                                    .filter(([k, c]) => k !== selectedPdfField.name && c.type === 'signature')
                                    .map(([k, c]) => (
                                      <option key={k} value={k}>{c.label || k}</option>
                                    ))}
                                </select>
                                {Object.values(fieldConfigs).every(c => c.type !== 'signature') && (
                                  <div className="text-xs text-warning mt-1">Set a field to type “signature” first, then link it here.</div>
                                )}
                                <div className="text-xs text-base-content/60 mt-1">Auto-fills with the date from that signature when it is signed.</div>
                              </div>
                              <div>
                                <label className="label"><span className="label-text">Format</span></label>
                                <select
                                  className="select select-bordered w-full"
                                  value={fieldConfigs[selectedPdfField.name]?.format || 'MM/DD/YYYY'}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: SIGNATURE_DATE_TYPE, required: true }),
                                      format: e.target.value,
                                    }
                                  }))}
                                >
                                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                </select>
                              </div>
                            </div>
                          )}
                          {(fieldConfigs[selectedPdfField.name]?.type === 'select' || fieldConfigs[selectedPdfField.name]?.type === 'radio') && (
                            <div>
                              <label className="label"><span className="label-text">Options (comma-separated)</span></label>
                              <input
                                className="input input-bordered w-full"
                                value={optionsDraft[selectedPdfField.name] ?? (fieldConfigs[selectedPdfField.name]?.options || []).join(', ')}
                                onChange={e => {
                                  const raw = e.target.value
                                  const fname = selectedPdfField.name
                                  setOptionsDraft(prev => ({ ...prev, [fname]: raw }))
                                  setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [fname]: {
                                      ...(cfg[fname] || { label: fname, type: 'select', required: true }),
                                      options: parseOptionsInput(raw),
                                    },
                                  }))
                                }}
                                placeholder="Option A, Option B, Option C"
                              />
                            </div>
                          )}
                          <div>
                            <label className="label"><span className="label-text">Description</span></label>
                            <textarea
                              className="textarea textarea-bordered w-full"
                              rows={2}
                              value={fieldConfigs[selectedPdfField.name]?.description || ''}
                              onChange={e => setFieldConfigs(cfg => ({
                                ...cfg,
                                [selectedPdfField.name]: {
                                  ...(cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: 'text', required: true }),
                                  description: e.target.value,
                                }
                              }))}
                              placeholder="Help text or guidance for the user"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={!!fieldConfigs[selectedPdfField.name]?.showDescription}
                              onChange={e => setFieldConfigs(cfg => ({
                                ...cfg,
                                [selectedPdfField.name]: {
                                  ...(cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: 'text', required: true }),
                                  showDescription: e.target.checked,
                                }
                              }))}
                            />
                            <span className="text-sm">Show description in form</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={fieldConfigs[selectedPdfField.name]?.required !== false && !fieldConfigs[selectedPdfField.name]?.readonly}
                              onChange={e => setFieldConfigs(cfg => ({
                                ...cfg,
                                [selectedPdfField.name]: applyRequiredFlag(
                                  (cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: 'text' }),
                                  e.target.checked,
                                ),
                              }))}
                            />
                            <span className="text-sm">Required</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary"
                              checked={!!fieldConfigs[selectedPdfField.name]?.readonly}
                              onChange={e => setFieldConfigs(cfg => ({
                                ...cfg,
                                [selectedPdfField.name]: applyReadonlyFlag(
                                  (cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: 'text', required: true }),
                                  e.target.checked,
                                ),
                              }))}
                            />
                            <span className="text-sm">Read only</span>
                          </div>
                          {normalizeFillers(managementDetails.fillers).length >= 2 && (
                            <div>
                              <label className="label"><span className="label-text">Filler stage</span></label>
                              <select
                                className="select select-bordered w-full"
                                value={Number(fieldConfigs[selectedPdfField.name]?.fillerStage) >= 1 ? Number(fieldConfigs[selectedPdfField.name]?.fillerStage) : 1}
                                onChange={e => {
                                  const stage = parseInt(e.target.value, 10)
                                  setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: 'text', required: true }),
                                      fillerStage: stage,
                                    },
                                  }))
                                }}
                              >
                                {normalizeFillers(managementDetails.fillers).map((f) => (
                                  <option key={f.stage} value={f.stage}>
                                    Stage {f.stage} — {fillerLabel(f)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          {fieldConfigs[selectedPdfField.name]?.type !== SIGNATURE_DATE_TYPE && (
                          <div>
                            <label className="label"><span className="label-text">Tool for this Field</span></label>
                            <select
                              className="select select-bordered w-full"
                              value={fieldConfigs[selectedPdfField.name]?.tool?.type || ''}
                              disabled={selectedPdfDuplicateMeta.isDuplicate && !selectedPdfDuplicateMeta.isPrimary}
                              onChange={e => setFieldConfigs(cfg => ({
                                ...cfg,
                                [selectedPdfField.name]: {
                                  ...(cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: 'text', required: true }),
                                  tool: e.target.value ? { type: e.target.value as any, config: {} } : undefined,
                                }
                              }))}
                            >
                              <option value="">None</option>
                              <option value="date">Current Date</option>
                              <option value="user">Current User</option>
                              <option value="weather">Weather</option>
                              <option value="maps">Google Maps</option>
                              <option value="jobtread">JobTread</option>
                            </select>
                            {selectedPdfDuplicateMeta.isDuplicate && !selectedPdfDuplicateMeta.isPrimary && (
                              <div className="text-xs text-base-content/60 mt-1">
                                This field is a duplicate of <strong>{selectedPdfDuplicateMeta.primaryField}</strong>. Tools can be set on the primary only; duplicates inherit the same answer.
                              </div>
                            )}
                          </div>
                          )}
                          {managementDetails.cache?.enabled && (
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                className="checkbox checkbox-sm"
                                checked={!!fieldConfigs[selectedPdfField.name]?.excludeFromCache}
                                onChange={e => setFieldConfigs(cfg => ({
                                  ...cfg,
                                  [selectedPdfField.name]: {
                                    ...(cfg[selectedPdfField.name] || { label: selectedPdfField.name, type: 'text', required: true }),
                                    excludeFromCache: e.target.checked,
                                  }
                                }))}
                              />
                              <span className="text-sm">Exclude from cache</span>
                            </div>
                          )}
                          {(() => {
                            const t = fieldConfigs[selectedPdfField.name]?.tool
                            const canPrefill = t?.type === 'date' || t?.type === 'user' || t?.type === 'weather' ||
                              (t?.type === 'maps' && t?.config?.nearbyPlaces) ||
                              (t?.type === 'jobtread' && t?.config?.prefill)
                            if (!canPrefill) return null
                            return (
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-sm checkbox-warning"
                                  checked={!!t?.config?.requirePrefillConfirmation}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { ...t!, config: { ...(t?.config || {}), requirePrefillConfirmation: e.target.checked } }
                                    }
                                  }))}
                                />
                                <span className="text-sm">Require prefill confirmation</span>
                              </div>
                            )
                          })()}
                          {/* Tool-specific configs */}
                          {fieldConfigs[selectedPdfField.name]?.tool?.type === 'date' && (
                            <div className="p-3 rounded-lg border border-base-300 space-y-2 bg-base-200/30">
                              <div className="font-semibold text-sm">Current Date</div>
                              <div>
                                <label className="label"><span className="label-text">Format</span></label>
                                <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.format || 'MM/DD/YYYY'}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'date', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), format: e.target.value } }
                                    }
                                  }))}
                                >
                                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                </select>
                                <div className="text-xs text-base-content/60 mt-1">Auto-fills when the step is shown.</div>
                              </div>
                            </div>
                          )}
                          {fieldConfigs[selectedPdfField.name]?.tool?.type === 'user' && (
                            <div className="p-3 rounded-lg border border-base-300 space-y-2 bg-base-200/30">
                              <div className="font-semibold text-sm">Current User</div>
                              <div>
                                <label className="label"><span className="label-text">Prefill with</span></label>
                                <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.prefillData || 'name'}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'user', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), prefillData: e.target.value } }
                                    }
                                  }))}
                                >
                                  <option value="name">Full name</option>
                                  <option value="preferredName">Preferred name</option>
                                  <option value="displayName">Display name (first/preferred)</option>
                                  <option value="position">Position</option>
                                  <option value="email">Email</option>
                                  <option value="phone">Phone</option>
                                  <option value="department">Department</option>
                                  <option value="supervisor">Supervisor</option>
                                  <option value="employeeId">Employee ID</option>
                                  <option value="username">Username</option>
                                </select>
                                <div className="text-xs text-base-content/60 mt-1">Fetched from the employee contact record when matched to the logged-in user. Auto-fills when the step is shown.</div>
                              </div>
                            </div>
                          )}
                          {fieldConfigs[selectedPdfField.name]?.tool?.type === 'weather' && (
                            <div className="p-4 rounded-lg border border-base-300 space-y-4 bg-base-200/30">
                              <div className="font-semibold text-base">Weather Settings</div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="label"><span className="label-text">Timeframe</span></label>
                                  <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.timeframe || 'current'}
                                    onChange={e => setFieldConfigs(cfg => ({
                                      ...cfg,
                                      [selectedPdfField.name]: {
                                        ...(cfg[selectedPdfField.name]!),
                                        tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), timeframe: e.target.value } }
                                      }
                                    }))}
                                  >
                                    <option value="current">Current</option>
                                    <option value="hourly">Next 6 hours</option>
                                    <option value="daily">Daily</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="label"><span className="label-text">Location Field</span></label>
                                  <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.locationField || ''}
                                    onChange={e => setFieldConfigs(cfg => ({
                                      ...cfg,
                                      [selectedPdfField.name]: {
                                        ...(cfg[selectedPdfField.name]!),
                                        tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), locationField: e.target.value } }
                                      }
                                    }))}
                                  >
                                    <option value="">Select a field...</option>
                                    {Object.keys(fieldConfigs).filter(k => k !== selectedPdfField.name).map(k => (
                                      <option key={k} value={k}>{fieldConfigs[k].label || k}</option>
                                    ))}
                                  </select>
                                  <div className="text-xs text-base-content/60 mt-1">Used to determine latitude/longitude for weather.</div>
                                </div>
                              </div>

                              <div>
                                <label className="label"><span className="label-text">Behavior</span></label>
                                <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.behavior || 'setText'}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), behavior: e.target.value } }
                                    }
                                  }))}
                                >
                                  <option value="setText">Set text summary</option>
                                  <option value="toggleCheckbox">Toggle checkbox by conditions</option>
                                </select>
                              </div>

                              {fieldConfigs[selectedPdfField.name]?.tool?.config?.behavior !== 'toggleCheckbox' && (
      <div>
        <label className="label"><span className="label-text">Include in summary</span></label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {['includeTemp','includeConditions','includeWind','includePrecip'].map(key => (
            <label key={key} className="flex items-center gap-2">
              <input type="checkbox" className="checkbox checkbox-sm" checked={!!fieldConfigs[selectedPdfField.name]?.tool?.config?.[key]}
                onChange={e => setFieldConfigs(cfg => ({
                  ...cfg,
                  [selectedPdfField.name]: {
                    ...(cfg[selectedPdfField.name]!),
                    tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), [key]: e.target.checked } }
                  }
                }))}
              />
              <span className="text-sm">{key.replace('include','')}</span>
            </label>
          ))}
        </div>
      </div>
    )}

                              {fieldConfigs[selectedPdfField.name]?.tool?.config?.behavior === 'toggleCheckbox' && (
      <div className="space-y-4">
        <div>
          <label className="label"><span className="label-text">Conditions (any)</span></label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {[
                                          { key: 'condRain', label: 'Rain' },
                                          { key: 'condSnow', label: 'Snow' },
                                          { key: 'condThunder', label: 'Thunder/Lightning' },
                                          { key: 'condFog', label: 'Fog' },
                                          { key: 'condIce', label: 'Ice' },
                                          { key: 'condExtremeHeat', label: 'Extreme Heat' }
                                        ].map(c => (
                                          <label key={c.key} className="flex items-center gap-2">
                                            <input type="checkbox" className="checkbox checkbox-sm" checked={!!fieldConfigs[selectedPdfField.name]?.tool?.config?.[c.key]}
                                              onChange={e => setFieldConfigs(cfg => ({
                                                ...cfg,
                                                [selectedPdfField.name]: {
                                                  ...(cfg[selectedPdfField.name]!),
                                                  tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), [c.key]: e.target.checked } }
                                                }
                                              }))}
                                            />
                                            <span className="text-sm">{c.label}</span>
                                          </label>
                                        ))}
          </div>
        </div>
        <div>
          <label className="label"><span className="label-text">Thresholds</span></label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <input className="input input-bordered w-full" placeholder=">= Heat °F" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.heatThreshold ?? ''}
                                          onChange={e => setFieldConfigs(cfg => ({
                                            ...cfg,
                                            [selectedPdfField.name]: {
                                              ...(cfg[selectedPdfField.name]!),
                                              tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), heatThreshold: e.target.value } }
                                            }
                                          }))}
                                        />
                                        <input className="input input-bordered w-full" placeholder="<= Cold °F" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.coldThreshold ?? ''}
                                          onChange={e => setFieldConfigs(cfg => ({
                                            ...cfg,
                                            [selectedPdfField.name]: {
                                              ...(cfg[selectedPdfField.name]!),
                                              tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), coldThreshold: e.target.value } }
                                            }
                                          }))}
                                        />
                                        <input className="input input-bordered w-full" placeholder=">= Wind mph" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.windThreshold ?? ''}
                                          onChange={e => setFieldConfigs(cfg => ({
                                            ...cfg,
                                            [selectedPdfField.name]: {
                                              ...(cfg[selectedPdfField.name]!),
                                              tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), windThreshold: e.target.value } }
                                            }
                                          }))}
                                        />
                                        <input className="input input-bordered w-full" placeholder=">= Precip %" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.precipThreshold ?? ''}
                                          onChange={e => setFieldConfigs(cfg => ({
                                            ...cfg,
                                            [selectedPdfField.name]: {
                                              ...(cfg[selectedPdfField.name]!),
                                              tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), precipThreshold: e.target.value } }
                                            }
                                          }))}
                                        />
          </div>
          <label className="flex items-center gap-2 mt-2">
                                        <input type="checkbox" className="checkbox checkbox-sm" checked={!!fieldConfigs[selectedPdfField.name]?.tool?.config?.uncheckOtherwise}
                                          onChange={e => setFieldConfigs(cfg => ({
                                            ...cfg,
                                            [selectedPdfField.name]: {
                                              ...(cfg[selectedPdfField.name]!),
                                              tool: { type: 'weather', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), uncheckOtherwise: e.target.checked } }
                                            }
                                          }))}
                                        />
                                        <span className="text-sm">Uncheck if conditions not met</span>
          </label>
        </div>
                                </div>
                              )}
                            </div>
                          )}
                          {fieldConfigs[selectedPdfField.name]?.tool?.type === 'maps' && (
                            <div className="p-3 rounded-lg border border-base-300 space-y-3">
                              <div className="font-semibold">Maps Settings</div>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="checkbox checkbox-sm" checked={!!fieldConfigs[selectedPdfField.name]?.tool?.config?.showMapButton}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'maps', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), showMapButton: e.target.checked } }
                                    }
                                  }))}
                                />
                                <span className="text-sm">Show map button</span>
                              </label>
                              <div className="divider my-1"></div>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="checkbox checkbox-sm" checked={!!fieldConfigs[selectedPdfField.name]?.tool?.config?.etaMode}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'maps', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), etaMode: e.target.checked } }
                                    }
                                  }))}
                                />
                                <span className="text-sm">ETA Mode</span>
                              </label>
                              {fieldConfigs[selectedPdfField.name]?.tool?.config?.etaMode && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="label"><span className="label-text">Start Location Field</span></label>
                                    <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.etaStartField || ''}
                                      onChange={e => setFieldConfigs(cfg => ({
                                        ...cfg,
                                        [selectedPdfField.name]: {
                                          ...(cfg[selectedPdfField.name]!),
                                          tool: { type: 'maps', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), etaStartField: e.target.value } }
                                        }
                                      }))}
                                    >
                                      <option value="">Select a field...</option>
                                      {Object.keys(fieldConfigs).filter(k => k !== selectedPdfField.name).map(k => (
                                        <option key={k} value={k}>{fieldConfigs[k].label || k}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="label"><span className="label-text">Location Types</span></label>
                                    <input className="input input-bordered w-full" placeholder="e.g., hospital, fire station" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.locationTypes || ''}
                                      onChange={e => setFieldConfigs(cfg => ({
                                        ...cfg,
                                        [selectedPdfField.name]: {
                                          ...(cfg[selectedPdfField.name]!),
                                          tool: { type: 'maps', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), locationTypes: e.target.value } }
                                        }
                                      }))}
                                    />
                                  </div>
                                </div>
                              )}
                              <div className="divider my-1"></div>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="checkbox checkbox-sm" checked={!!fieldConfigs[selectedPdfField.name]?.tool?.config?.nearbyPlaces}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'maps', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), nearbyPlaces: e.target.checked } }
                                    }
                                  }))}
                                />
                                <span className="text-sm">Nearby Places Mode</span>
                              </label>
                              {fieldConfigs[selectedPdfField.name]?.tool?.config?.nearbyPlaces && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="label"><span className="label-text">Source Address Field</span></label>
                                    <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.nearbySourceField || ''}
                                      onChange={e => setFieldConfigs(cfg => ({
                                        ...cfg,
                                        [selectedPdfField.name]: {
                                          ...(cfg[selectedPdfField.name]!),
                                          tool: { type: 'maps', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), nearbySourceField: e.target.value } }
                                        }
                                      }))}
                                    >
                                      <option value="">Select address field...</option>
                                      {Object.keys(fieldConfigs).filter(k => k !== selectedPdfField.name).map(k => (
                                        <option key={k} value={k}>{fieldConfigs[k].label || k}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="label"><span className="label-text">Place Type</span></label>
                                    <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.nearbyPlaceType || ''}
                                      onChange={e => setFieldConfigs(cfg => ({
                                        ...cfg,
                                        [selectedPdfField.name]: {
                                          ...(cfg[selectedPdfField.name]!),
                                          tool: { type: 'maps', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), nearbyPlaceType: e.target.value } }
                                        }
                                      }))}
                                    >
                                      <option value="">Select place type...</option>
                                      <option value="hospital">Hospital</option>
                                      <option value="fire_station">Fire Station</option>
                                      <option value="police">Police Station</option>
                                      <option value="pharmacy">Pharmacy</option>
                                      <option value="gas_station">Gas Station</option>
                                      <option value="urgent_care">Urgent Care</option>
                                      <option value="post_office">Post Office</option>
                                      <option value="bank">Bank</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="label"><span className="label-text">Data to Prefill</span></label>
                                    <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.nearbyPrefillData || ''}
                                      onChange={e => setFieldConfigs(cfg => ({
                                        ...cfg,
                                        [selectedPdfField.name]: {
                                          ...(cfg[selectedPdfField.name]!),
                                          tool: { type: 'maps', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), nearbyPrefillData: e.target.value } }
                                        }
                                      }))}
                                    >
                                      <option value="">Choose data...</option>
                                      <option value="name">Name</option>
                                      <option value="address">Address</option>
                                      <option value="phone">Phone</option>
                                      <option value="distance">Distance</option>
                                      <option value="rating">Rating</option>
                                      <option value="website">Website</option>
                                      <option value="hours">Opening Hours (Today)</option>
                                    </select>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {fieldConfigs[selectedPdfField.name]?.tool?.type === 'jobtread' && (
                            <div className="p-3 rounded-lg border border-base-300 space-y-3">
                              <div className="font-semibold">JobTread Settings</div>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="checkbox checkbox-sm" checked={!!fieldConfigs[selectedPdfField.name]?.tool?.config?.autocomplete}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'jobtread', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), autocomplete: e.target.checked } }
                                    }
                                  }))}
                                />
                                <span className="text-sm">Enable Autocomplete</span>
                              </label>
                              <div>
                                <label className="label"><span className="label-text">Autocomplete Type</span></label>
                                <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.autocompleteType || ''}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'jobtread', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), autocompleteType: e.target.value } }
                                    }
                                  }))}
                                >
                                  <option value="">Select type...</option>
                                  <option value="site">Site</option>
                                  <option value="job">Job</option>
                                  <option value="account">Account</option>
                                </select>
                              </div>
                              <div className="divider my-1"></div>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="checkbox checkbox-sm" checked={!!fieldConfigs[selectedPdfField.name]?.tool?.config?.prefill}
                                  onChange={e => setFieldConfigs(cfg => ({
                                    ...cfg,
                                    [selectedPdfField.name]: {
                                      ...(cfg[selectedPdfField.name]!),
                                      tool: { type: 'jobtread', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), prefill: e.target.checked } }
                                    }
                                  }))}
                                />
                                <span className="text-sm">Enable Prefill</span>
                              </label>
                              {fieldConfigs[selectedPdfField.name]?.tool?.config?.prefill && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="label"><span className="label-text">Source Field</span></label>
                                    <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.prefillSource || ''}
                                      onChange={e => setFieldConfigs(cfg => ({
                                        ...cfg,
                                        [selectedPdfField.name]: {
                                          ...(cfg[selectedPdfField.name]!),
                                          tool: { type: 'jobtread', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), prefillSource: e.target.value } }
                                        }
                                      }))}
                                    >
                                      <option value="">Select a field...</option>
                                      {Object.keys(fieldConfigs).filter(k => k !== selectedPdfField.name).map(k => (
                                        <option key={k} value={k}>{fieldConfigs[k].label || k}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="label"><span className="label-text">Prefill Data</span></label>
                                    <select className="select select-bordered w-full" value={fieldConfigs[selectedPdfField.name]?.tool?.config?.prefillData || ''}
                                      onChange={e => setFieldConfigs(cfg => ({
                                        ...cfg,
                                        [selectedPdfField.name]: {
                                          ...(cfg[selectedPdfField.name]!),
                                          tool: { type: 'jobtread', config: { ...(cfg[selectedPdfField.name]!.tool?.config || {}), prefillData: e.target.value } }
                                        }
                                      }))}
                                    >
                                      <option value="">Choose data...</option>
                                      <option value="name">Job Name</option>
                                      <option value="firstword">First Word (before space)</option>
                                      <option value="address">Address</option>
                                      <option value="number">Job Number</option>
                                      <option value="id">Job ID</option>
                                      <option value="status">Status</option>
                                    </select>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-base-content/60">Select a field from the left to configure.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <button className="btn" onClick={() => setManagementStep(0)}><FontAwesomeIcon icon={faArrowLeft} className="mr-2" />Back</button>
                <div className="flex gap-2">
                  <button className="btn btn-primary" onClick={async () => {
                    try {
                      const errs = validateBeforeSave()
                      if (errs.length) {
                        alert('Please fix the following before saving:\n\n' + errs.join('\n'))
                        return
                      }
                      const details = managementDetailsRef.current
                      const nextName = String(details.formName || '').trim() || 'untitled-form'
                      const previousName = editingSourceFormName || nextName
                      const nextFile = sanitizeFormFileName(nextName)
                      const conflict = formService.getForms().find(f =>
                        sanitizeFormFileName(f.formName) === nextFile && !formNamesMatch(f.formName, previousName)
                      )
                      if (conflict) {
                        const overwrite = window.confirm(
                          `Saving as "${nextName}" will overwrite existing form "${conflict.formName}". Continue?`
                        )
                        if (!overwrite) return
                      }
                      const payload = buildFormJson(true)
                      await formService.saveForm(nextName, payload, { previousFormName: previousName })
                      setEditingSourceFormName(nextName)
                      alert('Changes saved')
                      await formService.loadForms(true)
                      const loadedForms = formService.getForms()
                      setForms(loadedForms)
                      const loadedWorkflows = formService.getWorkflows()
                      setWorkflows(loadedWorkflows)
                      setView({ view: 'selection', tab: 'forms' })
                    } catch (e:any) {
                      alert(e.message || 'Failed to save')
                    }
                  }}>Save Changes</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tool Management Container */}
        <div className={view.view === 'toolManagement' ? '' : 'hidden'}>
          <div className="w-full mx-auto">
            <div className="mb-6 flex justify-center">
              <div className="breadcrumbs text-base">
                <ul>
                  <li>
                    <a className="flex items-center gap-1" onClick={() => setView({ view: 'selection', tab: 'management' })} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faCog} className="h-4 w-4" />
                      <span>Form Management</span>
                    </a>
                  </li>
                  <li><span>Tool Management</span></li>
                </ul>
              </div>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="tabs mb-4">
                <a className={`tab tab-bordered${toolTab === 'weather' ? ' tab-active' : ''}`} onClick={() => setToolTab('weather')}>Weather API</a>
                <a className={`tab tab-bordered${toolTab === 'maps' ? ' tab-active' : ''}`} onClick={() => setToolTab('maps')}>Google Maps API</a>
                <a className={`tab tab-bordered${toolTab === 'drive' ? ' tab-active' : ''}`} onClick={() => setToolTab('drive')}>Google Drive API</a>
                <a className={`tab tab-bordered${toolTab === 'jobtread' ? ' tab-active' : ''}`} onClick={() => setToolTab('jobtread')}>JobTread API</a>
                <a className={`tab tab-bordered${toolTab === 'linxup' ? ' tab-active' : ''}`} onClick={() => setToolTab('linxup')}>Linxup API</a>
              </div>

              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  {toolTab === 'weather' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="label"><span className="label-text font-medium">API Key</span></label>
                        <input className="input input-bordered w-full" placeholder="Enter Weather API key" />
                      </div>
                      <div>
                        <label className="label"><span className="label-text font-medium">Base URL</span></label>
                        <input className="input input-bordered w-full" placeholder="https://api.weather.com" />
                      </div>
                      <div className="md:col-span-2">
                        <button className="btn btn-outline" onClick={async () => {
                          try {
                            const baseUrl = API_BASE_URL
                            const token = localStorage.getItem('lts_jwt_token')
                            const headers: Record<string, string> = {
                              'Content-Type': 'application/json',
                              ...(token ? { Authorization: `Bearer ${token}` } : {}),
                            }
                            const capabilityRequest = {
                              app: 'forms',
                              capability: 'weather.currentByCoords',
                              input: {},
                            }
                            const res = await apiFetch(`${baseUrl}/capabilities/execute`, {
                              method: 'POST',
                              headers,
                              body: JSON.stringify(capabilityRequest),
                            })
                            const payload = await res.json()
                            alert(payload.success ? 'Weather API OK' : `Weather API error: ${payload.message || payload?.data?.message || 'unknown'}`)
                          } catch (e:any) { alert(e.message || 'Failed to test Weather API') }
                        }}>Test Weather API</button>
                      </div>
                    </div>
                  )}
                  {toolTab === 'maps' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="label"><span className="label-text font-medium">API Key</span></label>
                        <input className="input input-bordered w-full" placeholder="Enter Google Maps API key" />
                      </div>
                      <div>
                        <label className="label"><span className="label-text font-medium">Maps Embed Base URL</span></label>
                        <input className="input input-bordered w-full" placeholder="https://www.google.com/maps/embed/v1" />
                      </div>
                      <div className="md:col-span-2">
                        <button className="btn btn-outline" onClick={async () => {
                          try {
                            const baseUrl = API_BASE_URL
                            const res = await apiFetch(`${baseUrl}/integrations/maps/static?q=Seattle, WA&zoom=10`)
                            const data = await res.json()
                            alert(data.success ? 'Maps API OK' : `Maps API error: ${data.message || 'unknown'}`)
                          } catch (e:any) { alert(e.message || 'Failed to test Maps API') }
                        }}>Test Maps API</button>
                      </div>
                    </div>
                  )}
                  {toolTab === 'drive' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="label"><span className="label-text font-medium">Client ID</span></label>
                        <input className="input input-bordered w-full" placeholder="Google OAuth Client ID" />
                      </div>
                      <div>
                        <label className="label"><span className="label-text font-medium">Client Secret</span></label>
                        <input className="input input-bordered w-full" placeholder="Google OAuth Client Secret" />
                      </div>
                      <div className="md:col-span-2 flex gap-2">
                        <button className="btn btn-outline" onClick={async () => {
                          try {
                            const baseUrl = API_BASE_URL
                            const res = await apiFetch(`${baseUrl}/integrations/drive/test`)
                            const data = await res.json()
                            alert(data.success ? 'Drive config present' : 'Drive config missing')
                          } catch (e:any) { alert(e.message || 'Failed to test Drive config') }
                        }}>Test Drive Config</button>
                        <button className="btn btn-outline" onClick={async () => {
                          try {
                            const baseUrl = API_BASE_URL
                            const token = localStorage.getItem('lts_jwt_token')
                            // ensure user has authorized Google first
                            const statusRes = await apiFetch(`${baseUrl}/auth/google/status`, {
                              headers: { 'Authorization': token ? `Bearer ${token}` : '' }
                            })
                            const status = await statusRes.json()
                            if (!status?.authorized) {
                              // Start Google OAuth (GIS) Code flow with popup
                              const clientId = (import.meta as any).env.VITE_GOOGLE_DRIVE_CLIENT_ID || ''
                              if (!clientId) throw new Error('VITE_GOOGLE_DRIVE_CLIENT_ID is not set')
                              await new Promise<void>((resolve, reject) => {
                                /* global google */
                                const client = (window as any).google?.accounts?.oauth2?.initCodeClient({
                                  client_id: clientId,
                                  scope: 'https://www.googleapis.com/auth/drive.file',
                                  ux_mode: 'popup',
                                  prompt: 'consent',
                                  callback: async (resp: any) => {
                                    try {
                                      if (!resp || !resp.code) return reject(new Error('No authorization code returned'))
                                      const r = await apiFetch(`${baseUrl}/auth/google/exchange`, {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': token ? `Bearer ${token}` : ''
                                        },
                                        body: JSON.stringify({ code: resp.code })
                                      })
                                      const d = await r.json()
                                      if (!r.ok || !d?.success) return reject(new Error(d?.message || 'Failed to link Google account'))
                                      resolve()
                                    } catch (e) { reject(e) }
                                  }
                                })
                                if (!client) return reject(new Error('Google Identity Services not loaded'))
                                client.requestCode()
                              })
                            }
                            const r = await apiFetch(`${baseUrl}/integrations/drive/test-upload`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': token ? `Bearer ${token}` : ''
                              },
                              body: JSON.stringify({ path: '0AL0z_708YfvYUk9PVA/IT/Portal/API Tests' })
                            })
                            const data = await r.json()
                            if (!r.ok || !data?.success) throw new Error(data?.message || 'Drive test upload failed')
                            alert(`Drive test uploaded: ${data.name}`)
                          } catch (e:any) { alert(e.message || 'Failed to test Drive upload') }
                        }}>Test Drive Upload</button>
                      </div>
                    </div>
                  )}
                  {toolTab === 'jobtread' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="label"><span className="label-text font-medium">API Key</span></label>
                        <input className="input input-bordered w-full" placeholder="JobTread API Key" />
                      </div>
                      <div>
                        <label className="label"><span className="label-text font-medium">Base URL</span></label>
                        <input className="input input-bordered w-full" placeholder="https://api.jobtread.com" />
                      </div>
                    </div>
                  )}
                  {toolTab === 'linxup' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="label"><span className="label-text font-medium">API Key</span></label>
                        <input className="input input-bordered w-full" placeholder="Linxup API Key" />
                      </div>
                      <div>
                        <label className="label"><span className="label-text font-medium">Account ID</span></label>
                        <input className="input input-bordered w-full" placeholder="Linxup Account ID" />
                      </div>
                      <div className="md:col-span-2">
                        <button className="btn btn-outline" onClick={async () => {
                          try {
                            const baseUrl = API_BASE_URL
                            const res = await apiFetch(`${baseUrl}/integrations/linxup/test`)
                            const data = await res.json()
                            alert(data.success ? 'Linxup API OK' : `Linxup API error: ${data.message || 'unknown'}`)
                          } catch (e:any) { alert(e.message || 'Failed to test Linxup API') }
                        }}>Test Linxup API</button>
                      </div>
                    </div>
                  )}
                  <div className="mt-6 flex justify-end gap-2">
                    <button className="btn">Cancel</button>
                    <button className="btn btn-primary">Save Settings</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Completion Modal */}
      {completionOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-xl">
            <h3 className="font-bold text-lg mb-2">Form Completed</h3>
            <p className="text-sm text-base-content/70 mb-4">Would you like to download the completed PDF or upload it?</p>

            {/* Progress: Building PDF */}
            {completionPhase === 'building' && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-base-200 mb-4">
                <FontAwesomeIcon icon={faSpinner} className="fa-spin text-primary" />
                <div>
                  <div className="font-medium">Building your PDF</div>
                  <div className="text-sm text-base-content/60">Filling form fields and generating file...</div>
                </div>
              </div>
            )}

            {/* Progress: Uploading */}
            {completionPhase === 'uploading' && (
              <div className="rounded-lg bg-base-200 p-4 mb-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <FontAwesomeIcon icon={faSpinner} className="fa-spin text-primary" />
                  Uploading to destinations...
                </div>
                <ul className="space-y-1.5">
                  {currentForm?.upload?.drive && (
                    <li className="flex items-center gap-2 text-sm">
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin text-base-content/50" />
                      <span>Google Drive</span>
                    </li>
                  )}
                  {currentForm?.upload?.jobtread && (
                    <li className="flex items-center gap-2 text-sm">
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin text-base-content/50" />
                      <span>JobTread</span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Result: Per-destination status */}
            {uploadResult && !completionBusy && (
              <div className="rounded-lg bg-base-200 p-4 mb-4 space-y-2">
                <div className="text-sm font-medium mb-2">Upload results</div>
                <ul className="space-y-1.5">
                  {currentForm?.upload?.drive && (
                    <li className="flex items-center gap-2 text-sm">
                      {uploadResult.drive?.uploaded ? (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                          <span>Google Drive — uploaded</span>
                          {uploadResult.drive?.webViewLink && (
                            <a href={uploadResult.drive.webViewLink} target="_blank" rel="noopener noreferrer" className="link link-primary text-xs ml-1">Open</a>
                          )}
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCircleXmark} className="text-error" />
                          <span>Google Drive — {uploadResult.drive?.error || 'failed'}</span>
                        </>
                      )}
                    </li>
                  )}
                  {currentForm?.upload?.jobtread && (
                    <li className="flex items-center gap-2 text-sm">
                      {uploadResult.jobtread?.uploaded ? (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                          <span>JobTread — uploaded</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCircleXmark} className="text-error" />
                          <span>JobTread — {uploadResult.jobtread?.error || 'failed'}</span>
                        </>
                      )}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {completionError && (
              <div className="alert alert-error text-sm mb-2">{completionError}</div>
            )}
            <div className="modal-action">
              <button className="btn" onClick={() => { setCompletionOpen(false); completionBlobUrl && URL.revokeObjectURL(completionBlobUrl); setCompletionBlobUrl(null); setCompletionId(null); setCompletionError(null); setUploadResult(null); setCompletionPhase(null); }}>Close</button>
              <button className={`btn btn-secondary${currentForm?.upload?.drive || currentForm?.upload?.jobtread ? '' : ' btn-disabled'}${completionBusy ? ' btn-disabled' : ''}`} onClick={() => handleUploadClick()} disabled={completionBusy}>Upload</button>
              <a className={`btn btn-primary ${!completionBlobUrl ? 'btn-disabled' : ''}`} href={completionBlobUrl || undefined} download={`${getCompletionFileName()}.pdf`}>Download PDF</a>
              <button className="btn" onClick={() => { setCompletionOpen(false); if (completionBlobUrl) { URL.revokeObjectURL(completionBlobUrl); setCompletionBlobUrl(null); } setCompletionId(null); setUploadResult(null); setCompletionPhase(null); completionContinue && completionContinue(); }}>Continue</button>
            </div>
          </div>
        </div>
      )}

      {/* Job Select Modal (fallback when JobTread upload enabled but no job selected) */}
      {jobSelectModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg mb-2">Select Job for JobTread Upload</h3>
            <p className="text-sm text-base-content/70 mb-4">Choose a job to upload the completed form to.</p>
            <div className="relative">
              <input
                className="input input-bordered w-full"
                placeholder="Search job by name or number..."
                value={jtFallbackQuery}
                onChange={e => {
                  const val = e.target.value
                  setJtFallbackQuery(val)
                  if (jtFallbackTimerRef.current) window.clearTimeout(jtFallbackTimerRef.current)
                  if (!val.trim()) {
                    setJtFallbackSuggest([])
                    return
                  }
                  jtFallbackTimerRef.current = window.setTimeout(async () => {
                    setJtFallbackLoading(true)
                    const list = await fetchJobtreadSuggestions(val)
                    setJtFallbackSuggest(list)
                    setJtFallbackLoading(false)
                    jtFallbackTimerRef.current = null
                  }, 300) as unknown as number
                }}
              />
              {jtFallbackLoading && <span className="loading loading-spinner loading-sm absolute right-3 top-3" />}
              {jtFallbackSuggest.length > 0 && (
                <ul className="menu menu-compact bg-base-200 rounded-box mt-2 max-h-48 overflow-y-auto">
                  {jtFallbackSuggest.map((j: any) => (
                    <li key={j.id}>
                      <a
                        onClick={() => {
                          const job = (j?.node ?? j) as { id?: string; number?: string }
                          const id = job?.id
                          if (!id) return
                          const selected = { id: String(id), number: job?.number ?? undefined }
                          setJobSelectModalOpen(false)
                          handleUploadClick(selected)
                        }}
                      >
                        {j.name || j.number || j.id}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-action">
              <button className="btn" onClick={() => setJobSelectModalOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal - search, geocode, and select location */}
      {mapModal.open && (
        <div className="modal modal-open">
          <div className="modal-box max-w-3xl max-h-[90vh] flex flex-col p-0">
            <div className="p-4 border-b border-base-300">
              <h3 className="font-bold text-lg">Select Location</h3>
              <p className="text-sm text-base-content/70 mt-1">Search for an address or use your current location</p>
            </div>
            <div className="flex-1 overflow-hidden flex flex-col p-4 gap-3">
              <div className="join w-full">
                <input
                  className="input input-bordered join-item flex-1"
                  placeholder="Search location..."
                  value={mapModalSearch}
                  onChange={e => setMapModalSearch(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-outline join-item"
                  onClick={() => {
                    if (!navigator.geolocation) return
                    setMapModalLoading(true)
                    navigator.geolocation.getCurrentPosition(
                      pos => {
                        const lat = pos.coords.latitude
                        const lon = pos.coords.longitude
                        setMapModalSelected({ lat, lon, display_name: `${lat.toFixed(5)}, ${lon.toFixed(5)}` })
                        setMapModalSearch(`${lat.toFixed(5)}, ${lon.toFixed(5)}`)
                        setMapModalLoading(false)
                      },
                      () => setMapModalLoading(false)
                    )
                  }}
                >
                  Use current location
                </button>
              </div>
              {mapModalResults.length > 1 && (
                <select
                  className="select select-bordered w-full"
                  value={mapModalSelected ? mapModalResults.findIndex(r => r.display_name === mapModalSelected.display_name) : -1}
                  onChange={e => {
                    const idx = parseInt(e.target.value, 10)
                    const r = mapModalResults[idx]
                    if (r) setMapModalSelected({ lat: parseFloat(r.lat), lon: parseFloat(r.lon), display_name: r.display_name })
                  }}
                >
                  <option value={-1}>Choose a result...</option>
                  {mapModalResults.map((r, i) => (
                    <option key={i} value={i}>{r.display_name}</option>
                  ))}
                </select>
              )}
              <div className="flex-1 min-h-[280px] rounded-lg overflow-hidden bg-base-200">
                {mapModalLoading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="loading loading-spinner loading-lg" />
                  </div>
                ) : mapModalSelected ? (
                  <iframe
                    title="Location map"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '280px' }}
                    loading="lazy"
                    allowFullScreen
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapModalSelected.lon - 0.02},${mapModalSelected.lat - 0.02},${mapModalSelected.lon + 0.02},${mapModalSelected.lat + 0.02}&layer=mapnik&marker=${mapModalSelected.lat}%2C${mapModalSelected.lon}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-base-content/50 text-sm">
                    Search for a location or use current location
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-base-300 flex justify-end gap-2">
              <button className="btn" onClick={() => setMapModal({ open: false })}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={!mapModalSelected}
                onClick={() => {
                  if (mapModal.fieldId && mapModalSelected) {
                    handleAnswerChange(mapModal.fieldId, mapModalSelected.display_name)
                  }
                  setMapModal({ open: false })
                }}
              >
                Apply
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop bg-black/50" onClick={() => setMapModal({ open: false })} />
        </div>
      )}

      {/* Validation Failed Modal - shown when submit blocked by missing required or unconfirmed prefills */}
      {validationFailedModal.open && (
        <div className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-lg flex items-center gap-2 text-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Please fix the following before submitting
            </h3>
            <p className="text-sm text-base-content/70 mt-2 mb-4">
              Complete all required fields and confirm any prefilled values before submitting.
            </p>
            {(validationFailedModal.missingRequired.length > 0 || validationFailedModal.unconfirmedPrefills.length > 0) && (
              <ul className="space-y-2 mb-4">
                {validationFailedModal.missingRequired.map((item) => (
                  <li key={item.id} className="flex items-start gap-2 text-sm">
                    <span className="text-error">•</span>
                    <span><strong>{item.label}</strong> — required ({item.stepName || `Step ${item.stepIndex + 1}`})</span>
                  </li>
                ))}
                {validationFailedModal.unconfirmedPrefills.map((item) => (
                  <li key={item.id} className="flex items-start gap-2 text-sm">
                    <span className="text-warning">•</span>
                    <span><strong>{item.label}</strong> — tap to confirm prefilled value ({item.stepName || `Step ${item.stepIndex + 1}`})</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="modal-action">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setValidationFailedModal({ open: false, missingRequired: [], unconfirmedPrefills: [] })
                  const firstMissing = validationFailedModal.missingRequired[0] || validationFailedModal.unconfirmedPrefills[0]
                  if (firstMissing && firstMissing.stepIndex !== undefined) {
                    setCurrentStep(firstMissing.stepIndex)
                    try { window.scrollTo({ top: 0, behavior: 'smooth' }) } catch {}
                  }
                }}
              >
                Fix issues
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop bg-black/50" onClick={() => setValidationFailedModal({ open: false, missingRequired: [], unconfirmedPrefills: [] })} />
        </div>
      )}

      {/* Prefill Review Modal - checkbox confirmation before completion */}
      {prefillReviewModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg mb-2">Review prefilled fields</h3>
            <p className="text-sm text-base-content/70 mb-4">
              Please confirm you have reviewed all prefilled fields before generating your PDF.
            </p>
            <label className="label cursor-pointer justify-start gap-3 p-4 rounded-lg border border-base-300 bg-base-200/50">
              <input
                type="checkbox"
                className="checkbox checkbox-primary"
                checked={prefillReviewConfirmed}
                onChange={(e) => setPrefillReviewConfirmed(e.target.checked)}
              />
              <span className="label-text font-medium">I confirm I have reviewed all prefilled fields</span>
            </label>
            <div className="modal-action mt-4">
              <button className="btn" onClick={() => { setPrefillReviewModalOpen(false); setPrefillReviewConfirmed(false) }}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={!prefillReviewConfirmed}
                onClick={handlePrefillReviewContinue}
              >
                Continue
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop bg-black/50" onClick={() => { setPrefillReviewModalOpen(false); setPrefillReviewConfirmed(false) }} />
        </div>
      )}
      
      {/* Workflow Form Selection Modal */}
      {workflowSelectionModal.open && (
        <div className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faList} className="text-primary" />
              {workflowSelectionModal.workflowName}
            </h3>
            <p className="text-base-content/70 mb-4">
              Select which forms you'd like to complete. Forms marked as mandatory must be filled out.
            </p>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {workflowSelectionModal.forms.map((form, index) => {
                const isOptional = !!form.workflowOptional
                const isSelected = workflowSelectionModal.selectedFormNames.has(form.formName)
                return (
                  <div 
                    key={form.formName} 
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isSelected 
                        ? 'border-primary bg-primary/5' 
                        : 'border-base-300 bg-base-100 opacity-60'
                    } ${isOptional ? 'cursor-pointer hover:border-primary/50' : ''}`}
                    onClick={() => isOptional && toggleOptionalFormSelection(form.formName)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 pt-0.5">
                        {isOptional ? (
                          <input 
                            type="checkbox" 
                            className="checkbox checkbox-primary" 
                            checked={isSelected}
                            onChange={() => toggleOptionalFormSelection(form.formName)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div className="w-5 h-5 flex items-center justify-center">
                            <FontAwesomeIcon icon={faFileAlt} className="text-primary" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{form.formName}</span>
                          {!isOptional && (
                            <span className="badge badge-sm badge-primary">Required</span>
                          )}
                          {isOptional && (
                            <span className="badge badge-sm badge-ghost">Optional</span>
                          )}
                        </div>
                        <p className="text-sm text-base-content/60 mt-1 line-clamp-2">{form.description}</p>
                        <div className="flex gap-3 mt-2 text-xs text-base-content/50">
                          <span>{form.category}</span>
                          <span>•</span>
                          <span>{form.estimatedTime}</span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-base-content/30 font-mono text-sm">
                        #{index + 1}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="modal-action mt-6">
              <button 
                className="btn btn-ghost" 
                onClick={() => setWorkflowSelectionModal({ open: false, workflowName: '', forms: [], selectedFormNames: new Set() })}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleWorkflowSelectionContinue}
                disabled={!workflowSelectionModal.forms.some(f => !f.workflowOptional || workflowSelectionModal.selectedFormNames.has(f.formName))}
              >
                Continue ({workflowSelectionModal.forms.filter(f => !f.workflowOptional || workflowSelectionModal.selectedFormNames.has(f.formName)).length} forms)
              </button>
            </div>
          </div>
          <div className="modal-backdrop bg-black/50" onClick={() => setWorkflowSelectionModal({ open: false, workflowName: '', forms: [], selectedFormNames: new Set() })} />
        </div>
      )}

      {stageSubmitNotice && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg mb-2">Form update</h3>
            <p className="py-2">{stageSubmitNotice}</p>
            <div className="modal-action">
              <button className="btn btn-primary" onClick={() => setStageSubmitNotice(null)}>OK</button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop bg-black/50" onClick={() => setStageSubmitNotice(null)} />
        </div>
      )}

      {cancelTarget && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg mb-2">Cancel this agreement?</h3>
            <p className="py-2">
              {cancelTarget.label || cancelTarget.formName} will be cancelled and removed from Pending.
              This cannot be undone.
            </p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={cancelling}
                onClick={() => setCancelTarget(null)}
              >
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-error"
                disabled={cancelling}
                onClick={() => { void confirmCancelInstance() }}
              >
                {cancelling ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                    Cancelling
                  </>
                ) : (
                  'Cancel agreement'
                )}
              </button>
            </div>
          </div>
          <form
            method="dialog"
            className="modal-backdrop bg-black/50"
            onClick={() => { if (!cancelling) setCancelTarget(null) }}
          />
        </div>
      )}

      {nameModal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg mb-3">{nameModal.title}</h3>
            <label className="form-control w-full">
              <span className="label-text mb-1">{nameModal.label}</span>
              <input
                autoFocus
                className="input input-bordered w-full"
                value={nameModal.value}
                onChange={e => setNameModal(m => m ? { ...m, value: e.target.value } : m)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const trimmed = nameModal.value.trim()
                    nameModal.resolve(trimmed || null)
                    setNameModal(null)
                  } else if (e.key === 'Escape') {
                    nameModal.resolve(null)
                    setNameModal(null)
                  }
                }}
              />
            </label>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  nameModal.resolve(null)
                  setNameModal(null)
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!nameModal.value.trim()}
                onClick={() => {
                  nameModal.resolve(nameModal.value.trim())
                  setNameModal(null)
                }}
              >
                Confirm
              </button>
            </div>
          </div>
          <form
            method="dialog"
            className="modal-backdrop bg-black/50"
            onClick={() => {
              nameModal.resolve(null)
              setNameModal(null)
            }}
          />
        </div>
      )}

      <Footer />
    </div>
  )
}

export default FormsPage 